import { useLanguage } from "@/contexts/language-context";
import { cn } from "./utils";

type Dict = Record<string, any>;

const en: Dict = {
  hero: {
    goalLabel: "Goal",
    minuteShort: "min",
    hour: "hour",
    hours: "hours",
  },
  analytics: {
    learnMore: "Learn More",
    chart: {
      practiceTime: "Practice Time",
      minutes: "minutes",
      current: "Current",
    },
    cards: {
      today: {
        title: "Today",
        unit: "mins",
        subtitle: "Today's practice time",
      },
      sessions: {
        title: "Sessions",
        unit: "practices",
        subtitle: "Practice shadowing today",
      },
      streak: {
        title: "Streak",
        unitSingular: "day",
        unitPlural: "days",
        subtitle: "Current practice streak",
      },
    },
  },
  upgradeDialog: {
    title: "Upgrade to Premium",
    subtitle: {
      withEpisode: 'Access "{episodeTitle}" and unlock all premium content',
      default: "Unlock all premium content and features",
    },
    features: {
      episodes: "Access to all episodes",
      ai: "AI-powered learning features",
      support: "Priority support",
    },
    actions: {
      upgrade: "Upgrade Now",
      continueFree: "Continue with Free Access",
    },
  },
  shadowing: {
    title: "Shadowing Practice",
    showTranslation: "Show Translation",
    showSentenceHint: "Click the eye icon to show sentence",
    replay: "Replay",
    continue: "Continue",
    startShadowing: "Start Shadowing",
    stopRecording: "Stop Recording",
    transcribeFailed: "Failed to transcribe",
  },
  common: {
    loading: "Loading...",
    save: "Save",
    speak: "Speak",
    signIn: "Sign In",
    backToHome: "Back to Home",
    optional: "Optional",
  },
  installPrompt: {
    title: "Install Longoflows on your device",
    instructions: {
      tap: "Tap",
      thenAdd: 'then "Add to Home Screen"',
    },
    actions: {
      close: "Close install prompt",
    },
  },
  navigation: {
    tagline: "Your language learning platform",
    actions: {
      collapse: "Collapse sidebar",
      expand: "Expand sidebar",
    },
    links: {
      home: "Home",
      search: "Search",
      episodes: "Episodes",
      course: "Course",
      library: "Library",
      review: "Review",
      history: "History",
      settings: "Settings",
    },
  },
  tabs: {
    summary: { name: "Summary", desc: "AI-powered episode summary", mobile: "Summary" },
    mindmap: { name: "Mindmap", desc: "Visual concept mapping", mobile: "Map" },
    transcript: { name: "Transcript", desc: "Full episode transcript", mobile: "Script" },
    vocabulary: { name: "Vocabulary", desc: "Key terms & phrases", mobile: "Vocab" },
    phrase: { name: "Dictionary", desc: "Vocabulary in dictionary view", mobile: "Dict" },
    quiz: { name: "Quiz", desc: "Interactive practice", mobile: "Quiz" },
  },
  player: {
    freePreview: {
      toastTitle: "Free preview limit reached",
      toastDescription: "Upgrade to continue listening beyond the first 2 minutes.",
      toastUpgradeCta: "Upgrade",
    },
  },
  phraseSection: {
    title: "Vocabulary in Dictionary",
    subtitle: "See every dictionary word exactly where it appears in the transcript.",
    truncateNotice: "Showing the first {count} matches",
    actions: {
      openSettings: "Open settings",
    },
    badges: {
      dictionaryMode: "Dictionary mode",
    },
    countLabel: {
      singular: "words",
      plural: "words",
    },
    indicators: {
      cardsLabel: "Vocabulary",
      dictionaryLabel: "Dictionary",
      dictionaryMissing: "Dictionary not selected",
    },
    jump: "Jump to clip",
    empty: {
      noVocabulary: {
        title: "Vocabulary required",
        description: "Generate or import vocabulary to explore contextual phrases.",
      },
      noMatches: {
        title: "No contextual phrases yet",
        description: "Vocabulary terms haven’t appeared in the transcript so far.",
      },
      noDictionary: {
        title: "Choose a dictionary to unlock highlights",
        description: "Select a vocabulary dictionary in settings so we can spot words you’ve learned.",
      },
    },
  },
  quizSection: {
    title: "Interactive Quiz",
    description: "Practice key terms pulled from this episode.",
    empty: {
      title: "No quiz available yet",
      description: "Quiz questions will appear here once vocabulary has linked practice items.",
    },
    stats: {
      questionSingular: "question",
      questionPlural: "questions",
      countSingular: "Question",
      countPlural: "Questions",
    },
    overview: {
      title: "Quiz Overview",
      nextTerm: "Next term",
      progress: "Progress",
      accuracy: "Accuracy",
      allCompleted: "All quiz items completed",
    },
    toast: {
      noSentence: "Quiz sentence unavailable for audio generation.",
      audioGenerationFailed: "Failed to generate audio",
      audioGenerated: "Quiz audio generated",
    },
    messages: {
      completed: "Great job! This term is fully shadowed.",
    },
    status: {
      completed: "Completed",
      todo: "Todo",
    },
    translation: {
      show: "Show translation",
      hide: "Hide translation",
    },
    shadowing: {
      defaultTitle: "Episode Quiz Practice",
      cta: "Shadowing",
      dialogTitle: "Shadowing Practice",
      instructions: "Listen and repeat the sentence",
      showTranslation: "Show translation",
      hideTranslation: "Hide translation",
      audioUnavailable: "Reference audio not available yet. Generate it from the quiz card to listen.",
      stopRecording: "Stop Recording",
      startShadowing: "Start Shadowing",
      yourRecording: "Your recording",
      attemptsLabel: "Attempts",
    },
  },
  persona: {
    progress: {
      label: "Step {current} of {total}",
    },
    actions: {
      skip: "Skip for now",
      back: "Back",
      continue: "Continue",
      finish: "Finish",
    },
    steps: {
      languages: {
        eyebrow: "Step 1 · Languages",
        title: "What are your native and target languages?",
        description: "Tell us your preferred languages so we can personalize translations.",
      },
      level: {
        eyebrow: "Step 2 · Target Level",
        title: "How would you rate your target language level?",
        description: "Just a rough self-assessment, no test needed.",
      },
      goals: {
        eyebrow: "Step 3 · Goals",
        title: "What’s your main goal for learning your target language?",
        description: "Pick the goals that best match your journey. You can adjust them anytime.",
      },
      topics: {
        eyebrow: "Step 4 · Topics / Interests",
        title: "What kind of videos do you enjoy the most?",
        description: "We’ll recommend English videos based on these topics.",
      },
      formats: {
        eyebrow: "Step 5 · Video Formats",
        title: "What kind of video format do you like?",
        description: "Choose the styles you enjoy. We’ll mix them in your recommendations.",
      },
      session: {
        eyebrow: "Step 6 · Session Length",
        title: "How long do you usually have for one session?",
        description: "We’ll match video length to your schedule.",
      },
      subtitles: {
        eyebrow: "Step 7 · Subtitles & Explanation",
        title: "How should we support your learning?",
        description: "Choose the subtitles and explanations you prefer.",
      },
    },
    goals: {
      everyday: "Everyday conversation",
      watchYoutube: "Watch YouTube without subtitles",
      jobInterview: "Job interviews & career",
      studyExam: "Study / exams (IELTS, TOEFL…)",
      travelAbroad: "Travel & living abroad",
    },
    topics: {
      dailyVlog: "Daily vlog & lifestyle",
      aiTech: "Tech & AI",
      career: "Career & self-improvement",
      comedy: "Comedy & entertainment",
      moviesTv: "Movies & TV shows",
      news: "News & current events",
      gaming: "Gaming",
      education: "Education & tutorials",
    },
    levels: {
      rangeHint: "We’ll aim for videos in the {from}–{to} range.",
      a2: {
        title: "Beginner – I know some words but can’t follow most videos.",
        description: "Basic CEFR level",
      },
      b1: {
        title: "Lower-intermediate – I need subtitles to follow most content.",
        description: "Lower-intermediate",
      },
      b2: {
        title: "Intermediate – With subtitles I understand 70–80%.",
        description: "Upper-intermediate",
      },
      c1: {
        title: "Advanced – I can follow most videos without subtitles.",
        description: "Advanced",
      },
    },
    formats: {
      vlog: "Vlogs / daily life",
      interview: "Interviews & podcasts",
      podcast: "Podcasts / audio stories",
      tutorial: "Explainer / tutorials",
      shorts: "Short clips / highlights",
      movieClip: "Movie / TV show scenes",
    },
    sessionLength: {
      label: "Session length",
      description: "{minutes} min target",
      ten: "5–10 minutes",
      twenty: "10–20 minutes",
      thirty: "20–30 minutes",
      fortyFive: "30+ minutes",
    },
    sessionFrequency: {
      prompt: "How often do you want to practice?",
      three: "A few times per week",
      five: "Almost every day",
      seven: "Every day",
    },
    subtitles: {
      alwaysTitle: "Always show English subtitles",
      alwaysDescription: "Keep captions on by default for every video.",
      nativeTitle: "Show translation in my language when I need help",
      nativeDescription: "Toggle native-language support whenever you get stuck.",
      nativeLanguageLabel: "Preferred native language",
      nativeLanguagePlaceholder: "Select language",
      nativeLanguageHint: "We’ll use this when showing translations or explanations.",
      nativeLanguage: {
        auto: "Auto detect",
        zh: "Chinese",
        es: "Spanish",
        ja: "Japanese",
        ko: "Korean",
      },
    },
    languages: {
      nativeLabel: "What's your native language?",
      targetLabel: "Which language are you learning?",
      targetPlaceholder: "Select target language",
      hint: "We'll use this to match lessons and level guidance.",
      native: {
        en: "English",
        zh: "Chinese",
        es: "Spanish",
        ja: "Japanese",
        ko: "Korean",
        fr: "French",
        de: "German",
        pt: "Portuguese",
        ru: "Russian",
      },
      target: {
        en: "English",
        zh: "Chinese",
        es: "Spanish",
        ja: "Japanese",
        ko: "Korean",
        fr: "French",
        de: "German",
        pt: "Portuguese",
        ru: "Russian",
      },
    },
    explanations: {
      title: "Explanations you want to see",
      keyVocab: "Show key vocabulary for each video",
      showPhrases: "Highlight useful phrases & expressions",
      translateSentences: "Translate tricky sentences into your native language",
      pronunciation: "Focus on pronunciation and shadowing",
    },
  },
  vocabQuiz: {
    title: "Vocabulary Quiz",
    translationUnavailable: "Translation not available for this segment",
    loading: "Loading...",
    translation: {
      show: "Show translation",
      hide: "Hide translation",
    },
    actions: {
      replay: "Replay",
      continue: "Continue",
    },
  },
  mobileImmersive: {
    subtitles: {
      show: "Show subtitles",
      hide: "Hide subtitles",
    },
    translation: {
      show: "Show translation",
      hide: "Hide translation",
    },
    narration: {
      enable: "Enable Narration Mode",
      disable: "Disable Narration Mode",
      tooltipEnable: "Auto-play vocabulary explanations after playback",
      tooltipDisable: "Disable vocabulary narration",
      generating: "Generating narration audio...",
      failed: "Failed to generate narration",
      modeActive: "Narration Mode Active",
      playing: "Playing narration...",
      ready: "Narration ready",
      skip: "Skip",
      skipTooltip: "Skip narration and continue video",
      currentTerm: "Current term",
      pause: "Pause",
      resume: "Resume",
      repeat: "Replay",
    },
    status: {
      new: "New",
      newDescription: "Just added, needs first review",
      fuzzy: "Fuzzy",
      fuzzyDescription: "Needs more practice soon",
      mastered: "Mastered",
      masteredDescription: "Confident and on long review",
    },
    shadowing: {
      tooltip: "Shadowing practice",
      dialogTitle: "Shadowing Practice",
      dialogDescription: "Repeat the current subtitle aloud.",
      attemptLabel: "Attempt",
      hideText: "Hide text",
      showText: "Show text",
      hideTranslationShort: "Hide translation",
      showTranslationShort: "Show translation",
      transcriptionLabel: "Your transcription",
      startRecording: "Start shadowing",
      stopRecording: "Stop recording",
      start: "Start shadowing",
      stop: "Stop shadowing",
      unknownEpisode: "Unknown Episode",
    },
  },
  vocabulary: {
    status: {
      label: "Vocabulary classification",
      new: "New",
      newDescription: "Just added, needs first review",
      fuzzy: "Fuzzy",
      fuzzyDescription: "Needs more practice soon",
      mastered: "Mastered",
      masteredDescription: "Confident and on long review",
    },
    section: {
      title: "Keywords & Topics",
      topicCountSingular: "{count} topic",
      topicCountPlural: "{count} topics",
      revealAllButton: "Reveal all",
      revealAllTitle: "Reveal all explanations",
      restoreBlurButton: "Restore blur",
      restoreBlurTitle: "Restore blur",
      levelFilterLabel: "Filter by CEFR level",
      levelFilterUserLevel: "Your level: {level}",
      levelFilterAll: "All",
      noTermsMatchFilter: "No terms match the selected CEFR levels yet. Try selecting additional levels.",
    },
    swipeTip: {
      badge: "Tip",
      heading: "Swipe to manage this stack",
      description: "Swipe left to hide words you know, or right to keep them in your practice flow.",
    },
    desktopActions: {
      markKnown: "Mark as known",
      keepLearning: "Keep practicing",
    },
    card: {
      examplesLabel: "Examples",
    },
    stack: {
      viewDetails: "View details",
      closeDetails: "Close",
    },
  },
  transcript: {
    title: "Transcript",
    generate: "Generate Transcript",
    generating: "Generating Transcript...",
    generateDesc: "Generate a transcript to see the full text with timestamps and interactive features.",
    generatingDesc: "Please wait while we generate the transcript for this episode.",
    segments: "segments",
    usageDeducted: "Transcript credit used",
    usageRemaining: "{remaining}/{limit} left this month",
    usageRemainingUnlimited: "Unlimited transcripts with your plan",
    dynamicIslandSettings: "Dynamic Island settings",
    dynamicIslandTitle: "Dynamic Island",
    dynamicIslandDescription: "Control what appears in the Dynamic Island while learning.",
    dynamicIslandVocabulary: "Vocab popup",
    dynamicIslandVocabularyHint: "Show vocabulary prompts inside the popup overlay",
    toggleDynamicIslandVocabulary: "Toggle vocab popup",
    wordMask: "Subtitle animation",
    toggleWordMask: "Toggle subtitle animation",
  },
  premiumPrompt: {
    guest: {
      title: "Sign in required",
      description: "Please sign in to access this content and unlock learning features.",
    },
    free: {
      title: "Premium content",
      description: "This episode requires a premium subscription to access all features.",
      secondary: "Or check out our featured episodes for free access.",
    },
    featured: {
      title: "Free featured content",
      description: "Enjoying this free episode? Upgrade to premium to unlock our full content library!",
      cta: "Learn about Premium",
    },
    fallback: "Access restricted",
  },
  contentRenderer: {
    alerts: {
      vocabFallback: "Failed to generate vocabulary: {error}. Using demo data instead.",
    },
  },
  upgradePromptDialog: {
    titles: {
      limitReached: "Monthly Limit Reached",
      upgrade: "Upgrade to Premium",
      error: "Generation Failed",
    },
    usage: {
      label: "Monthly Usage",
      summary: "{used}/{limit} transcripts used this month",
      remaining: "{remaining}/{limit} remaining",
      unlimited: "Unlimited transcripts with your premium plan",
    },
    benefits: {
      title: "Upgrade to Premium",
      items: {
        unlimitedTranscripts: "Unlimited transcript generation",
        aiSummaries: "Watch all Ready transcribed videos",
        vocabulary: "Full vocabulary analysis",
        priority: "Priority processing",
        export: "Export to all formats",
      },
    },
    plans: {
      basic: {
        name: "Basic Plan",
        short: "Basic",
        badge: "Popular",
        price: "$12.99",
        period: "/month",
        description: "Perfect for individual learners",
      },
      premium: {
        name: "Premium Plan",
        short: "Premium",
        badge: "Best Value",
        price: "$19.99",
        period: "/month",
        description: "Advanced features & priority support",
      },
    },
    actions: {
      upgradeToPlan: "Upgrade to {plan}",
      continueWithPlan: "Continue with {plan}",
      later: "Later",
      close: "Close",
      tryAgain: "Try Again",
    },
    status: {
      processing: "Processing...",
    },
    alerts: {
      checkoutFailed: "Failed to start checkout. Please try again.",
    },
    error: {
      generic: "Unable to generate transcript",
      loginRequired: "Please log in to generate transcripts. Your progress will be saved.",
    },
    premiumStatus: {
      title: "You have a Premium Plan",
      description: "Enjoy unlimited transcript generation and all premium features.",
    },
  },
  episode: {
    floatingActions: {
      buttons: {
        open: "Open quick actions",
        collapse: "Collapse quick actions",
        showTranslation: "Show translation",
        hideTranslation: "Hide translation",
        generateTranslation: "Generate translation",
        generatingTranslation: "Generating translation...",
        generateChosenExplanation: "Generate AI explanations",
        generatingChosenExplanation: "Generating explanations",
        enableWordHighlight: "Enable word highlight",
        disableWordHighlight: "Disable word highlight",
        adjustPracticeMode: "Adjust practice mode",
        enablePracticeMode: "Enable practice mode",
        resetProgress: "Reset episode progress",
        backToTop: "Back to top",
      },
      tooltips: {
        open: "Open Quick Actions",
        collapse: "Collapse Quick Actions",
        showTranslation: "Show Translation",
        hideTranslation: "Hide Translation",
        generateTranslation: "Generate Translation",
        generatingTranslation: "Generating...",
        generateChosenExplanation: "Generate example-based explanation",
        generatingChosenExplanation: "Generating explanations...",
        dictionaryRequiredTitle: "Dictionary required",
        dictionaryRequiredDescription: "Set up a dictionary in Settings to unlock highlights.",
        enableWordHighlight: "Enable Word Highlight",
        disableWordHighlight: "Disable Word Highlight",
        practiceSetupTitle: "Setup required",
        practiceSetupDescription: "Generate vocabulary or choose a dictionary to begin practice mode.",
        practiceActiveTitle: "Practice mode active",
        practiceOpenTitle: "Open practice modes",
        practiceActiveDescription: "Currently using {mode} mode.",
        practiceOpenDescription: "Choose a learning mode tailored to your goal.",
        resetProgressTitle: "Reset episode progress",
        resetProgressDescription: "Clear mastered words for this episode only.",
        backToTop: "Back to top",
      },
      success: {
        translateGenerated: "Translation generated successfully!",
        chosenExplanationGenerated: "Explanations generated successfully!",
      },
      alerts: {
        translateMissingData: "Unable to generate translation: missing required data.",
      },
      errors: {
        translateRequestFailed: "Translation request failed",
        translateResponseInvalid: "Translation response format error",
        translateConsoleError: "Failed to generate translation",
        translateFailed: "Translation generation failed, please try again.",
        generateVocabularyFirst: "Please generate vocabulary first",
        dictionaryInitConsole: "Dictionary initialization failed",
        dictionaryLoadFailed: "Unable to load dictionary, please try again",
        dictionaryNotSelected: "Please select a vocabulary dictionary in Settings first",
        dictionaryInitFailed: "Vocabulary manager failed to initialize",
        dictionaryInitUnable: "Unable to load vocabulary dictionary, please try again",
        chosenExplanationFailed: "Failed to generate explanations",
        chosenExplanationMissing: "Unable to generate explanations: vocabulary is not ready.",
        chosenExplanationUnauthorized: "Please sign in to generate explanations.",
      },
      practice: {
        noDictionary: "No Dictionary",
        spellingSuffix: "Spelling",
      },
      practiceModes: {
        vocabulary: "Vocabulary",
        toefl: "TOEFL",
        shadowing: "Shadowing",
      },
      dialog: {
        title: "Reset Episode Progress",
        description: "This will clear all mastered words for this episode only and restart your vocabulary practice. Other episodes' progress will not be affected. This action cannot be undone.",
        cancel: "Cancel",
        confirm: "Reset Progress",
      },
    },
    exportDialog: {
      lockedBadge: "Premium feature",
      lockedDescription: "Export is available on premium plans. Upgrade to download, copy, or share full episode content.",
      title: "Export Episode",
      description: "Export \"{title}\" in your preferred format",
      quickActionsTitle: "Quick Actions",
      downloadTitle: "Download Files",
      quickActions: {
        copyMarkdown: "Copy Markdown",
        copyText: "Copy Text",
        copyUrl: "Copy URL",
        email: "Email",
      },
      options: {
        markdown: {
          title: "Markdown File",
          description: "Download as formatted Markdown (.md) — Perfect for documentation and note-taking",
        },
        text: {
          title: "Plain Text File",
          description: "Download as simple text (.txt) — Universal compatibility",
        },
        json: {
          title: "JSON Data",
          description: "Download structured data (.json) — For developers and automation",
        },
      },
      episodeInfo: {
        episodeLabel: "Episode",
        authorLabel: "Author",
        unknownAuthor: "Unknown",
        includesPrefix: "Includes",
        includesSummary: "Summary",
        includesChapters: "Chapters",
        includesKeyPoints: "Key Points",
        includesTranscript: "Full Transcript",
      },
    },
    immersiveFloating: {
      subtitles: {
        show: "Show subtitles",
        hide: "Hide subtitles",
      },
      translation: {
        show: "Show card translations",
        hide: "Hide card translations",
      },
      translationReveal: {
        show: "Reveal all card translations",
        hide: "Hide all card translations",
      },
      exit: "Exit immersive mode",
    },
    videoPlayer: {
      immersive: {
        enterFocus: "Focus mode",
        exitFocus: "Exit focus",
        enterImmersive: "Immersive focus",
        exitImmersive: "Exit immersive",
        enterFocusTitle: "Enter focus mode",
        exitFocusTitle: "Exit focus mode",
        enterImmersiveTitle: "Enter immersive mode",
        exitImmersiveTitle: "Exit immersive mode (Esc)",
      },
      controls: {
        minimize: "Minimize player",
        maximize: "Restore player",
      },
    },
    player: {
      previewLock: {
        badge: "Preview limit",
        message: "Free plan preview is limited to the first 2 minutes. Upgrade to continue listening.",
        messageShort: "Preview limit — upgrade to continue",
        tooltip: "Free plan preview limited to the first 2 minutes",
      },
      freePreview: {
        toastTitle: "Free preview limit reached",
        toastDescription: "Upgrade to continue listening beyond the first 2 minutes.",
        toastUpgradeCta: "Upgrade",
      },
    },
  },
  summary: {
    title: "Summary",
    noSummary: "No Summary Available",
    generate: "Generate Summary",
    generating: "Generating Summary...",
    generateDesc: "Generate an AI-powered summary to get a comprehensive overview of this episode.",
    generatingDesc: "Please wait while we create an AI-powered summary of this episode.",
    aiOverview: "AI-generated overview",
    zhTranslation: "Chinese Translation",
    zhTranslationExtra: "Chinese Translation (extra)",
    translationLoading: "Translation loading...",
  },
  home: {
    featured: {
      title: "Featured",
      error: "Failed to load featured episodes",
      refresh: "Refresh page to try again",
    },
    following: {
      title: "Following",
    },
    episodes: {
      title: "Episodes",
      viewAll: "View All",
      empty: "No episodes available at the moment.",
    },
    channels: {
      title: "Popular Channels",
      viewAll: "View All",
    },
    collections: {
      title: "Collections",
      viewAll: "View All",
      countSuffix: "episodes",
      viewAllInline: "View all",
      categories: {
        "1": "Tech & AI Frontiers",
        "2": "Personal Growth & Mindset",
        "3": "Health & Wellness",
        "4": "Business & Finance",
        "5": "Global Issues & Politics",
        "6": "Relationships & Human Connection",
        "7": "Science & Discovery",
        "8": "Lifestyle & Vlogs",
        "9": "History & Culture",
        "10": "Environment & Sustainability",
      },
    },
    youtube: {
      title: "YouTube Featured",
      missingThumbnail: "Missing thumbnail",
      playVideo: "Play {title}",
      defaultCreator: "YouTube Creator",
    },
    free: "FREE",
  },
  channel: {
    filters: {
      ariaLabel: "Filter episodes by season",
      allSeasons: "All Seasons",
      season: "Season {season}",
    },
    badges: {
      episodes: "Episodes",
    },
    empty: {
      title: "No Episodes Found",
      seasonFiltered: "No episodes found for the selected season.",
      generic: "This channel doesn't have any episodes yet.",
    },
  },
  episodes: {
    title: "Trending Episodes",
    loadingMore: "Loading more...",
    loadMore: "Load More",
    noMore: "No more episodes to load",
    noneTitle: "No Episodes Found",
    noneDesc: "No trending episodes available at the moment.",
    errorTitle: "Error Loading Episodes",
    tryAgain: "Try Again",
  },
  library: {
    title: "My Library",
    tabs: {
      favorites: "Favorites",
      playlists: "Playlists",
      podcasts: "Podcasts",
    },
    emptyFavoritesTitle: "No favorites yet",
    emptyFavoritesDesc: "Episodes you star will appear here",
    browseEpisodes: "Browse Episodes",
    playlistEmpty: "This playlist is empty",
    playlistsEmptyTitle: "No playlists yet",
    playlistsEmptyDesc: "Create playlists to organize your episodes",
    podcastsEmptyTitle: "No podcast subscriptions yet",
    podcastsEmptyDesc: "Follow podcasts to see them here",
    discoverPodcasts: "Discover Podcasts",
    playlistDialog: {
      title: "Add to Playlist",
      description: "Select a playlist to add this episode to, or create a new playlist.",
      addingLabel: "Adding:",
      nameLabel: "Playlist Name *",
      namePlaceholder: "Enter playlist name...",
      descriptionLabel: "Description (optional)",
      descriptionPlaceholder: "Add a description...",
      cancel: "Cancel",
      creating: "Creating...",
      createAction: "Create Playlist",
      yourPlaylists: "Your Playlists",
      createNew: "Create New Playlist",
      createFirst: "Create Your First Playlist",
      episodesLabel: "episodes",
    },
  },
  history: {
    title: "Recently Watched",
    subtitle: "Track your progress and continue where you left off",
    loading: "Loading...",
    loginRequiredTitle: "Login Required",
    loginRequiredDesc: "Please log in to view your listening history.",
    noHistoryTitle: "No History Found",
    noHistoryAll: "You haven't listened to any episodes yet.",
    noHistoryFiltered: (filter: string) => `No ${filter} episodes found.`,
    completed: "Completed",
    left: "left",
    watchAgain: "Watch Again",
    continue: "Continue",
    view: "View",
  },
  course: {
    index: {
      title: "Courses",
      subtitle: "Explore curated courses to level up faster.",
      cards: {
        idioms: {
          provider: "Lingoflows",
          title: "Most Common American Idioms",
          description: "Master idioms with everyday examples and explanations.",
          coverAlt: "Most Common American Idioms cover",
        },
      },
    },
    detail: {
      headerTitle: "Courses",
      menuButton: "Open menu",
      breadcrumb: {
        courses: "Courses",
      },
      actions: {
        continue: "Continue Learning",
      },
      pagination: "Page {page} / {totalPages} • {total} {unit}",
      paginationPrev: "Previous",
      paginationNext: "Next",
      totalBadge: "{total} {unit}",
      idiomUnit: "idioms",
      errors: {
        load: "Failed to load idioms: {message}",
      },
      courses: {
        idioms: {
          title: "Most Common American Idioms",
          tagline: "Master idioms with me!",
          provider: "Lingoflows",
          coverAlt: "Most Common American Idioms cover",
        },
        default: {
          title: "Course",
          tagline: "",
          provider: "",
          coverAlt: "Course cover",
        },
      },
    },
    common: {
      previous: "Previous",
      next: "Next",
    },
    progress: {
      headerLabel: "Progress",
      loading: "Loading",
      status: {
        completed: "Completed",
        todo: "Todo",
      },
      completedTooltip: "Completed",
      exampleAria: "Example {index} {status}",
      defaultCourseLabel: "Course",
    },
    freePlanLimit: {
      title: "Upgrade to unlock this lesson",
      description:
        "Free plan users can access the first 10 idioms. Upgrade your plan to keep learning the full course.",
      lockedCardLabel: "Upgrade to unlock this lesson",
      lockedBanner: "Upgrade to unlock",
      lockedHeading: "Upgrade to continue learning",
      lockedMessage:
        "Free plan users can access the first 10 idioms. Upgrade to unlock the full course, including lesson #{number}.",
      backToList: "Back to course overview",
    },
    idiomDetail: {
      breadcrumb: {
        courses: "Courses",
      },
      sidebar: {
        idiomLabel: "Idiom",
        difficulty: "Difficulty",
        examples: "Examples",
        backToList: "Back to list",
      },
      translationToggle: "Translation",
      untitled: "Untitled",
      examples: {
        title: "Examples",
        description: "Shadow each example and record your voice to finish this chapter.",
        empty: "No examples available for this idiom yet.",
      },
      errors: {
        load: "Unable to load idiom details. Please try again later.",
      },
    },
    examples: {
      transcribing: "Transcribing...",
    },
  },
  savedWords: {
    title: "Review",
    countSuffix: "saved words",
    selectedSuffix: "selected",
    blur: {
      show: "Show Explains",
      hide: "Blur Explains",
    },
    play: {
      playing: "Playing...",
      playSelected: "Play Selected",
      playAll: "Play All",
    },
    export: {
      label: "Export",
      json: "Export JSON",
      csv: "Export CSV",
      jsonShort: "JSON",
      csvShort: "CSV",
    },
    selectMode: {
      enter: "Select",
      exit: "Done",
      selectAll: "Select All",
      clear: "Clear",
    },
    filters: {
      all: "All",
    },
    sort: {
      newest: "Newest First",
      oldest: "Oldest First",
      alphabetical: "A-Z",
    },
    reviewNow: "Review Now",
    due: "due",
    onDeck: "in stack",
    status: {
      new: "New",
      fuzzy: "Fuzzy",
      mastered: "Mastered",
    },
    next: "Next",
    interval: "Interval",
    correct: "Correct",
    wrong: "Wrong",
    reviewed: "Reviewed",
    explains: "Explains",
    reviewAgain: "Again",
    reviewHard: "Hard",
    reviewGood: "Good",
    reviewEasy: "Easy",
    reviewAllCaughtUp: "All caught up!",
    reviewAllCaughtUpHint: "No cards need review right now. Check back later or add new words to study.",
    empty: {
      title: "No words found",
      filterHint: "Try adjusting your search or filters.",
      defaultHint: "Start saving words while watching episodes!",
    },
    loginRequiredTitle: "Login Required",
    loginRequiredDesc: "Please log in to view your saved words.",
    searchPlaceholder: "Search words...",
    sources: {
      youdao: "Youdao",
      original: "Original",
    },
    relative: {
      now: "now",
      dueSoon: "due soon",
      justReviewed: "just reviewed",
      in: "in {value}{unit}",
      ago: "{value}{unit} ago",
      today: "Today",
      yesterday: "Yesterday",
    },
    selectionBar: {
      selectedSuffix: "selected",
      delete: "Delete Selected",
    },
  },
  settings: {
    title: "Settings",
    nativeLanguage: "Native language",
    nativeLanguageHint: "Choose your native language for translations and interface",
    theme: {
      title: "Appearance",
      hint: "Choose your preferred color theme",
      light: "Light",
      lightDesc: "Light color theme",
      dark: "Dark",
      darkDesc: "Dark color theme",
      system: "System",
      systemDesc: "Follow system preference",
    },
    cefrLevel: {
      title: "CEFR Level",
      description: "Pick the CEFR stage that best matches your studies so we can personalize content.",
      unset: "Not set",
      saving: "Saving CEFR level...",
    },
    interfaceLanguage: "Interface Language",
    interfaceLanguageHint: "Choose the language for the app interface. This is independent from your native language setting.",
    videoTitles: {
      title: "Episode title language",
      description: "Show Chinese titles on the homepage when translations are available.",
      chineseLabel: "Chinese title",
      originalLabel: "Original title",
      saving: "Saving preference...",
    },
    vocabularyDict: "Vocabulary Dictionary",
    remove: "Remove",
    removing: "Removing...",
    selectVocabularyHint: "Select the vocabulary you wish to learn based on your needs",
    dailyGoal: {
      title: "Daily Shadowing Goal",
      desc: "Set your daily practice time and quickly view your progress",
      targetMinutes: "Target minutes",
      minutesLabel: "Minutes",
      progressPrefix: "Today's progress:",
      remainingPrefix: "Remaining",
      minAbbr: "min",
      completedPrefix: "Completed",
      todayWord: "today",
      saving: "Saving...",
      saved: "Saved",
      loadFailed: "Failed to load goal",
      saveFailed: "Save failed",
    },
    examVocab: {
      title: "Exam Vocabulary",
      subtitle: "Professional and standardized exam vocabulary",
    },
  },
  user: {
    plan: {
      premium: "Premium",
      free: "Free Plan",
    },
    badge: {
      premiumMember: "Premium Member",
      unlimited: "Unlimited",
    },
    usage: {
      transcriptsLabel: "transcripts",
      remaining: "remaining this month",
      limitReached: "Monthly limit reached",
    },
    actions: {
      upgrade: "Upgrade to Premium",
      settings: "Settings",
      help: "Help & Support",
      logout: "Log out",
    },
    dialog: {
      limitReached: "You've reached your monthly transcript limit. Upgrade to Premium for unlimited access!",
      remaining: "You have {remaining} transcripts remaining this month. Upgrade for unlimited access!",
    },
  },
  auth: {
    headings: {
      welcome: "Make today count!",
      start: "Start Your Learning Journey",
      createAccount: "Create Account",
      signIn: "Sign In",
    },
    subheadings: {
      login: "Welcome back! Please enter your details",
      signup: "Enter your details to get started",
      leftLogin: "Continue your language learning adventure with interactive podcasts and AI-powered features.",
      leftSignup: "Learn English effectively with immersive videos and engaging podcast content.",
    },
    form: {
      email: "Email address",
      emailPlaceholder: "Enter your email",
      password: "Password",
      createPassword: "Create password",
      passwordPlaceholderLogin: "Enter your password",
      passwordPlaceholderSignup: "Create a strong password",
      forgotPassword: "Forgot your password?",
      signIn: "Sign in",
      createAccount: "Create account",
      signingIn: "Signing in...",
      creatingAccount: "Creating account...",
      haveAccount: "Already have an account?",
      dontHaveAccount: "Don't have an account?",
      signInInstead: "Sign in instead",
    },
    reset: {
      title: "Reset Password",
      sentTitle: "Check Your Email",
      sentDesc: "We've sent a password reset link to your email address.",
      enterDesc: "Enter your email address and we'll send you a link to reset your password.",
      sentTo: "We sent a password reset link to:",
      notReceived: "Didn't receive the email? Check your spam folder or try again.",
      backToSignIn: "Back to Sign In",
      sendLink: "Send Reset Link",
      sending: "Sending Reset Link...",
      cancel: "Cancel",
    },
    errors: {
      invalidEmail: "Please enter a valid email address.",
      shortPassword: "Password must be at least 6 characters.",
    }
  },
  search: {
    search: "Search",
    trending: "Trending",
    podcast: "Podcast",
    video: "Video",
    allCategories: "All Categories",
    categoryPrefix: "Category:",
    searchingPodcasts: "Searching podcasts...",
    loadingVideos: "Loading videos...",
    errorTitle: "Search Error",
    noResults: "No results found",
    tryAdjustPodcasts: "Try adjusting your search terms or browse trending podcasts instead",
    tryAdjustVideos: "Try adjusting your search terms or browse trending videos instead",
    trendingVideos: "Trending Videos",
    loadingMorePodcasts: "Loading more...",
    endOfResults: "You've reached the end! That's all the podcasts we found.",
    categories: {
      "Arts": "Arts",
      "Business": "Business",
      "Comedy": "Comedy",
      "Education": "Education",
      "Fiction": "Fiction",
      "Government": "Government",
      "Health & Fitness": "Health & Fitness",
      "History": "History",
      "Kids & Family": "Kids & Family",
      "Leisure": "Leisure",
      "Music": "Music",
      "News": "News",
      "Religion & Spirituality": "Religion & Spirituality",
      "Science": "Science",
      "Society & Culture": "Society & Culture",
      "Sports": "Sports",
      "Technology": "Technology",
      "True Crime": "True Crime",
      "TV & Film": "TV & Film",
    },
  },
};

const zh: Dict = {
  hero: {
    goalLabel: "目标",
    minuteShort: "分钟",
    hour: "小时",
    hours: "小时",
  },
  analytics: {
    learnMore: "了解更多",
    chart: {
      practiceTime: "练习时长",
      minutes: "分钟",
      current: "本月",
    },
    cards: {
      today: {
        title: "今日",
        unit: "分钟",
        subtitle: "今日练习时长",
      },
      sessions: {
        title: "次数",
        unit: "次",
        subtitle: "今日跟读次数",
      },
      streak: {
        title: "连续天数",
        unitSingular: "天",
        unitPlural: "天",
        subtitle: "当前连续练习天数",
      },
    },
  },
  shadowing: {
    title: "跟读练习",
    showTranslation: "显示翻译",
    showSentenceHint: "点击眼睛图标显示句子",
    replay: "重播",
    continue: "继续",
    startShadowing: "开始跟读",
    stopRecording: "停止录音",
    transcribeFailed: "转写失败",
  },
  common: {
    loading: "加载中...",
    save: "保存",
    speak: "发音",
    signIn: "登录",
    backToHome: "返回首页",
    optional: "可选",
  },
  installPrompt: {
    title: "将 Longoflows 安装到主屏幕",
    instructions: {
      tap: "点击",
      thenAdd: "然后选择「添加到主屏幕」",
    },
    actions: {
      close: "关闭安装提示",
    },
  },
  navigation: {
    tagline: "你的语言学习平台",
    actions: {
      collapse: "收起侧边栏",
      expand: "展开侧边栏",
    },
    links: {
      home: "首页",
      search: "搜索",
      episodes: "节目",
      course: "课程",
      library: "收藏",
      review: "复习",
      history: "历史",
      settings: "设置",
    },
  },
  upgradeDialog: {
    title: "升级至高级会员",
    subtitle: {
      withEpisode: '解锁「{episodeTitle}」并畅享全部高级内容',
      default: "解锁全部高级内容与功能",
    },
    features: {
      episodes: "访问全部节目",
      ai: "AI 智能学习功能",
      support: "优先客服支持",
    },
    actions: {
      upgrade: "立即升级",
      continueFree: "继续使用免费版",
    },
  },
  tabs: {
    summary: { name: "总结", desc: "AI 智能总结", mobile: "总结" },
    mindmap: { name: "思维导图", desc: "概念可视化", mobile: "导图" },
    transcript: { name: "字幕", desc: "完整节目字幕", mobile: "字幕" },
    vocabulary: { name: "词汇", desc: "关键词与短语", mobile: "词汇" },
    phrase: { name: "词典", desc: "词库语境词典视图", mobile: "词典" },
    quiz: { name: "测验", desc: "互动练习", mobile: "测验" },
  },
  player: {
    freePreview: {
      toastTitle: "试听时长已达上限",
      toastDescription: "升级会员后即可收听完整节目（不限前 2 分钟）。",
      toastUpgradeCta: "立即升级",
    },
  },
  phraseSection: {
    title: "词库词汇卡片",
    subtitle: "查看词库中的词汇在字幕里的出现句子与翻译。",
    truncateNotice: "仅展示前 {count} 个匹配结果",
    actions: {
      openSettings: "前往设置",
    },
    badges: {
      dictionaryMode: "词典模式",
    },
    countLabel: {
      singular: "个词汇",
      plural: "个词汇",
    },
    indicators: {
      cardsLabel: "词汇卡片",
      dictionaryLabel: "当前词库",
      dictionaryMissing: "尚未选择词库",
    },
    jump: "跳转到片段",
    empty: {
      noVocabulary: {
        title: "请先生成词汇",
        description: "生成或导入词汇后即可查看对应的语境卡片。",
      },
      noMatches: {
        title: "暂未找到词库单词",
        description: "当前字幕中还没有出现词库里的词汇。",
      },
      noDictionary: {
        title: "请先选择词库文件",
        description: "在设置中挑选一个词库，我们才能高亮字幕里的学习词汇。",
      },
    },
  },
  quizSection: {
    title: "互动测验",
    description: "练习本集中的重点词汇。",
    empty: {
      title: "暂无测验",
      description: "当词汇关联上练习题后，这里会显示测验内容。",
    },
    stats: {
      questionSingular: "题目",
      questionPlural: "题目",
      countSingular: "题目",
      countPlural: "题目",
    },
    overview: {
      title: "测验概览",
      nextTerm: "下一个词条",
      progress: "进度",
      accuracy: "准确率",
      allCompleted: "所有测验词条已完成",
    },
    toast: {
      noSentence: "测验句子不存在，无法生成音频。",
      audioGenerationFailed: "音频生成失败",
      audioGenerated: "测验音频已生成",
    },
    messages: {
      completed: "太棒了！该词条的跟读已完成。",
    },
    status: {
      completed: "已完成",
      todo: "待完成",
    },
    translation: {
      show: "显示翻译",
      hide: "隐藏翻译",
    },
    shadowing: {
      defaultTitle: "测验跟读练习",
      cta: "跟读练习",
      dialogTitle: "跟读练习",
      instructions: "请听并重复句子",
      showTranslation: "显示翻译",
      hideTranslation: "隐藏翻译",
      audioUnavailable: "尚未生成参考音频，请在测验卡片中先生成。",
      stopRecording: "停止录音",
      startShadowing: "开始跟读",
      yourRecording: "你的录音",
      attemptsLabel: "尝试次数",
    },
  },
  persona: {
    progress: {
      label: "第 {current} 步，共 {total} 步",
    },
    actions: {
      skip: "暂时跳过",
      back: "上一步",
      continue: "继续",
      finish: "完成",
    },
    steps: {
      languages: {
        eyebrow: "步骤 1 · 语言设置",
        title: "你的母语与目标语言是什么？",
        description: "告诉我们你的语言偏好，便于个性化字幕与内容推荐。",
      },
      level: {
        eyebrow: "步骤 2 · 目标语言水平",
        title: "你觉得自己的目标语言水平如何？",
        description: "无需测试，简单自我评估即可。",
      },
      goals: {
        eyebrow: "步骤 3 · 学习目标",
        title: "你学习目标语言的主要目的是什么？",
        description: "挑选最符合你的学习目标，之后随时可以修改。",
      },
      topics: {
        eyebrow: "步骤 4 · 内容兴趣",
        title: "你最喜欢哪类英文视频？",
        description: "我们会根据这些兴趣推荐合适的英文内容。",
      },
      formats: {
        eyebrow: "步骤 5 · 内容形式",
        title: "你偏好哪种视频形式？",
        description: "选择喜欢的风格，我们会混合推荐。",
      },
      session: {
        eyebrow: "步骤 6 · 学习时长",
        title: "每次学习你通常能安排多久？",
        description: "我们会根据时间匹配合适的时长。",
      },
      subtitles: {
        eyebrow: "步骤 7 · 字幕与讲解",
        title: "我们要如何辅助你的学习？",
        description: "选择你想要的字幕与讲解支持。",
      },
    },
    goals: {
      everyday: "日常交流",
      watchYoutube: "无字幕观看 YouTube",
      jobInterview: "求职面试 / 职业发展",
      studyExam: "考试 / 备考（雅思、托福…）",
      travelAbroad: "出国旅行与生活",
    },
    topics: {
      dailyVlog: "日常 vlog / 生活方式",
      aiTech: "科技与 AI",
      career: "职业 & 自我提升",
      comedy: "喜剧与娱乐",
      moviesTv: "电影 / 电视剧",
      news: "新闻与时事",
      gaming: "游戏",
      education: "教育 / 教程",
    },
    levels: {
      rangeHint: "我们会推荐 {from}–{to} 难度的内容。",
      a2: {
        title: "入门：能听懂一些词，但大多视频跟不上。",
        description: "CEFR A2 水平",
      },
      b1: {
        title: "中低级：大部分内容需要字幕辅助。",
        description: "CEFR B1 水平",
      },
      b2: {
        title: "中高级：配合字幕能理解 70–80%。",
        description: "CEFR B2 水平",
      },
      c1: {
        title: "高级：多数视频无需字幕也能理解。",
        description: "CEFR C1 水平",
      },
    },
    formats: {
      vlog: "Vlog / 日常生活",
      interview: "访谈 / 播客",
      podcast: "播客 / 纯音频内容",
      tutorial: "讲解 / 教程",
      shorts: "短视频 / 精彩剪辑",
      movieClip: "电影 / 剧集片段",
    },
    sessionLength: {
      label: "单次学习时长",
      description: "目标时长约 {minutes} 分钟",
      ten: "5–10 分钟",
      twenty: "10–20 分钟",
      thirty: "20–30 分钟",
      fortyFive: "30 分钟以上",
    },
    sessionFrequency: {
      prompt: "你希望多久练习一次？",
      three: "每周练几次",
      five: "几乎每天",
      seven: "每天练习",
    },
    subtitles: {
      alwaysTitle: "始终显示英文字幕",
      alwaysDescription: "默认打开字幕，随时看得更清楚。",
      nativeTitle: "需要时显示母语翻译",
      nativeDescription: "遇到难句可切换母语辅助理解。",
      nativeLanguageLabel: "首选母语",
      nativeLanguagePlaceholder: "选择语言",
      nativeLanguageHint: "这些信息将帮助我们在提供翻译或讲解时使用正确语言。",
      nativeLanguage: {
        auto: "自动检测",
        zh: "中文",
        es: "西班牙语",
        ja: "日语",
        ko: "韩语",
      },
    },
    languages: {
      nativeLabel: "你的母语是什么？",
      targetLabel: "你正在学习哪种语言？",
      targetPlaceholder: "选择目标语言",
      hint: "这些信息将帮助我们匹配内容和难度。",
      native: {
        en: "英语",
        zh: "中文",
        es: "西班牙语",
        ja: "日语",
        ko: "韩语",
        fr: "法语",
        de: "德语",
        pt: "葡萄牙语",
        ru: "俄语",
      },
      target: {
        en: "英语",
        zh: "中文",
        es: "西班牙语",
        ja: "日语",
        ko: "韩语",
        fr: "法语",
        de: "德语",
        pt: "葡萄牙语",
        ru: "俄语",
      },
    },
    explanations: {
      title: "你想要哪些讲解？",
      keyVocab: "每个视频列出重点词汇",
      showPhrases: "标出实用表达与句型",
      translateSentences: "难句提供母语翻译",
      pronunciation: "强调发音 / 跟读技巧",
    },
  },
  vocabQuiz: {
    title: "词汇测验",
    translationUnavailable: "暂无本句翻译",
    loading: "加载中...",
    translation: {
      show: "显示翻译",
      hide: "隐藏翻译",
    },
    actions: {
      replay: "重播",
      continue: "继续",
    },
  },
  mobileImmersive: {
    subtitles: {
      show: "显示字幕",
      hide: "隐藏字幕",
    },
    translation: {
      show: "显示翻译",
      hide: "隐藏翻译",
    },
    narration: {
      enable: "开启讲解模式",
      disable: "关闭讲解模式",
      tooltipEnable: "播放后自动朗读词汇讲解",
      tooltipDisable: "关闭词汇讲解",
      generating: "正在生成讲解音频...",
      failed: "讲解音频生成失败",
      modeActive: "讲解模式已开启",
      playing: "正在播放讲解...",
      ready: "讲解准备就绪",
      skip: "跳过",
      skipTooltip: "跳过讲解并继续播放",
      currentTerm: "当前词汇",
      pause: "暂停讲解",
      resume: "继续讲解",
      repeat: "重播讲解",
    },
    status: {
      new: "新学",
      newDescription: "刚添加，需要首次复习",
      fuzzy: "模糊",
      fuzzyDescription: "需要尽快多练习",
      mastered: "掌握",
      masteredDescription: "很熟练，可拉长复习间隔",
    },
    shadowing: {
      tooltip: "跟读练习",
      dialogTitle: "跟读练习",
      dialogDescription: "请大声重复当前字幕。",
      attemptLabel: "尝试",
      hideText: "隐藏原文",
      showText: "显示原文",
      hideTranslationShort: "隐藏翻译",
      showTranslationShort: "显示翻译",
      transcriptionLabel: "你的转写",
      startRecording: "开始跟读",
      stopRecording: "停止录音",
      start: "开始跟读",
      stop: "停止跟读",
      unknownEpisode: "未知节目",
    },
  },
  vocabulary: {
    status: {
      label: "词汇分类",
      new: "新词",
      newDescription: "刚添加，需要首次复习",
      fuzzy: "待巩固",
      fuzzyDescription: "需要尽快加强练习",
      mastered: "已掌握",
      masteredDescription: "有信心，可拉长复习间隔",
    },
    section: {
      title: "关键词与主题",
      topicCountSingular: "{count} 个主题",
      topicCountPlural: "{count} 个主题",
      revealAllButton: "全部显示",
      revealAllTitle: "显示所有解释",
      restoreBlurButton: "恢复模糊",
      restoreBlurTitle: "恢复模糊",
      levelFilterLabel: "按 CEFR 难度筛选",
      levelFilterUserLevel: "当前等级：{level}",
      levelFilterAll: "全部",
      noTermsMatchFilter: "当前筛选下没有匹配的词条，试试选择更多难度级别。",
    },
    swipeTip: {
      badge: "提示",
      heading: "滑动管理词卡",
      description: "左滑隐藏已掌握的词汇，右滑保留在练习中。",
    },
    desktopActions: {
      markKnown: "标记为已掌握",
      keepLearning: "继续学习",
    },
    card: {
      examplesLabel: "例句",
    },
    stack: {
      viewDetails: "查看详情",
      closeDetails: "收起",
    },
  },
  transcript: {
    title: "字幕",
    generate: "生成字幕",
    generating: "正在生成字幕...",
    generateDesc: "生成字幕后可查看时间轴与交互功能。",
    generatingDesc: "正在为本期内容生成字幕，请稍候。",
    segments: "段",
    usageDeducted: "已使用 1 次字幕额度",
    usageRemaining: "本月还剩 {remaining}/{limit} 次",
    usageRemainingUnlimited: "当前计划的字幕额度不限",
    dynamicIslandSettings: "动态岛设置",
    dynamicIslandTitle: "动态岛",
    dynamicIslandDescription: "自定义学习时在动态岛中显示的内容。",
    dynamicIslandVocabulary: "词汇弹窗",
    dynamicIslandVocabularyHint: "在弹窗中显示词汇提示",
    toggleDynamicIslandVocabulary: "切换词汇弹窗",
    wordMask: "字幕动画",
    toggleWordMask: "切换字幕动画",
  },
  premiumPrompt: {
    guest: {
      title: "需要登录",
      description: "请登录后访问此内容并解锁学习功能。",
    },
    free: {
      title: "高级会员内容",
      description: "本节目需要高级会员订阅才能解锁全部功能。",
      secondary: "或浏览精选节目免费体验。",
    },
    featured: {
      title: "精选节目限时免费",
      description: "喜欢这期节目吗？升级为高级会员即可解锁完整内容库！",
      cta: "了解高级会员",
    },
    fallback: "暂无法访问此内容",
  },
  contentRenderer: {
    alerts: {
      vocabFallback: "生成词汇失败：{error}。已改为使用示例数据。",
    },
  },
  upgradePromptDialog: {
    titles: {
      limitReached: "本月额度已用完",
      upgrade: "升级到高级会员",
      error: "生成失败",
    },
    usage: {
      label: "本月使用情况",
      summary: "已使用 {used}/{limit} 次字幕额度",
      remaining: "剩余 {remaining}/{limit} 次",
      unlimited: "当前高级会员享有无限字幕额度",
    },
    benefits: {
      title: "升级到高级会员",
      items: {
        unlimitedTranscripts: "无限制字幕生成",
        aiSummaries: "可以观看所有Ready的转录视频",
        vocabulary: "完整词汇分析",
        priority: "优先处理速度",
        export: "支持所有格式导出",
      },
    },
    plans: {
      basic: {
        name: "基础会员",
        short: "基础版",
        badge: "热门",
        price: "$12.99",
        period: "/月",
        description: "适合个人学习者",
      },
      premium: {
        name: "高级会员",
        short: "高级版",
        badge: "最超值",
        price: "$19.99",
        period: "/月",
        description: "进阶功能与优先支持",
      },
    },
    actions: {
      upgradeToPlan: "升级到{plan}",
      continueWithPlan: "继续使用{plan}",
      later: "稍后再说",
      close: "关闭",
      tryAgain: "重试",
    },
    status: {
      processing: "处理中...",
    },
    alerts: {
      checkoutFailed: "初始化支付失败，请重试。",
    },
    error: {
      generic: "无法生成字幕",
      loginRequired: "请登录后再生成字幕，我们会保存你的进度。",
    },
    premiumStatus: {
      title: "你正在使用高级会员",
      description: "可享受无限次字幕生成以及全部高级功能。",
    },
  },
  episode: {
    floatingActions: {
      buttons: {
        open: "打开快捷操作",
        collapse: "收起快捷操作",
        showTranslation: "显示翻译",
        hideTranslation: "隐藏翻译",
        generateTranslation: "生成翻译",
        generatingTranslation: "正在生成翻译...",
        generateChosenExplanation: "生成口语化解释",
        generatingChosenExplanation: "正在生成解释",
        enableWordHighlight: "开启单词高亮",
        disableWordHighlight: "关闭单词高亮",
        adjustPracticeMode: "调整练习模式",
        enablePracticeMode: "开启练习模式",
        resetProgress: "重置本集进度",
        backToTop: "返回顶部",
      },
      tooltips: {
        open: "打开快捷操作",
        collapse: "收起快捷操作",
        showTranslation: "显示翻译",
        hideTranslation: "隐藏翻译",
        generateTranslation: "生成翻译",
        generatingTranslation: "生成中...",
        generateChosenExplanation: "生成例句场景解释",
        generatingChosenExplanation: "正在生成解释...",
        dictionaryRequiredTitle: "需要词典",
        dictionaryRequiredDescription: "请先在设置中选择词典以启用高亮。",
        enableWordHighlight: "开启单词高亮",
        disableWordHighlight: "关闭单词高亮",
        practiceSetupTitle: "需要先完成设置",
        practiceSetupDescription: "请先生成词汇或选择词典后再开启练习模式。",
        practiceActiveTitle: "练习模式已开启",
        practiceOpenTitle: "选择练习模式",
        practiceActiveDescription: "当前正在使用 {mode} 模式。",
        practiceOpenDescription: "请选择符合你目标的学习模式。",
        resetProgressTitle: "重置本集进度",
        resetProgressDescription: "仅清除此节目标记完成的单词。",
        backToTop: "返回顶部",
      },
      success: {
        translateGenerated: "翻译生成成功！",
        chosenExplanationGenerated: "口语化解释生成成功！",
      },
      alerts: {
        translateMissingData: "无法生成翻译：缺少必要数据。",
      },
      errors: {
        translateRequestFailed: "翻译请求失败",
        translateResponseInvalid: "翻译响应格式错误",
        translateConsoleError: "翻译生成失败",
        translateFailed: "翻译生成失败，请重试。",
        generateVocabularyFirst: "请先生成词汇内容",
        dictionaryInitConsole: "词库初始化失败",
        dictionaryLoadFailed: "无法加载词库，请重试",
        dictionaryNotSelected: "请先在设置中选择词典",
        dictionaryInitFailed: "词汇管理器初始化失败",
        dictionaryInitUnable: "无法加载词典，请重试",
        chosenExplanationFailed: "生成解释失败",
        chosenExplanationMissing: "无法生成解释：词汇数据尚未准备好。",
        chosenExplanationUnauthorized: "请先登录后再生成解释。",
      },
      practice: {
        noDictionary: "未选择词典",
        spellingSuffix: "拼写练习",
      },
      practiceModes: {
        vocabulary: "词汇练习",
        toefl: "托福",
        shadowing: "跟读练习",
      },
      dialog: {
        title: "重置本集进度",
        description: "这将清除本集所有已掌握的单词并重新开始本集的词汇练习。其他节目的进度不会受到影响。此操作不可撤销。",
        cancel: "取消",
        confirm: "重置进度",
      },
    },
    exportDialog: {
      lockedBadge: "高级功能",
      lockedDescription: "导出功能仅向付费计划开放。升级会员即可下载、复制或分享完整节目内容。",
      title: "导出节目",
      description: "将《{title}》导出为你喜欢的格式",
      quickActionsTitle: "快捷操作",
      downloadTitle: "下载文件",
      quickActions: {
        copyMarkdown: "复制 Markdown",
        copyText: "复制文本",
        copyUrl: "复制链接",
        email: "发送邮件",
      },
      options: {
        markdown: {
          title: "Markdown 文件",
          description: "下载为排版精美的 Markdown (.md)，适合记录与整理笔记",
        },
        text: {
          title: "纯文本文件",
          description: "下载为简单的文本 (.txt)，兼容所有平台",
        },
        json: {
          title: "JSON 数据",
          description: "下载结构化数据 (.json)，适用于开发与自动化",
        },
      },
      episodeInfo: {
        episodeLabel: "节目",
        authorLabel: "作者",
        unknownAuthor: "未知",
        includesPrefix: "包含内容",
        includesSummary: "摘要",
        includesChapters: "章节",
        includesKeyPoints: "要点",
        includesTranscript: "完整文本",
      },
    },
    immersiveFloating: {
      subtitles: {
        show: "显示字幕",
        hide: "隐藏字幕",
      },
      translation: {
        show: "显示卡片翻译",
        hide: "隐藏卡片翻译",
      },
      translationReveal: {
        show: "显示所有卡片翻译",
        hide: "隐藏所有卡片翻译",
      },
      exit: "退出沉浸模式",
    },
    videoPlayer: {
      immersive: {
        enterFocus: "专注模式",
        exitFocus: "退出专注",
        enterImmersive: "沉浸模式",
        exitImmersive: "退出沉浸",
        enterFocusTitle: "进入专注模式",
        exitFocusTitle: "退出专注模式",
        enterImmersiveTitle: "进入沉浸模式",
        exitImmersiveTitle: "退出沉浸模式（Esc）",
      },
      controls: {
        minimize: "最小化播放器",
        maximize: "恢复播放器",
      },
    },
    player: {
      previewLock: {
        badge: "试听限制",
        message: "免费计划仅可试听前 2 分钟，升级会员即可继续收听。",
        messageShort: "试听限制：升级后继续收听",
        tooltip: "免费计划只能试听前 2 分钟",
      },
      freePreview: {
        toastTitle: "试听时长已达上限",
        toastDescription: "升级会员后即可收听完整节目（不限前 2 分钟）。",
        toastUpgradeCta: "立即升级",
      },
    },
  },
  summary: {
    title: "总结",
    noSummary: "暂无总结",
    generate: "生成总结",
    generating: "正在生成总结...",
    generateDesc: "生成 AI 总结，快速了解本期内容要点。",
    generatingDesc: "正在为本期内容生成 AI 总结，请稍候。",
    aiOverview: "AI 生成概览",
    zhTranslation: "中文翻译",
    zhTranslationExtra: "中文翻译（补充）",
    translationLoading: "翻译加载中...",
  },
  home: {
    featured: {
      title: "精选推荐",
      error: "精选内容加载失败",
      refresh: "刷新页面重试",
    },
    following: {
      title: "我的关注",
    },
    episodes: {
      title: "节目",
      viewAll: "查看全部",
      empty: "当前暂无可用节目",
    },
    channels: {
      title: "热门频道",
      viewAll: "查看全部",
    },
    collections: {
      title: "合集",
      viewAll: "查看全部",
      countSuffix: "个节目",
      viewAllInline: "查看全部",
      categories: {
        "1": "科技与人工智能",
        "2": "个人成长与心态",
        "3": "健康与生活方式",
        "4": "商业与理财",
        "5": "全球议题与政治",
        "6": "人际关系与连接",
        "7": "科学与探索",
        "8": "生活方式与 vlog",
        "9": "历史与文化",
        "10": "环境与可持续",
      },
    },
    youtube: {
      title: "YouTube 精选",
      missingThumbnail: "缺少封面",
      playVideo: "播放 {title}",
      defaultCreator: "YouTube Creator",
    },
    free: "免费",
  },
  channel: {
    filters: {
      ariaLabel: "按季筛选节目",
      allSeasons: "全部季数",
      season: "第 {season} 季",
    },
    badges: {
      episodes: "节目",
    },
    empty: {
      title: "未找到节目",
      seasonFiltered: "该季暂无节目。",
      generic: "该频道暂时还没有节目。",
    },
  },
  episodes: {
    title: "热门节目",
    loadingMore: "正在加载更多节目...",
    loadMore: "加载更多",
    noMore: "没有更多节目了",
    noneTitle: "暂无节目",
    noneDesc: "当前暂无热门节目。",
    errorTitle: "加载节目出错",
    tryAgain: "重试",
  },
  library: {
    title: "我的库",
    tabs: {
      favorites: "收藏",
      playlists: "播放列表",
      podcasts: "播客订阅",
    },
    emptyFavoritesTitle: "还没有收藏",
    emptyFavoritesDesc: "你标星的节目会出现在这里",
    browseEpisodes: "浏览节目",
    playlistEmpty: "该播放列表暂无内容",
    playlistsEmptyTitle: "还没有播放列表",
    playlistsEmptyDesc: "创建播放列表来管理你的节目",
    podcastsEmptyTitle: "还没有播客订阅",
    podcastsEmptyDesc: "关注播客后会显示在这里",
    discoverPodcasts: "发现播客",
    playlistDialog: {
      title: "添加到播放列表",
      description: "创建或选择一个播放列表添加此节目。",
      addingLabel: "正在添加：",
      nameLabel: "播放列表名称 *",
      namePlaceholder: "请输入播放列表名称...",
      descriptionLabel: "描述（可选）",
      descriptionPlaceholder: "添加一个描述...",
      cancel: "取消",
      creating: "创建中...",
      createAction: "创建播放列表",
      yourPlaylists: "你的播放列表",
      createNew: "创建新播放列表",
      createFirst: "创建第一个播放列表",
      episodesLabel: "个节目",
    },
  },
  history: {
    title: "收听历史",
    subtitle: "查看学习进度，继续上次进度",
    loading: "加载中...",
    loginRequiredTitle: "需要登录",
    loginRequiredDesc: "请先登录以查看你的收听历史。",
    noHistoryTitle: "暂无历史记录",
    noHistoryAll: "你还没有收听任何节目。",
    noHistoryFiltered: (filter: string) => `暂无${filter}的节目。` as any,
    completed: "已完成",
    left: "剩余",
    watchAgain: "重新播放",
    continue: "继续",
    view: "查看",
  },
  course: {
    index: {
      title: "课程",
      subtitle: "探索精心策划的课程，帮助你更快提升。",
      cards: {
        idioms: {
          provider: "Lingoflows",
          title: "最常见的美式习语",
          description: "通过日常示例和讲解掌握地道习语。",
          coverAlt: "最常见的美式习语封面",
        },
      },
    },
    detail: {
      headerTitle: "课程",
      menuButton: "打开菜单",
      breadcrumb: {
        courses: "课程",
      },
      actions: {
        continue: "继续学习",
      },
      pagination: "第 {page} / {totalPages} 页 • 共 {total}{unit}",
      paginationPrev: "上一页",
      paginationNext: "下一页",
      totalBadge: "{total}{unit}",
      idiomUnit: "个习语",
      errors: {
        load: "无法加载习语：{message}",
      },
      courses: {
        idioms: {
          title: "最常见的美式习语",
          tagline: "一起掌握地道习语！",
          provider: "Lingoflows",
          coverAlt: "最常见的美式习语封面",
        },
        default: {
          title: "课程",
          tagline: "",
          provider: "",
          coverAlt: "课程封面",
        },
      },
    },
    common: {
      previous: "上一条",
      next: "下一条",
    },
    progress: {
      headerLabel: "学习进度",
      loading: "加载中",
      status: {
        completed: "已完成",
        todo: "待完成",
      },
      completedTooltip: "已完成",
      exampleAria: "示例 {index} {status}",
      defaultCourseLabel: "课程",
    },
    freePlanLimit: {
      title: "升级即可解锁此课程",
      description: "免费计划仅可查看前 10 个习语，升级会员即可继续学习完整课程。",
      lockedCardLabel: "升级解锁此课程",
      lockedBanner: "升级解锁",
      lockedHeading: "升级后继续学习",
      lockedMessage: "免费计划仅可查看前 10 个习语。升级会员即可解锁包括第 #{number} 课在内的全部内容。",
      backToList: "返回课程总览",
    },
    idiomDetail: {
      breadcrumb: {
        courses: "课程",
      },
      sidebar: {
        idiomLabel: "习语编号",
        difficulty: "难度",
        examples: "示例",
        backToList: "返回列表",
      },
      translationToggle: "翻译",
      untitled: "未命名",
      examples: {
        title: "示例练习",
        description: "跟读每个示例并录音，完成本章节的练习。",
        empty: "该习语暂时没有示例。",
      },
      errors: {
        load: "无法加载习语详情，请稍后重试。",
      },
    },
    examples: {
      transcribing: "转写中...",
    },
  },
  savedWords: {
    title: "复习",
    countSuffix: "个生词",
    selectedSuffix: "项已选",
    blur: {
      show: "显示释义",
      hide: "模糊释义",
    },
    play: {
      playing: "播放中...",
      playSelected: "播放所选",
      playAll: "播放全部",
    },
    export: {
      label: "导出",
      json: "导出 JSON",
      csv: "导出 CSV",
      jsonShort: "JSON",
      csvShort: "CSV",
    },
    selectMode: {
      enter: "多选",
      exit: "完成",
      selectAll: "全选",
      clear: "清除",
    },
    filters: {
      all: "全部",
    },
    sort: {
      newest: "最新优先",
      oldest: "最旧优先",
      alphabetical: "按字母排序",
    },
    reviewNow: "立即复习",
    due: "个待复习",
    onDeck: "个待学习",
    status: {
      new: "新词",
      fuzzy: "模糊",
      mastered: "已掌握",
    },
    next: "下次",
    interval: "间隔",
    correct: "连续正确",
    wrong: "连续错误",
    reviewed: "上次复习",
    explains: "释义",
    reviewAgain: "再试",
    reviewHard: "较难",
    reviewGood: "良好",
    reviewEasy: "太容易",
    reviewAllCaughtUp: "全部完成！",
    reviewAllCaughtUpHint: "目前没有需要复习的卡片，稍后再来或添加新单词。",
    empty: {
      title: "未找到单词",
      filterHint: "试试调整搜索词或筛选条件。",
      defaultHint: "观看节目时保存单词吧！",
    },
    loginRequiredTitle: "请登录",
    loginRequiredDesc: "登录后即可查看已保存的单词。",
    searchPlaceholder: "搜索单词...",
    sources: {
      youdao: "有道",
      original: "原文",
    },
    relative: {
      now: "现在",
      dueSoon: "即将到期",
      justReviewed: "刚刚复习过",
      in: "{value}{unit}后",
      ago: "{value}{unit}前",
      today: "今天",
      yesterday: "昨天",
    },
    selectionBar: {
      selectedSuffix: "项已选",
      delete: "删除所选",
    },
  },
  settings: {
    title: "设置",
    nativeLanguage: "母语设置",
    nativeLanguageHint: "选择你的母语用于翻译和界面显示",
    theme: {
      title: "外观主题",
      hint: "选择你喜欢的颜色主题",
      light: "浅色",
      lightDesc: "浅色主题",
      dark: "深色",
      darkDesc: "深色主题",
      system: "跟随系统",
      systemDesc: "跟随系统设置",
    },
    cefrLevel: {
      title: "CEFR 等级",
      description: "选择最适合你的 CEFR 学习阶段，用于个性化内容推荐。",
      unset: "未设置",
      saving: "正在保存 CEFR 等级...",
    },
    interfaceLanguage: "界面语言",
    interfaceLanguageHint: "选择应用界面的显示语言。此设置与母语设置相互独立。",
    videoTitles: {
      title: "视频标题显示",
      description: "当有中文翻译时，在首页优先显示中文标题。",
      chineseLabel: "显示中文标题",
      originalLabel: "显示原始标题",
      saving: "正在保存偏好...",
    },
    vocabularyDict: "词汇词典",
    remove: "移除",
    removing: "正在移除...",
    selectVocabularyHint: "根据需要选择要学习的词汇库",
    dailyGoal: {
      title: "每日跟读目标",
      desc: "设定每天的练习时长，并快速查看今日进度",
      targetMinutes: "目标分钟数",
      minutesLabel: "分钟",
      progressPrefix: "今日进度：",
      remainingPrefix: "还差",
      minAbbr: "分钟",
      completedPrefix: "今日完成",
      todayWord: "",
      saving: "保存中...",
      saved: "已保存",
      loadFailed: "目标加载失败",
      saveFailed: "保存失败",
    },
    examVocab: {
      title: "考试词汇",
      subtitle: "专业考试和标准化测试词汇库",
    },
  },
  user: {
    plan: {
      premium: "高级会员",
      free: "免费计划",
    },
    badge: {
      premiumMember: "高级会员",
      unlimited: "无限制",
    },
    usage: {
      transcriptsLabel: "条字幕",
      remaining: "本月剩余 {remaining} 次生成",
      limitReached: "本月额度已用完",
    },
    actions: {
      upgrade: "升级为高级会员",
      settings: "设置",
      help: "帮助与支持",
      logout: "退出登录",
    },
    dialog: {
      limitReached: "您的本月字幕生成额度已用完，升级为高级会员解锁无限生成！",
      remaining: "您本月还剩 {remaining} 次，升级后可无限使用！",
    },
  },
  auth: {
    headings: {
      welcome: "欢迎回来！",
      start: "开始你的学习之旅",
      createAccount: "创建账号",
      signIn: "登录",
    },
    subheadings: {
      login: "欢迎回来！请输入你的账号信息",
      signup: "输入你的信息开始使用",
      leftLogin: "结合互动播客与 AI 功能，继续你的语言学习之旅。",
      leftSignup: "通过沉浸式视频与优质播客内容，高效学习英语。",
    },
    form: {
      email: "邮箱地址",
      emailPlaceholder: "请输入邮箱",
      password: "密码",
      createPassword: "创建密码",
      passwordPlaceholderLogin: "请输入密码",
      passwordPlaceholderSignup: "创建一个安全的密码",
      forgotPassword: "忘记密码？",
      signIn: "登录",
      createAccount: "创建账号",
      signingIn: "正在登录...",
      creatingAccount: "正在创建账号...",
      haveAccount: "已经有账号？",
      dontHaveAccount: "还没有账号？",
      signInInstead: "去登录",
    },
    reset: {
      title: "重置密码",
      sentTitle: "请检查邮箱",
      sentDesc: "我们已向你的邮箱发送了重置密码链接。",
      enterDesc: "输入你的邮箱地址，我们会发送重置链接。",
      sentTo: "重置链接已发送至：",
      notReceived: "没有收到邮件？检查垃圾箱或重试。",
      backToSignIn: "返回登录",
      sendLink: "发送重置链接",
      sending: "正在发送重置链接...",
      cancel: "取消",
    },
    errors: {
      invalidEmail: "请输入有效的邮箱地址。",
      shortPassword: "密码长度至少 6 位。",
    }
  },
  search: {
    search: "搜索",
    trending: "趋势",
    podcast: "播客",
    video: "视频",
    allCategories: "全部分类",
    categoryPrefix: "分类：",
    searchingPodcasts: "正在搜索播客...",
    loadingVideos: "正在加载视频...",
    errorTitle: "搜索出错",
    noResults: "未找到结果",
    tryAdjustPodcasts: "试试调整搜索词，或浏览趋势播客",
    tryAdjustVideos: "试试调整搜索词，或浏览趋势视频",
    trendingVideos: "趋势视频",
    loadingMorePodcasts: "正在加载更多播客...",
    endOfResults: "已到达末尾，暂无更多播客。",
    categories: {
      "Arts": "艺术",
      "Business": "商业",
      "Comedy": "喜剧",
      "Education": "教育",
      "Fiction": "小说",
      "Government": "政府",
      "Health & Fitness": "健康与健身",
      "History": "历史",
      "Kids & Family": "儿童与家庭",
      "Leisure": "休闲",
      "Music": "音乐",
      "News": "新闻",
      "Religion & Spirituality": "宗教与灵性",
      "Science": "科学",
      "Society & Culture": "社会与文化",
      "Sports": "体育",
      "Technology": "科技",
      "True Crime": "真实犯罪",
      "TV & Film": "电视与电影",
    },  },
};

// Spanish (es) translations
const es: Dict = {
  hero: {
    goalLabel: "Objetivo",
    minuteShort: "min",
    hour: "hora",
    hours: "horas",
  },
  analytics: {
    learnMore: "Aprender más",
    chart: {
      practiceTime: "Tiempo de práctica",
      minutes: "minutos",
      current: "Actual",
    },
    cards: {
      today: {
        title: "Hoy",
        unit: "min",
        subtitle: "Tiempo de práctica de hoy",
      },
      sessions: {
        title: "Sesiones",
        unit: "prácticas",
        subtitle: "Practicar shadowing hoy",
      },
      streak: {
        title: "Racha",
        unitSingular: "día",
        unitPlural: "días",
        subtitle: "Racha de práctica actual",
      },
    },
  },
  upgradeDialog: {
    title: "Actualizar a Premium",
    subtitle: {
      withEpisode: 'Accede a "{episodeTitle}" y desbloquea todo el contenido premium',
      default: "Desbloquea todo el contenido y funciones premium",
    },
    features: {
      episodes: "Acceso a todos los episodios",
      ai: "Funciones de aprendizaje con IA",
      support: "Soporte prioritario",
    },
    actions: {
      upgrade: "Actualizar ahora",
      continueFree: "Continuar con acceso gratuito",
    },
  },
  shadowing: {
    title: "Práctica de Shadowing",
    showTranslation: "Mostrar traducción",
    showSentenceHint: "Haz clic en el icono del ojo para mostrar la oración",
    replay: "Repetir",
    continue: "Continuar",
    startShadowing: "Iniciar Shadowing",
    stopRecording: "Detener grabación",
    transcribeFailed: "Error al transcribir",
  },
  common: {
    loading: "Cargando...",
    save: "Guardar",
    speak: "Hablar",
    signIn: "Iniciar sesión",
    backToHome: "Volver al inicio",
    optional: "Opcional",
  },
  installPrompt: {
    title: "Instalar Longoflows en tu dispositivo",
    instructions: {
      tap: "Toca",
      thenAdd: 'luego "Añadir a la pantalla de inicio"',
    },
    actions: {
      close: "Cerrar aviso de instalación",
    },
  },
  navigation: {
    tagline: "Tu plataforma de aprendizaje de idiomas",
    actions: {
      collapse: "Contraer barra lateral",
      expand: "Expandir barra lateral",
    },
    links: {
      home: "Inicio",
      search: "Buscar",
      episodes: "Episodios",
      course: "Curso",
      library: "Biblioteca",
      review: "Revisar",
      history: "Historial",
      settings: "Configuración",
    },
  },
  tabs: {
    summary: { name: "Resumen", desc: "Resumen del episodio con IA", mobile: "Resumen" },
    mindmap: { name: "Mapa mental", desc: "Mapeo visual de conceptos", mobile: "Mapa" },
    transcript: { name: "Transcripción", desc: "Transcripción completa del episodio", mobile: "Texto" },
    vocabulary: { name: "Vocabulario", desc: "Términos y frases clave", mobile: "Vocab" },
    phrase: { name: "Diccionario", desc: "Vocabulario en vista de diccionario", mobile: "Dict" },
    quiz: { name: "Quiz", desc: "Práctica interactiva", mobile: "Quiz" },
  },
  player: {
    freePreview: {
      toastTitle: "Límite de vista previa gratuita alcanzado",
      toastDescription: "Actualiza para continuar escuchando más allá de los primeros 2 minutos.",
      toastUpgradeCta: "Actualizar",
    },
  },
  phraseSection: {
    title: "Vocabulario en Diccionario",
    subtitle: "Ve cada palabra del diccionario exactamente donde aparece en la transcripción.",
    truncateNotice: "Mostrando los primeros {count} resultados",
    actions: {
      openSettings: "Abrir configuración",
    },
    badges: {
      dictionaryMode: "Modo diccionario",
    },
    countLabel: {
      singular: "palabras",
      plural: "palabras",
    },
    indicators: {
      cardsLabel: "Vocabulario",
      dictionaryLabel: "Diccionario",
      dictionaryMissing: "Diccionario no seleccionado",
    },
    jump: "Saltar al clip",
    empty: {
      noVocabulary: {
        title: "Vocabulario requerido",
        description: "Genera o importa vocabulario para explorar frases contextuales.",
      },
      noMatches: {
        title: "Aún no hay frases contextuales",
        description: "Los términos del vocabulario aún no han aparecido en la transcripción.",
      },
      noDictionary: {
        title: "Elige un diccionario para desbloquear resaltados",
        description: "Selecciona un diccionario de vocabulario en configuración para que podamos identificar las palabras que has aprendido.",
      },
    },
  },
  quizSection: {
    title: "Quiz Interactivo",
    description: "Practica términos clave extraídos de este episodio.",
    empty: {
      title: "Aún no hay quiz disponible",
      description: "Las preguntas del quiz aparecerán aquí una vez que el vocabulario tenga elementos de práctica vinculados.",
    },
    stats: {
      questionSingular: "pregunta",
      questionPlural: "preguntas",
      countSingular: "Pregunta",
      countPlural: "Preguntas",
    },
    overview: {
      title: "Resumen del Quiz",
      nextTerm: "Siguiente término",
      progress: "Progreso",
      accuracy: "Precisión",
      allCompleted: "Todos los elementos del quiz completados",
    },
    toast: {
      noSentence: "Oración del quiz no disponible para generación de audio.",
      audioGenerationFailed: "Error al generar audio",
      audioGenerated: "Audio del quiz generado",
    },
    messages: {
      completed: "¡Excelente trabajo! Este término está completamente practicado.",
    },
    status: {
      completed: "Completado",
      todo: "Por hacer",
    },
    translation: {
      show: "Mostrar traducción",
      hide: "Ocultar traducción",
    },
    shadowing: {
      defaultTitle: "Práctica de Quiz del Episodio",
      cta: "Shadowing",
      dialogTitle: "Práctica de Shadowing",
      instructions: "Escucha y repite la oración",
      showTranslation: "Mostrar traducción",
      hideTranslation: "Ocultar traducción",
      audioUnavailable: "Audio de referencia aún no disponible. Genéralo desde la tarjeta del quiz para escuchar.",
      stopRecording: "Detener grabación",
      startShadowing: "Iniciar Shadowing",
      yourRecording: "Tu grabación",
      attemptsLabel: "Intentos",
    },
  },
  persona: {
    progress: {
      label: "Paso {current} de {total}",
    },
    actions: {
      skip: "Omitir por ahora",
      back: "Atrás",
      continue: "Continuar",
      finish: "Finalizar",
    },
    steps: {
      languages: {
        eyebrow: "Paso 1 · Idiomas",
        title: "¿Cuáles son tus idiomas nativo y objetivo?",
        description: "Cuéntanos tus idiomas preferidos para personalizar las traducciones.",
      },
      level: {
        eyebrow: "Paso 2 · Nivel Objetivo",
        title: "¿Cómo calificarías tu nivel en el idioma objetivo?",
        description: "Solo una autoevaluación aproximada, no se necesita prueba.",
      },
      goals: {
        eyebrow: "Paso 3 · Objetivos",
        title: "¿Cuál es tu objetivo principal para aprender tu idioma objetivo?",
        description: "Elige los objetivos que mejor coincidan con tu viaje. Puedes ajustarlos en cualquier momento.",
      },
      topics: {
        eyebrow: "Paso 4 · Temas / Intereses",
        title: "¿Qué tipo de videos disfrutas más?",
        description: "Recomendaremos videos en inglés basados en estos temas.",
      },
      formats: {
        eyebrow: "Paso 5 · Formatos de Video",
        title: "¿Qué tipo de formato de video te gusta?",
        description: "Elige los estilos que disfrutas. Los mezclaremos en tus recomendaciones.",
      },
      session: {
        eyebrow: "Paso 6 · Duración de la Sesión",
        title: "¿Cuánto tiempo sueles tener para una sesión?",
        description: "Ajustaremos la duración del video a tu horario.",
      },
      subtitles: {
        eyebrow: "Paso 7 · Subtítulos y Explicación",
        title: "¿Cómo deberíamos apoyar tu aprendizaje?",
        description: "Elige los subtítulos y explicaciones que prefieras.",
      },
    },
    goals: {
      everyday: "Conversación cotidiana",
      watchYoutube: "Ver YouTube sin subtítulos",
      jobInterview: "Entrevistas de trabajo y carrera",
      studyExam: "Estudiar / exámenes (IELTS, TOEFL…)",
      travelAbroad: "Viajar y vivir en el extranjero",
    },
    topics: {
      dailyVlog: "Vlog diario y estilo de vida",
      aiTech: "Tecnología e IA",
      career: "Carrera y superación personal",
      comedy: "Comedia y entretenimiento",
      moviesTv: "Películas y programas de TV",
      news: "Noticias y eventos actuales",
      gaming: "Videojuegos",
      education: "Educación y tutoriales",
    },
    levels: {
      rangeHint: "Apuntaremos a videos en el rango {from}–{to}.",
      a2: {
        title: "Principiante – Conozco algunas palabras pero no puedo seguir la mayoría de los videos.",
        description: "Nivel CEFR básico",
      },
      b1: {
        title: "Pre-intermedio – Necesito subtítulos para seguir la mayoría del contenido.",
        description: "Pre-intermedio",
      },
      b2: {
        title: "Intermedio – Con subtítulos entiendo 70–80%.",
        description: "Intermedio alto",
      },
      c1: {
        title: "Avanzado – Puedo seguir la mayoría de los videos sin subtítulos.",
        description: "Avanzado",
      },
    },
    formats: {
      vlog: "Vlogs / vida diaria",
      interview: "Entrevistas y podcasts",
      podcast: "Podcasts / historias de audio",
      tutorial: "Explicaciones / tutoriales",
      shorts: "Clips cortos / destacados",
      movieClip: "Escenas de películas / programas de TV",
    },
    sessionLength: {
      label: "Duración de la sesión",
      description: "Objetivo de {minutes} min",
      ten: "5–10 minutos",
      twenty: "10–20 minutos",
      thirty: "20–30 minutos",
      fortyFive: "30+ minutos",
    },
    sessionFrequency: {
      prompt: "¿Con qué frecuencia quieres practicar?",
      three: "Algunas veces por semana",
      five: "Casi todos los días",
      seven: "Todos los días",
    },
    subtitles: {
      alwaysTitle: "Mostrar siempre subtítulos en inglés",
      alwaysDescription: "Mantén los subtítulos activados por defecto para cada video.",
      nativeTitle: "Mostrar traducción en mi idioma cuando necesite ayuda",
      nativeDescription: "Activa el soporte en idioma nativo cuando te quedes atascado.",
      nativeLanguageLabel: "Idioma nativo preferido",
      nativeLanguagePlaceholder: "Seleccionar idioma",
      nativeLanguageHint: "Usaremos esto al mostrar traducciones o explicaciones.",
      nativeLanguage: {
        auto: "Detección automática",
        zh: "Chino",
        es: "Español",
        ja: "Japonés",
        ko: "Coreano",
      },
    },
    languages: {
      nativeLabel: "¿Cuál es tu idioma nativo?",
      targetLabel: "¿Qué idioma estás aprendiendo?",
      targetPlaceholder: "Seleccionar idioma objetivo",
      hint: "Usaremos esto para hacer coincidir lecciones y guía de nivel.",
      native: {
        en: "Inglés",
        zh: "Chino",
        es: "Español",
        ja: "Japonés",
        ko: "Coreano",
        fr: "Francés",
        de: "Alemán",
        pt: "Portugués",
        ru: "Ruso",
      },
      target: {
        en: "Inglés",
        zh: "Chino",
        es: "Español",
        ja: "Japonés",
        ko: "Coreano",
        fr: "Francés",
        de: "Alemán",
        pt: "Portugués",
        ru: "Ruso",
      },
    },
    explanations: {
      title: "Explicaciones que quieres ver",
      keyVocab: "Mostrar vocabulario clave para cada video",
      showPhrases: "Resaltar frases y expresiones útiles",
      translateSentences: "Traducir oraciones difíciles a tu idioma nativo",
      pronunciation: "Enfocarse en pronunciación y shadowing",
    },
  },
  vocabQuiz: {
    title: "Quiz de Vocabulario",
    translationUnavailable: "Traducción no disponible para este segmento",
    loading: "Cargando...",
    translation: {
      show: "Mostrar traducción",
      hide: "Ocultar traducción",
    },
    actions: {
      replay: "Repetir",
      continue: "Continuar",
    },
  },
  mobileImmersive: {
    subtitles: {
      show: "Mostrar subtítulos",
      hide: "Ocultar subtítulos",
    },
    translation: {
      show: "Mostrar traducción",
      hide: "Ocultar traducción",
    },
    narration: {
      enable: "Habilitar Modo Narración",
      disable: "Deshabilitar Modo Narración",
      tooltipEnable: "Reproducir automáticamente explicaciones de vocabulario después de la reproducción",
      tooltipDisable: "Deshabilitar narración de vocabulario",
      generating: "Generando audio de narración...",
      failed: "Error al generar narración",
      modeActive: "Modo Narración Activo",
      playing: "Reproduciendo narración...",
      ready: "Narración lista",
      skip: "Omitir",
      skipTooltip: "Omitir narración y continuar video",
      currentTerm: "Término actual",
      pause: "Pausar",
      resume: "Reanudar",
      repeat: "Repetir",
    },
    status: {
      new: "Nuevo",
      newDescription: "Recién añadido, necesita primera revisión",
      fuzzy: "Difuso",
      fuzzyDescription: "Necesita más práctica pronto",
      mastered: "Dominado",
      masteredDescription: "Seguro y en revisión larga",
    },
    shadowing: {
      tooltip: "Práctica de shadowing",
      dialogTitle: "Práctica de Shadowing",
      dialogDescription: "Repite el subtítulo actual en voz alta.",
      attemptLabel: "Intento",
      hideText: "Ocultar texto",
      showText: "Mostrar texto",
      hideTranslationShort: "Ocultar traducción",
      showTranslationShort: "Mostrar traducción",
      transcriptionLabel: "Tu transcripción",
      startRecording: "Iniciar shadowing",
      stopRecording: "Detener grabación",
      start: "Iniciar shadowing",
      stop: "Detener shadowing",
      unknownEpisode: "Episodio desconocido",
    },
  },
  vocabulary: {
    status: {
      label: "Clasificación de vocabulario",
      new: "Nuevo",
      newDescription: "Recién añadido, necesita primera revisión",
      fuzzy: "Difuso",
      fuzzyDescription: "Necesita más práctica pronto",
      mastered: "Dominado",
      masteredDescription: "Seguro y en revisión larga",
    },
    section: {
      title: "Palabras clave y temas",
      topicCountSingular: "{count} tema",
      topicCountPlural: "{count} temas",
      revealAllButton: "Revelar todo",
      revealAllTitle: "Revelar todas las explicaciones",
      restoreBlurButton: "Restaurar desenfoque",
      restoreBlurTitle: "Restaurar desenfoque",
      levelFilterLabel: "Filtrar por nivel CEFR",
      levelFilterUserLevel: "Tu nivel: {level}",
      levelFilterAll: "Todos",
      noTermsMatchFilter: "Aún no hay términos que coincidan con los niveles CEFR seleccionados. Intenta seleccionar niveles adicionales.",
    },
    swipeTip: {
      badge: "Consejo",
      heading: "Desliza para gestionar esta pila",
      description: "Desliza a la izquierda para ocultar palabras que conoces, o a la derecha para mantenerlas en tu flujo de práctica.",
    },
    desktopActions: {
      markKnown: "Marcar como conocido",
      keepLearning: "Seguir practicando",
    },
    card: {
      examplesLabel: "Ejemplos",
    },
    stack: {
      viewDetails: "Ver detalles",
      closeDetails: "Cerrar",
    },
  },
  transcript: {
    title: "Transcripción",
    generate: "Generar Transcripción",
    generating: "Generando Transcripción...",
    generateDesc: "Genera una transcripción para ver el texto completo con marcas de tiempo y funciones interactivas.",
    generatingDesc: "Por favor espera mientras generamos la transcripción para este episodio.",
    segments: "segmentos",
    usageDeducted: "Crédito de transcripción usado",
    usageRemaining: "{remaining}/{limit} restantes este mes",
    usageRemainingUnlimited: "Transcripciones ilimitadas con tu plan",
    dynamicIslandSettings: "Configuración de Dynamic Island",
    dynamicIslandTitle: "Dynamic Island",
    dynamicIslandDescription: "Controla lo que aparece en el Dynamic Island mientras aprendes.",
    dynamicIslandVocabulary: "Popup de vocabulario",
    dynamicIslandVocabularyHint: "Mostrar avisos de vocabulario dentro de la superposición popup",
    toggleDynamicIslandVocabulary: "Alternar popup de vocabulario",
    wordMask: "Animación de subtítulos",
    toggleWordMask: "Alternar animación de subtítulos",
  },
  premiumPrompt: {
    guest: {
      title: "Inicio de sesión requerido",
      description: "Por favor inicia sesión para acceder a este contenido y desbloquear funciones de aprendizaje.",
    },
    free: {
      title: "Contenido premium",
      description: "Este episodio requiere una suscripción premium para acceder a todas las funciones.",
      secondary: "O consulta nuestros episodios destacados para acceso gratuito.",
    },
    featured: {
      title: "Contenido destacado gratuito",
      description: "¿Disfrutando este episodio gratuito? ¡Actualiza a premium para desbloquear nuestra biblioteca completa de contenido!",
      cta: "Aprender sobre Premium",
    },
    fallback: "Acceso restringido",
  },
  contentRenderer: {
    alerts: {
      vocabFallback: "Error al generar vocabulario: {error}. Usando datos de demostración en su lugar.",
    },
  },
  upgradePromptDialog: {
    titles: {
      limitReached: "Límite Mensual Alcanzado",
      upgrade: "Actualizar a Premium",
      error: "Error de Generación",
    },
    usage: {
      label: "Uso Mensual",
      summary: "{used}/{limit} transcripciones usadas este mes",
      remaining: "{remaining}/{limit} restantes",
      unlimited: "Transcripciones ilimitadas con tu plan premium",
    },
    benefits: {
      title: "Actualizar a Premium",
      items: {
        unlimitedTranscripts: "Generación ilimitada de transcripciones",
        aiSummaries: "Ver todos los videos transcritos Ready",
        vocabulary: "Análisis completo de vocabulario",
        priority: "Procesamiento prioritario",
        export: "Exportar a todos los formatos",
      },
    },
    plans: {
      basic: {
        name: "Plan Básico",
        short: "Básico",
        badge: "Popular",
        price: "$12.99",
        period: "/mes",
        description: "Perfecto para estudiantes individuales",
      },
      premium: {
        name: "Plan Premium",
        short: "Premium",
        badge: "Mejor Valor",
        price: "$19.99",
        period: "/mes",
        description: "Funciones avanzadas y soporte prioritario",
      },
    },
    actions: {
      upgradeToPlan: "Actualizar a {plan}",
      continueWithPlan: "Continuar con {plan}",
      later: "Más tarde",
      close: "Cerrar",
      tryAgain: "Intentar de nuevo",
    },
    status: {
      processing: "Procesando...",
    },
    alerts: {
      checkoutFailed: "Error al iniciar el pago. Por favor intenta de nuevo.",
    },
    error: {
      generic: "No se puede generar la transcripción",
      loginRequired: "Por favor inicia sesión para generar transcripciones. Tu progreso se guardará.",
    },
    premiumStatus: {
      title: "Tienes un Plan Premium",
      description: "Disfruta de generación ilimitada de transcripciones y todas las funciones premium.",
    },
  },
  episode: {
    floatingActions: {
      buttons: {
        open: "Abrir acciones rápidas",
        collapse: "Contraer acciones rápidas",
        showTranslation: "Mostrar traducción",
        hideTranslation: "Ocultar traducción",
        generateTranslation: "Generar traducción",
        generatingTranslation: "Generando traducción...",
        generateChosenExplanation: "Generar explicaciones con IA",
        generatingChosenExplanation: "Generando explicaciones",
        enableWordHighlight: "Habilitar resaltado de palabras",
        disableWordHighlight: "Deshabilitar resaltado de palabras",
        adjustPracticeMode: "Ajustar modo de práctica",
        enablePracticeMode: "Habilitar modo de práctica",
        resetProgress: "Restablecer progreso del episodio",
        backToTop: "Volver arriba",
      },
      tooltips: {
        open: "Abrir Acciones Rápidas",
        collapse: "Contraer Acciones Rápidas",
        showTranslation: "Mostrar Traducción",
        hideTranslation: "Ocultar Traducción",
        generateTranslation: "Generar Traducción",
        generatingTranslation: "Generando...",
        generateChosenExplanation: "Generar explicación basada en ejemplos",
        generatingChosenExplanation: "Generando explicaciones...",
        dictionaryRequiredTitle: "Diccionario requerido",
        dictionaryRequiredDescription: "Configura un diccionario en Configuración para desbloquear resaltados.",
        enableWordHighlight: "Habilitar Resaltado de Palabras",
        disableWordHighlight: "Deshabilitar Resaltado de Palabras",
        practiceSetupTitle: "Configuración requerida",
        practiceSetupDescription: "Genera vocabulario o elige un diccionario para comenzar el modo de práctica.",
        practiceActiveTitle: "Modo de práctica activo",
        practiceOpenTitle: "Abrir modos de práctica",
        practiceActiveDescription: "Actualmente usando modo {mode}.",
        practiceOpenDescription: "Elige un modo de aprendizaje adaptado a tu objetivo.",
        resetProgressTitle: "Restablecer progreso del episodio",
        resetProgressDescription: "Limpiar palabras dominadas solo para este episodio.",
        backToTop: "Volver arriba",
      },
      success: {
        translateGenerated: "¡Traducción generada con éxito!",
        chosenExplanationGenerated: "¡Explicaciones generadas con éxito!",
      },
      alerts: {
        translateMissingData: "No se puede generar traducción: faltan datos requeridos.",
      },
      errors: {
        translateRequestFailed: "Error en la solicitud de traducción",
        translateResponseInvalid: "Error de formato de respuesta de traducción",
        translateConsoleError: "Error al generar traducción",
        translateFailed: "Error al generar traducción, por favor intenta de nuevo.",
        generateVocabularyFirst: "Por favor genera vocabulario primero",
        dictionaryInitConsole: "Error al inicializar diccionario",
        dictionaryLoadFailed: "No se puede cargar el diccionario, por favor intenta de nuevo",
        dictionaryNotSelected: "Por favor selecciona un diccionario de vocabulario en Configuración primero",
        dictionaryInitFailed: "Error al inicializar el gestor de vocabulario",
        dictionaryInitUnable: "No se puede cargar el diccionario de vocabulario, por favor intenta de nuevo",
        chosenExplanationFailed: "Error al generar explicaciones",
        chosenExplanationMissing: "No se pueden generar explicaciones: el vocabulario no está listo.",
        chosenExplanationUnauthorized: "Por favor inicia sesión para generar explicaciones.",
      },
      practice: {
        noDictionary: "Sin Diccionario",
        spellingSuffix: "Ortografía",
      },
      practiceModes: {
        vocabulary: "Vocabulario",
        toefl: "TOEFL",
        shadowing: "Shadowing",
      },
      dialog: {
        title: "Restablecer Progreso del Episodio",
        description: "Esto limpiará todas las palabras dominadas solo para este episodio y reiniciará tu práctica de vocabulario. El progreso de otros episodios no se verá afectado. Esta acción no se puede deshacer.",
        cancel: "Cancelar",
        confirm: "Restablecer Progreso",
      },
    },
    exportDialog: {
      lockedBadge: "Función premium",
      lockedDescription: "La exportación está disponible en planes premium. Actualiza para descargar, copiar o compartir contenido completo del episodio.",
      title: "Exportar Episodio",
      description: 'Exportar "{title}" en tu formato preferido',
      quickActionsTitle: "Acciones Rápidas",
      downloadTitle: "Descargar Archivos",
      quickActions: {
        copyMarkdown: "Copiar Markdown",
        copyText: "Copiar Texto",
        copyUrl: "Copiar URL",
        email: "Correo electrónico",
      },
      options: {
        markdown: {
          title: "Archivo Markdown",
          description: "Descargar como Markdown formateado (.md) — Perfecto para documentación y toma de notas",
        },
        text: {
          title: "Archivo de Texto Plano",
          description: "Descargar como texto simple (.txt) — Compatibilidad universal",
        },
        json: {
          title: "Datos JSON",
          description: "Descargar datos estructurados (.json) — Para desarrolladores y automatización",
        },
      },
      episodeInfo: {
        episodeLabel: "Episodio",
        authorLabel: "Autor",
        unknownAuthor: "Desconocido",
        includesPrefix: "Incluye",
        includesSummary: "Resumen",
        includesChapters: "Capítulos",
        includesKeyPoints: "Puntos Clave",
        includesTranscript: "Transcripción Completa",
      },
    },
    immersiveFloating: {
      subtitles: {
        show: "Mostrar subtítulos",
        hide: "Ocultar subtítulos",
      },
      translation: {
        show: "Mostrar traducciones de tarjetas",
        hide: "Ocultar traducciones de tarjetas",
      },
      translationReveal: {
        show: "Revelar todas las traducciones de tarjetas",
        hide: "Ocultar todas las traducciones de tarjetas",
      },
      exit: "Salir del modo inmersivo",
    },
    videoPlayer: {
      immersive: {
        enterFocus: "Modo enfoque",
        exitFocus: "Salir del enfoque",
        enterImmersive: "Enfoque inmersivo",
        exitImmersive: "Salir del inmersivo",
        enterFocusTitle: "Entrar en modo enfoque",
        exitFocusTitle: "Salir del modo enfoque",
        enterImmersiveTitle: "Entrar en modo inmersivo",
        exitImmersiveTitle: "Salir del modo inmersivo (Esc)",
      },
      controls: {
        minimize: "Minimizar reproductor",
        maximize: "Restaurar reproductor",
      },
    },
    player: {
      previewLock: {
        badge: "Límite de vista previa",
        message: "La vista previa del plan gratuito está limitada a los primeros 2 minutos. Actualiza para continuar escuchando.",
        messageShort: "Límite de vista previa — actualiza para continuar",
        tooltip: "Vista previa del plan gratuito limitada a los primeros 2 minutos",
      },
      freePreview: {
        toastTitle: "Límite de vista previa gratuita alcanzado",
        toastDescription: "Actualiza para continuar escuchando más allá de los primeros 2 minutos.",
        toastUpgradeCta: "Actualizar",
      },
    },
  },
  summary: {
    title: "Resumen",
    noSummary: "No hay resumen disponible",
    generate: "Generar Resumen",
    generating: "Generando Resumen...",
    generateDesc: "Genera un resumen con IA para obtener una visión general completa de este episodio.",
    generatingDesc: "Por favor espera mientras creamos un resumen con IA de este episodio.",
    aiOverview: "Resumen generado por IA",
    zhTranslation: "Traducción al Chino",
    zhTranslationExtra: "Traducción al Chino (extra)",
    translationLoading: "Cargando traducción...",
  },
  home: {
    featured: {
      title: "Destacados",
      error: "Error al cargar episodios destacados",
      refresh: "Actualizar página para intentar de nuevo",
    },
    following: {
      title: "Siguiendo",
    },
    episodes: {
      title: "Episodios",
      viewAll: "Ver Todo",
      empty: "No hay episodios disponibles en este momento.",
    },
    channels: {
      title: "Canales Populares",
      viewAll: "Ver Todo",
    },
    collections: {
      title: "Colecciones",
      viewAll: "Ver Todo",
      countSuffix: "episodios",
      viewAllInline: "Ver todo",
      categories: {
        "1": "Fronteras de Tecnología e IA",
        "2": "Crecimiento Personal y Mentalidad",
        "3": "Salud y Bienestar",
        "4": "Negocios y Finanzas",
        "5": "Problemas Globales y Política",
        "6": "Relaciones y Conexión Humana",
        "7": "Ciencia y Descubrimiento",
        "8": "Estilo de Vida y Vlogs",
        "9": "Historia y Cultura",
        "10": "Medio Ambiente y Sostenibilidad",
      },
    },
    youtube: {
      title: "YouTube Destacado",
      missingThumbnail: "Miniatura faltante",
      playVideo: "Reproducir {title}",
      defaultCreator: "Creador de YouTube",
    },
    free: "GRATIS",
  },
  channel: {
    filters: {
      ariaLabel: "Filtrar episodios por temporada",
      allSeasons: "Todas las Temporadas",
      season: "Temporada {season}",
    },
    badges: {
      episodes: "Episodios",
    },
    empty: {
      title: "No se encontraron episodios",
      seasonFiltered: "No se encontraron episodios para la temporada seleccionada.",
      generic: "Este canal aún no tiene episodios.",
    },
  },
  episodes: {
    title: "Episodios Trending",
    loadingMore: "Cargando más episodios...",
    loadMore: "Cargar Más",
    noMore: "No hay más episodios para cargar",
    noneTitle: "No se encontraron episodios",
    noneDesc: "No hay episodios trending disponibles en este momento.",
    errorTitle: "Error al Cargar Episodios",
    tryAgain: "Intentar de Nuevo",
  },
  library: {
    title: "Mi Biblioteca",
    tabs: {
      favorites: "Favoritos",
      playlists: "Listas de Reproducción",
      podcasts: "Podcasts",
    },
    emptyFavoritesTitle: "Aún no hay favoritos",
    emptyFavoritesDesc: "Los episodios que marques aparecerán aquí",
    browseEpisodes: "Explorar Episodios",
    playlistEmpty: "Esta lista de reproducción está vacía",
    playlistsEmptyTitle: "Aún no hay listas de reproducción",
    playlistsEmptyDesc: "Crea listas de reproducción para organizar tus episodios",
    podcastsEmptyTitle: "Aún no hay suscripciones de podcasts",
    podcastsEmptyDesc: "Sigue podcasts para verlos aquí",
    discoverPodcasts: "Descubrir Podcasts",
    playlistDialog: {
      title: "Añadir a Lista de Reproducción",
      description: "Selecciona una lista de reproducción para añadir este episodio, o crea una nueva lista de reproducción.",
      addingLabel: "Añadiendo:",
      nameLabel: "Nombre de la Lista *",
      namePlaceholder: "Introduce el nombre de la lista...",
      descriptionLabel: "Descripción (opcional)",
      descriptionPlaceholder: "Añade una descripción...",
      cancel: "Cancelar",
      creating: "Creando...",
      createAction: "Crear Lista de Reproducción",
      yourPlaylists: "Tus Listas de Reproducción",
      createNew: "Crear Nueva Lista de Reproducción",
      createFirst: "Crea Tu Primera Lista de Reproducción",
      episodesLabel: "episodios",
    },
  },
  history: {
    title: "Vistos Recientemente",
    subtitle: "Rastrea tu progreso y continúa donde lo dejaste",
    loading: "Cargando...",
    loginRequiredTitle: "Inicio de Sesión Requerido",
    loginRequiredDesc: "Por favor inicia sesión para ver tu historial de escucha.",
    noHistoryTitle: "No se Encontró Historial",
    noHistoryAll: "Aún no has escuchado ningún episodio.",
    noHistoryFiltered: (filter: string) => `No se encontraron episodios ${filter}.`,
    completed: "Completado",
    left: "restante",
    watchAgain: "Ver de Nuevo",
    continue: "Continuar",
    view: "Ver",
  },
  course: {
    index: {
      title: "Cursos",
      subtitle: "Explora cursos curados para subir de nivel más rápido.",
      cards: {
        idioms: {
          provider: "Lingoflows",
          title: "Modismos Americanos Más Comunes",
          description: "Domina modismos con ejemplos y explicaciones cotidianas.",
          coverAlt: "Portada de Modismos Americanos Más Comunes",
        },
      },
    },
    detail: {
      headerTitle: "Cursos",
      menuButton: "Abrir menú",
      breadcrumb: {
        courses: "Cursos",
      },
      actions: {
        continue: "Continuar Aprendiendo",
      },
      pagination: "Página {page} / {totalPages} • {total} {unit}",
      paginationPrev: "Anterior",
      paginationNext: "Siguiente",
      totalBadge: "{total} {unit}",
      idiomUnit: "modismos",
      errors: {
        load: "Error al cargar modismos: {message}",
      },
      courses: {
        idioms: {
          title: "Modismos Americanos Más Comunes",
          tagline: "¡Domina modismos conmigo!",
          provider: "Lingoflows",
          coverAlt: "Portada de Modismos Americanos Más Comunes",
        },
        default: {
          title: "Curso",
          tagline: "",
          provider: "",
          coverAlt: "Portada del curso",
        },
      },
    },
    common: {
      previous: "Anterior",
      next: "Siguiente",
    },
    progress: {
      headerLabel: "Progreso",
      loading: "Cargando",
      status: {
        completed: "Completado",
        todo: "Por hacer",
      },
      completedTooltip: "Completado",
      exampleAria: "Ejemplo {index} {status}",
      defaultCourseLabel: "Curso",
    },
    freePlanLimit: {
      title: "Actualiza para desbloquear esta lección",
      description:
        "Los usuarios del plan gratuito pueden acceder a los primeros 10 modismos. Actualiza tu plan para seguir aprendiendo el curso completo.",
      lockedCardLabel: "Actualiza para desbloquear esta lección",
      lockedBanner: "Actualiza para desbloquear",
      lockedHeading: "Actualiza para continuar aprendiendo",
      lockedMessage:
        "Los usuarios del plan gratuito pueden acceder a los primeros 10 modismos. Actualiza para desbloquear el curso completo, incluyendo la lección #{number}.",
      backToList: "Volver a la vista general del curso",
    },
    idiomDetail: {
      breadcrumb: {
        courses: "Cursos",
      },
      sidebar: {
        idiomLabel: "Modismo",
        difficulty: "Dificultad",
        examples: "Ejemplos",
        backToList: "Volver a la lista",
      },
      translationToggle: "Traducción",
      untitled: "Sin título",
      examples: {
        title: "Ejemplos",
        description: "Practica cada ejemplo y graba tu voz para terminar este capítulo.",
        empty: "Aún no hay ejemplos disponibles para este modismo.",
      },
      errors: {
        load: "No se pueden cargar los detalles del modismo. Por favor intenta más tarde.",
      },
    },
    examples: {
      transcribing: "Transcribiendo...",
    },
  },
  savedWords: {
    title: "Revisar",
    countSuffix: "palabras guardadas",
    selectedSuffix: "seleccionadas",
    blur: {
      show: "Mostrar Explicaciones",
      hide: "Ocultar Explicaciones",
    },
    play: {
      playing: "Reproduciendo...",
      playSelected: "Reproducir Seleccionadas",
      playAll: "Reproducir Todo",
    },
    export: {
      label: "Exportar",
      json: "Exportar JSON",
      csv: "Exportar CSV",
      jsonShort: "JSON",
      csvShort: "CSV",
    },
    selectMode: {
      enter: "Seleccionar",
      exit: "Hecho",
      selectAll: "Seleccionar Todo",
      clear: "Limpiar",
    },
    filters: {
      all: "Todos",
    },
    sort: {
      newest: "Más Recientes Primero",
      oldest: "Más Antiguos Primero",
      alphabetical: "A-Z",
    },
    reviewNow: "Revisar Ahora",
    due: "pendiente",
    onDeck: "en pila",
    status: {
      new: "Nuevo",
      fuzzy: "Difuso",
      mastered: "Dominado",
    },
    next: "Siguiente",
    interval: "Intervalo",
    correct: "Correcto",
    wrong: "Incorrecto",
    reviewed: "Revisado",
    explains: "Explicaciones",
    reviewAgain: "De Nuevo",
    reviewHard: "Difícil",
    reviewGood: "Bueno",
    reviewEasy: "Fácil",
    reviewAllCaughtUp: "¡Todo al día!",
    reviewAllCaughtUpHint: "No hay tarjetas que necesiten revisión en este momento. Vuelve más tarde o añade nuevas palabras para estudiar.",
    empty: {
      title: "No se encontraron palabras",
      filterHint: "Intenta ajustar tu búsqueda o filtros.",
      defaultHint: "¡Comienza a guardar palabras mientras ves episodios!",
    },
    loginRequiredTitle: "Inicio de Sesión Requerido",
    loginRequiredDesc: "Por favor inicia sesión para ver tus palabras guardadas.",
    searchPlaceholder: "Buscar palabras...",
    sources: {
      youdao: "Youdao",
      original: "Original",
    },
    relative: {
      now: "ahora",
      dueSoon: "pronto pendiente",
      justReviewed: "recién revisado",
      in: "en {value}{unit}",
      ago: "hace {value}{unit}",
      today: "Hoy",
      yesterday: "Ayer",
    },
    selectionBar: {
      selectedSuffix: "seleccionadas",
      delete: "Eliminar Seleccionadas",
    },
  },
  settings: {
    title: "Configuración",
    nativeLanguage: "Idioma nativo",
    nativeLanguageHint: "Elige tu idioma nativo para traducciones e interfaz",
    theme: {
      title: "Apariencia",
      hint: "Elige tu tema de color preferido",
      light: "Claro",
      lightDesc: "Tema de color claro",
      dark: "Oscuro",
      darkDesc: "Tema de color oscuro",
      system: "Sistema",
      systemDesc: "Seguir preferencia del sistema",
    },
    cefrLevel: {
      title: "Nivel CEFR",
      description: "Elige el nivel CEFR que mejor se adapte a tus estudios para personalizar el contenido.",
      unset: "No establecido",
      saving: "Guardando nivel CEFR...",
    },
    interfaceLanguage: "Idioma de Interfaz",
    interfaceLanguageHint: "Elige el idioma para la interfaz de la aplicación. Esto es independiente de tu configuración de idioma nativo.",
    videoTitles: {
      title: "Idioma del título del episodio",
      description: "Mostrar títulos en chino en la página de inicio cuando las traducciones estén disponibles.",
      chineseLabel: "Título en chino",
      originalLabel: "Título original",
      saving: "Guardando preferencia...",
    },
    vocabularyDict: "Diccionario de Vocabulario",
    remove: "Eliminar",
    removing: "Eliminando...",
    selectVocabularyHint: "Selecciona el vocabulario que deseas aprender según tus necesidades",
    dailyGoal: {
      title: "Objetivo Diario de Shadowing",
      desc: "Establece tu tiempo de práctica diario y visualiza rápidamente tu progreso",
      targetMinutes: "Minutos objetivo",
      minutesLabel: "Minutos",
      progressPrefix: "Progreso de hoy:",
      remainingPrefix: "Restante",
      minAbbr: "min",
      completedPrefix: "Completado",
      todayWord: "hoy",
      saving: "Guardando...",
      saved: "Guardado",
      loadFailed: "Error al cargar objetivo",
      saveFailed: "Error al guardar",
    },
    examVocab: {
      title: "Vocabulario de Examen",
      subtitle: "Vocabulario de exámenes profesionales y estandarizados",
    },
  },
  user: {
    plan: {
      premium: "Premium",
      free: "Plan Gratuito",
    },
    badge: {
      premiumMember: "Miembro Premium",
      unlimited: "Ilimitado",
    },
    usage: {
      transcriptsLabel: "transcripciones",
      remaining: "restantes este mes",
      limitReached: "Límite mensual alcanzado",
    },
    actions: {
      upgrade: "Actualizar a Premium",
      settings: "Configuración",
      help: "Ayuda y Soporte",
      logout: "Cerrar sesión",
    },
    dialog: {
      limitReached: "Has alcanzado tu límite mensual de transcripciones. ¡Actualiza a Premium para acceso ilimitado!",
      remaining: "Tienes {remaining} transcripciones restantes este mes. ¡Actualiza para acceso ilimitado!",
    },
  },
  auth: {
    headings: {
      welcome: "¡Haz que hoy cuente!",
      start: "Comienza Tu Viaje de Aprendizaje",
      createAccount: "Crear Cuenta",
      signIn: "Iniciar Sesión",
    },
    subheadings: {
      login: "¡Bienvenido de vuelta! Por favor ingresa tus datos",
      signup: "Ingresa tus datos para comenzar",
      leftLogin: "Continúa tu aventura de aprendizaje de idiomas con podcasts interactivos y funciones con IA.",
      leftSignup: "Aprende inglés de manera efectiva con videos inmersivos y contenido de podcasts atractivo.",
    },
    form: {
      email: "Dirección de correo electrónico",
      emailPlaceholder: "Ingresa tu correo electrónico",
      password: "Contraseña",
      createPassword: "Crear contraseña",
      passwordPlaceholderLogin: "Ingresa tu contraseña",
      passwordPlaceholderSignup: "Crea una contraseña segura",
      forgotPassword: "¿Olvidaste tu contraseña?",
      signIn: "Iniciar sesión",
      createAccount: "Crear cuenta",
      signingIn: "Iniciando sesión...",
      creatingAccount: "Creando cuenta...",
      haveAccount: "¿Ya tienes una cuenta?",
      dontHaveAccount: "¿No tienes una cuenta?",
      signInInstead: "Iniciar sesión en su lugar",
    },
    reset: {
      title: "Restablecer Contraseña",
      sentTitle: "Revisa Tu Correo Electrónico",
      sentDesc: "Hemos enviado un enlace para restablecer la contraseña a tu dirección de correo electrónico.",
      enterDesc: "Ingresa tu dirección de correo electrónico y te enviaremos un enlace para restablecer tu contraseña.",
      sentTo: "Enviamos un enlace para restablecer la contraseña a:",
      notReceived: "¿No recibiste el correo? Revisa tu carpeta de spam o intenta de nuevo.",
      backToSignIn: "Volver a Iniciar Sesión",
      sendLink: "Enviar Enlace de Restablecimiento",
      sending: "Enviando Enlace de Restablecimiento...",
      cancel: "Cancelar",
    },
    errors: {
      invalidEmail: "Por favor ingresa una dirección de correo electrónico válida.",
      shortPassword: "La contraseña debe tener al menos 6 caracteres.",
    }
  },
  search: {
    search: "Buscar",
    trending: "Trending",
    podcast: "Podcast",
    video: "Video",
    allCategories: "Todas las Categorías",
    categoryPrefix: "Categoría:",
    searchingPodcasts: "Buscando podcasts...",
    loadingVideos: "Cargando videos...",
    errorTitle: "Error de Búsqueda",
    noResults: "No se encontraron resultados",
    tryAdjustPodcasts: "Intenta ajustar tus términos de búsqueda o explora podcasts trending en su lugar",
    tryAdjustVideos: "Intenta ajustar tus términos de búsqueda o explora videos trending en su lugar",
    trendingVideos: "Videos Trending",
    loadingMorePodcasts: "Cargando más podcasts...",
    endOfResults: "¡Has llegado al final! Esos son todos los podcasts que encontramos.",
    categories: {
      "Arts": "Artes",
      "Business": "Negocios",
      "Comedy": "Comedia",
      "Education": "Educación",
      "Fiction": "Ficción",
      "Government": "Gobierno",
      "Health & Fitness": "Salud y Fitness",
      "History": "Historia",
      "Kids & Family": "Niños y Familia",
      "Leisure": "Ocio",
      "Music": "Música",
      "News": "Noticias",
      "Religion & Spirituality": "Religión y Espiritualidad",
      "Science": "Ciencia",
      "Society & Culture": "Sociedad y Cultura",
      "Sports": "Deportes",
      "Technology": "Tecnología",
      "True Crime": "Crimen Real",
      "TV & Film": "TV y Cine",
    },
  },
};

// Japanese (ja) translations
const ja: Dict = {
  hero: {
    goalLabel: "目標",
    minuteShort: "分",
    hour: "時間",
    hours: "時間",
  },
  analytics: {
    learnMore: "もっと見る",
    chart: {
      practiceTime: "練習時間",
      minutes: "分",
      current: "現在",
    },
    cards: {
      today: {
        title: "今日",
        unit: "分",
        subtitle: "今日の練習時間",
      },
      sessions: {
        title: "セッション",
        unit: "回",
        subtitle: "今日のシャドーイング練習",
      },
      streak: {
        title: "連続記録",
        unitSingular: "日",
        unitPlural: "日",
        subtitle: "現在の練習連続記録",
      },
    },
  },
  upgradeDialog: {
    title: "プレミアムにアップグレード",
    subtitle: {
      withEpisode: '「{episodeTitle}」にアクセスして、すべてのプレミアムコンテンツを解除',
      default: "すべてのプレミアムコンテンツと機能を解除",
    },
    features: {
      episodes: "すべてのエピソードにアクセス",
      ai: "AI搭載の学習機能",
      support: "優先サポート",
    },
    actions: {
      upgrade: "今すぐアップグレード",
      continueFree: "無料アクセスを続ける",
    },
  },
  shadowing: {
    title: "シャドーイング練習",
    showTranslation: "翻訳を表示",
    showSentenceHint: "目のアイコンをクリックして文を表示",
    replay: "再生",
    continue: "続ける",
    startShadowing: "シャドーイングを開始",
    stopRecording: "録音を停止",
    transcribeFailed: "転写に失敗しました",
  },
  common: {
    loading: "読み込み中...",
    save: "保存",
    speak: "話す",
    signIn: "サインイン",
    backToHome: "ホームに戻る",
    optional: "オプション",
  },
  installPrompt: {
    title: "デバイスにLongoflowsをインストール",
    instructions: {
      tap: "タップ",
      thenAdd: 'して「ホーム画面に追加」',
    },
    actions: {
      close: "インストールプロンプトを閉じる",
    },
  },
  navigation: {
    tagline: "あなたの言語学習プラットフォーム",
    actions: {
      collapse: "サイドバーを折りたたむ",
      expand: "サイドバーを展開",
    },
    links: {
      home: "ホーム",
      search: "検索",
      episodes: "エピソード",
      course: "コース",
      library: "ライブラリ",
      review: "復習",
      history: "履歴",
      settings: "設定",
    },
  },
  tabs: {
    summary: { name: "要約", desc: "AI搭載のエピソード要約", mobile: "要約" },
    mindmap: { name: "マインドマップ", desc: "視覚的な概念マッピング", mobile: "マップ" },
    transcript: { name: "トランスクリプト", desc: "完全なエピソードトランスクリプト", mobile: "スクリプト" },
    vocabulary: { name: "語彙", desc: "重要な用語とフレーズ", mobile: "語彙" },
    phrase: { name: "辞書", desc: "辞書ビューの語彙", mobile: "辞書" },
    quiz: { name: "クイズ", desc: "インタラクティブな練習", mobile: "クイズ" },
  },
  player: {
    freePreview: {
      toastTitle: "無料プレビュー制限に達しました",
      toastDescription: "最初の2分を超えて聞き続けるにはアップグレードしてください。",
      toastUpgradeCta: "アップグレード",
    },
  },
  phraseSection: {
    title: "辞書の語彙",
    subtitle: "トランスクリプトに表示される各辞書の単語を正確に確認してください。",
    truncateNotice: "最初の{count}件の一致を表示",
    actions: {
      openSettings: "設定を開く",
    },
    badges: {
      dictionaryMode: "辞書モード",
    },
    countLabel: {
      singular: "語",
      plural: "語",
    },
    indicators: {
      cardsLabel: "語彙",
      dictionaryLabel: "辞書",
      dictionaryMissing: "辞書が選択されていません",
    },
    jump: "クリップにジャンプ",
    empty: {
      noVocabulary: {
        title: "語彙が必要です",
        description: "コンテキストフレーズを探索するには、語彙を生成またはインポートしてください。",
      },
      noMatches: {
        title: "まだコンテキストフレーズがありません",
        description: "語彙用語はまだトランスクリプトに表示されていません。",
      },
      noDictionary: {
        title: "ハイライトを解除する辞書を選択",
        description: "設定で語彙辞書を選択すると、学習した単語を特定できます。",
      },
    },
  },
  quizSection: {
    title: "インタラクティブクイズ",
    description: "このエピソードから抽出された重要な用語を練習します。",
    empty: {
      title: "まだクイズが利用できません",
      description: "語彙に練習項目がリンクされると、クイズの質問がここに表示されます。",
    },
    stats: {
      questionSingular: "質問",
      questionPlural: "質問",
      countSingular: "質問",
      countPlural: "質問",
    },
    overview: {
      title: "クイズ概要",
      nextTerm: "次の用語",
      progress: "進捗",
      accuracy: "精度",
      allCompleted: "すべてのクイズ項目が完了しました",
    },
    toast: {
      noSentence: "クイズの文は音声生成に利用できません。",
      audioGenerationFailed: "音声生成に失敗しました",
      audioGenerated: "クイズ音声が生成されました",
    },
    messages: {
      completed: "素晴らしい！この用語は完全にシャドーイングされました。",
    },
    status: {
      completed: "完了",
      todo: "未完了",
    },
    translation: {
      show: "翻訳を表示",
      hide: "翻訳を非表示",
    },
    shadowing: {
      defaultTitle: "エピソードクイズ練習",
      cta: "シャドーイング",
      dialogTitle: "シャドーイング練習",
      instructions: "文を聞いて繰り返してください",
      showTranslation: "翻訳を表示",
      hideTranslation: "翻訳を非表示",
      audioUnavailable: "参照音声はまだ利用できません。聞くにはクイズカードから生成してください。",
      stopRecording: "録音を停止",
      startShadowing: "シャドーイングを開始",
      yourRecording: "あなたの録音",
      attemptsLabel: "試行",
    },
  },
  persona: {
    progress: {
      label: "ステップ {current} / {total}",
    },
    actions: {
      skip: "今はスキップ",
      back: "戻る",
      continue: "続ける",
      finish: "完了",
    },
    steps: {
      languages: {
        eyebrow: "ステップ1 · 言語",
        title: "あなたの母国語と目標言語は何ですか？",
        description: "翻訳をパーソナライズするために、希望する言語を教えてください。",
      },
      level: {
        eyebrow: "ステップ2 · 目標レベル",
        title: "目標言語のレベルをどのように評価しますか？",
        description: "大まかな自己評価のみで、テストは不要です。",
      },
      goals: {
        eyebrow: "ステップ3 · 目標",
        title: "目標言語を学ぶ主な目標は何ですか？",
        description: "あなたの旅に最も一致する目標を選択してください。いつでも調整できます。",
      },
      topics: {
        eyebrow: "ステップ4 · トピック / 興味",
        title: "どのような種類の動画を最も楽しみますか？",
        description: "これらのトピックに基づいて英語の動画をおすすめします。",
      },
      formats: {
        eyebrow: "ステップ5 · 動画形式",
        title: "どのような動画形式が好きですか？",
        description: "楽しむスタイルを選択してください。おすすめに混ぜます。",
      },
      session: {
        eyebrow: "ステップ6 · セッション時間",
        title: "通常、1セッションにどのくらいの時間がありますか？",
        description: "動画の長さをあなたのスケジュールに合わせます。",
      },
      subtitles: {
        eyebrow: "ステップ7 · 字幕と説明",
        title: "学習をどのようにサポートすべきですか？",
        description: "希望する字幕と説明を選択してください。",
      },
    },
    goals: {
      everyday: "日常会話",
      watchYoutube: "字幕なしでYouTubeを見る",
      jobInterview: "就職面接とキャリア",
      studyExam: "学習 / 試験（IELTS、TOEFLなど）",
      travelAbroad: "海外旅行と生活",
    },
    topics: {
      dailyVlog: "日常vlogとライフスタイル",
      aiTech: "テクノロジーとAI",
      career: "キャリアと自己啓発",
      comedy: "コメディとエンターテインメント",
      moviesTv: "映画とTV番組",
      news: "ニュースと時事",
      gaming: "ゲーム",
      education: "教育とチュートリアル",
    },
    levels: {
      rangeHint: "{from}–{to}の範囲の動画を目指します。",
      a2: {
        title: "初心者 – いくつかの単語を知っていますが、ほとんどの動画を追えません。",
        description: "基本的なCEFRレベル",
      },
      b1: {
        title: "初級中級 – ほとんどのコンテンツを追うには字幕が必要です。",
        description: "初級中級",
      },
      b2: {
        title: "中級 – 字幕があれば70–80%理解できます。",
        description: "中級上級",
      },
      c1: {
        title: "上級 – 字幕なしでほとんどの動画を追えます。",
        description: "上級",
      },
    },
    formats: {
      vlog: "Vlog / 日常生活",
      interview: "インタビューとポッドキャスト",
      podcast: "ポッドキャスト / 音声ストーリー",
      tutorial: "説明 / チュートリアル",
      shorts: "ショートクリップ / ハイライト",
      movieClip: "映画 / TV番組のシーン",
    },
    sessionLength: {
      label: "セッション時間",
      description: "{minutes}分を目標",
      ten: "5–10分",
      twenty: "10–20分",
      thirty: "20–30分",
      fortyFive: "30分以上",
    },
    sessionFrequency: {
      prompt: "どのくらいの頻度で練習したいですか？",
      three: "週に数回",
      five: "ほぼ毎日",
      seven: "毎日",
    },
    subtitles: {
      alwaysTitle: "常に英語字幕を表示",
      alwaysDescription: "すべての動画でデフォルトで字幕をオンにします。",
      nativeTitle: "助けが必要なときに私の言語で翻訳を表示",
      nativeDescription: "行き詰まったときはいつでも母国語サポートを切り替えます。",
      nativeLanguageLabel: "希望する母国語",
      nativeLanguagePlaceholder: "言語を選択",
      nativeLanguageHint: "翻訳や説明を表示するときにこれを使用します。",
      nativeLanguage: {
        auto: "自動検出",
        zh: "中国語",
        es: "スペイン語",
        ja: "日本語",
        ko: "韓国語",
      },
    },
    languages: {
      nativeLabel: "あなたの母国語は何ですか？",
      targetLabel: "どの言語を学んでいますか？",
      targetPlaceholder: "目標言語を選択",
      hint: "これを使用してレッスンとレベルガイダンスを一致させます。",
      native: {
        en: "英語",
        zh: "中国語",
        es: "スペイン語",
        ja: "日本語",
        ko: "韓国語",
        fr: "フランス語",
        de: "ドイツ語",
        pt: "ポルトガル語",
        ru: "ロシア語",
      },
      target: {
        en: "英語",
        zh: "中国語",
        es: "スペイン語",
        ja: "日本語",
        ko: "韓国語",
        fr: "フランス語",
        de: "ドイツ語",
        pt: "ポルトガル語",
        ru: "ロシア語",
      },
    },
    explanations: {
      title: "見たい説明",
      keyVocab: "各動画の重要な語彙を表示",
      showPhrases: "有用なフレーズと表現をハイライト",
      translateSentences: "難しい文を母国語に翻訳",
      pronunciation: "発音とシャドーイングに焦点を当てる",
    },
  },
  vocabQuiz: {
    title: "語彙クイズ",
    translationUnavailable: "このセグメントの翻訳は利用できません",
    loading: "読み込み中...",
    translation: {
      show: "翻訳を表示",
      hide: "翻訳を非表示",
    },
    actions: {
      replay: "再生",
      continue: "続ける",
    },
  },
  mobileImmersive: {
    subtitles: {
      show: "字幕を表示",
      hide: "字幕を非表示",
    },
    translation: {
      show: "翻訳を表示",
      hide: "翻訳を非表示",
    },
    narration: {
      enable: "ナレーションモードを有効化",
      disable: "ナレーションモードを無効化",
      tooltipEnable: "再生後に語彙説明を自動再生",
      tooltipDisable: "語彙ナレーションを無効化",
      generating: "ナレーション音声を生成中...",
      failed: "ナレーション生成に失敗しました",
      modeActive: "ナレーションモード有効",
      playing: "ナレーションを再生中...",
      ready: "ナレーション準備完了",
      skip: "スキップ",
      skipTooltip: "ナレーションをスキップして動画を続ける",
      currentTerm: "現在の用語",
      pause: "一時停止",
      resume: "再開",
      repeat: "再生",
    },
    status: {
      new: "新規",
      newDescription: "追加されたばかり、最初の復習が必要",
      fuzzy: "曖昧",
      fuzzyDescription: "すぐにより多くの練習が必要",
      mastered: "習得済み",
      masteredDescription: "自信があり、長期復習中",
    },
    shadowing: {
      tooltip: "シャドーイング練習",
      dialogTitle: "シャドーイング練習",
      dialogDescription: "現在の字幕を声に出して繰り返してください。",
      attemptLabel: "試行",
      hideText: "テキストを非表示",
      showText: "テキストを表示",
      hideTranslationShort: "翻訳を非表示",
      showTranslationShort: "翻訳を表示",
      transcriptionLabel: "あなたの転写",
      startRecording: "シャドーイングを開始",
      stopRecording: "録音を停止",
      start: "シャドーイングを開始",
      stop: "シャドーイングを停止",
      unknownEpisode: "不明なエピソード",
    },
  },
  vocabulary: {
    status: {
      label: "語彙分類",
      new: "新規",
      newDescription: "追加されたばかり、最初の復習が必要",
      fuzzy: "曖昧",
      fuzzyDescription: "すぐにより多くの練習が必要",
      mastered: "習得済み",
      masteredDescription: "自信があり、長期復習中",
    },
    section: {
      title: "キーワードとトピック",
      topicCountSingular: "{count}トピック",
      topicCountPlural: "{count}トピック",
      revealAllButton: "すべて表示",
      revealAllTitle: "すべての説明を表示",
      restoreBlurButton: "ぼかしを復元",
      restoreBlurTitle: "ぼかしを復元",
      levelFilterLabel: "CEFRレベルでフィルター",
      levelFilterUserLevel: "あなたのレベル: {level}",
      levelFilterAll: "すべて",
      noTermsMatchFilter: "選択したCEFRレベルに一致する用語はまだありません。追加のレベルを選択してみてください。",
    },
    swipeTip: {
      badge: "ヒント",
      heading: "スワイプしてこのスタックを管理",
      description: "左にスワイプして知っている単語を非表示にし、右にスワイプして練習フローに保持します。",
    },
    desktopActions: {
      markKnown: "既知としてマーク",
      keepLearning: "練習を続ける",
    },
    card: {
      examplesLabel: "例",
    },
    stack: {
      viewDetails: "詳細を表示",
      closeDetails: "閉じる",
    },
  },
  transcript: {
    title: "トランスクリプト",
    generate: "トランスクリプトを生成",
    generating: "トランスクリプトを生成中...",
    generateDesc: "タイムスタンプとインタラクティブ機能を備えた完全なテキストを表示するためにトランスクリプトを生成します。",
    generatingDesc: "このエピソードのトランスクリプトを生成している間、お待ちください。",
    segments: "セグメント",
    usageDeducted: "トランスクリプトクレジット使用済み",
    usageRemaining: "今月残り{remaining}/{limit}",
    usageRemainingUnlimited: "あなたのプランで無制限のトランスクリプト",
    dynamicIslandSettings: "Dynamic Island設定",
    dynamicIslandTitle: "Dynamic Island",
    dynamicIslandDescription: "学習中にDynamic Islandに表示される内容を制御します。",
    dynamicIslandVocabulary: "語彙ポップアップ",
    dynamicIslandVocabularyHint: "ポップアップオーバーレイ内に語彙プロンプトを表示",
    toggleDynamicIslandVocabulary: "語彙ポップアップを切り替え",
    wordMask: "字幕アニメーション",
    toggleWordMask: "字幕アニメーションを切り替え",
  },
  premiumPrompt: {
    guest: {
      title: "サインインが必要です",
      description: "このコンテンツにアクセスして学習機能を解除するには、サインインしてください。",
    },
    free: {
      title: "プレミアムコンテンツ",
      description: "このエピソードはすべての機能にアクセスするためにプレミアムサブスクリプションが必要です。",
      secondary: "または、無料アクセスのために注目のエピソードをチェックしてください。",
    },
    featured: {
      title: "無料の注目コンテンツ",
      description: "この無料エピソードを楽しんでいますか？プレミアムにアップグレードして、完全なコンテンツライブラリを解除してください！",
      cta: "プレミアムについて学ぶ",
    },
    fallback: "アクセス制限",
  },
  contentRenderer: {
    alerts: {
      vocabFallback: "語彙生成に失敗しました: {error}。代わりにデモデータを使用します。",
    },
  },
  upgradePromptDialog: {
    titles: {
      limitReached: "月間制限に達しました",
      upgrade: "プレミアムにアップグレード",
      error: "生成失敗",
    },
    usage: {
      label: "月間使用量",
      summary: "今月使用されたトランスクリプト{used}/{limit}",
      remaining: "残り{remaining}/{limit}",
      unlimited: "プレミアムプランで無制限のトランスクリプト",
    },
    benefits: {
      title: "プレミアムにアップグレード",
      items: {
        unlimitedTranscripts: "無制限のトランスクリプト生成",
        aiSummaries: "すべてのReady文字起こし動画を視聴可能",
        vocabulary: "完全な語彙分析",
        priority: "優先処理",
        export: "すべての形式にエクスポート",
      },
    },
    plans: {
      basic: {
        name: "ベーシックプラン",
        short: "ベーシック",
        badge: "人気",
        price: "$12.99",
        period: "/月",
        description: "個人学習者に最適",
      },
      premium: {
        name: "プレミアムプラン",
        short: "プレミアム",
        badge: "最高の価値",
        price: "$19.99",
        period: "/月",
        description: "高度な機能と優先サポート",
      },
    },
    actions: {
      upgradeToPlan: "{plan}にアップグレード",
      continueWithPlan: "{plan}を続ける",
      later: "後で",
      close: "閉じる",
      tryAgain: "再試行",
    },
    status: {
      processing: "処理中...",
    },
    alerts: {
      checkoutFailed: "チェックアウトの開始に失敗しました。もう一度お試しください。",
    },
    error: {
      generic: "トランスクリプトを生成できません",
      loginRequired: "トランスクリプトを生成するには、サインインしてください。進捗は保存されます。",
    },
    premiumStatus: {
      title: "プレミアムプランをお持ちです",
      description: "無制限のトランスクリプト生成とすべてのプレミアム機能をお楽しみください。",
    },
  },
  episode: {
    floatingActions: {
      buttons: {
        open: "クイックアクションを開く",
        collapse: "クイックアクションを折りたたむ",
        showTranslation: "翻訳を表示",
        hideTranslation: "翻訳を非表示",
        generateTranslation: "翻訳を生成",
        generatingTranslation: "翻訳を生成中...",
        generateChosenExplanation: "AI説明を生成",
        generatingChosenExplanation: "説明を生成中",
        enableWordHighlight: "単語ハイライトを有効化",
        disableWordHighlight: "単語ハイライトを無効化",
        adjustPracticeMode: "練習モードを調整",
        enablePracticeMode: "練習モードを有効化",
        resetProgress: "エピソードの進捗をリセット",
        backToTop: "トップに戻る",
      },
      tooltips: {
        open: "クイックアクションを開く",
        collapse: "クイックアクションを折りたたむ",
        showTranslation: "翻訳を表示",
        hideTranslation: "翻訳を非表示",
        generateTranslation: "翻訳を生成",
        generatingTranslation: "生成中...",
        generateChosenExplanation: "例ベースの説明を生成",
        generatingChosenExplanation: "説明を生成中...",
        dictionaryRequiredTitle: "辞書が必要です",
        dictionaryRequiredDescription: "ハイライトを解除するには、設定で辞書を設定してください。",
        enableWordHighlight: "単語ハイライトを有効化",
        disableWordHighlight: "単語ハイライトを無効化",
        practiceSetupTitle: "設定が必要です",
        practiceSetupDescription: "練習モードを開始するには、語彙を生成するか辞書を選択してください。",
        practiceActiveTitle: "練習モードがアクティブです",
        practiceOpenTitle: "練習モードを開く",
        practiceActiveDescription: "現在{mode}モードを使用中です。",
        practiceOpenDescription: "目標に合わせた学習モードを選択してください。",
        resetProgressTitle: "エピソードの進捗をリセット",
        resetProgressDescription: "このエピソードのみの習得済み単語をクリアします。",
        backToTop: "トップに戻る",
      },
      success: {
        translateGenerated: "翻訳が正常に生成されました！",
        chosenExplanationGenerated: "説明が正常に生成されました！",
      },
      alerts: {
        translateMissingData: "翻訳を生成できません: 必要なデータが不足しています。",
      },
      errors: {
        translateRequestFailed: "翻訳リクエストに失敗しました",
        translateResponseInvalid: "翻訳レスポンス形式エラー",
        translateConsoleError: "翻訳生成に失敗しました",
        translateFailed: "翻訳生成に失敗しました。もう一度お試しください。",
        generateVocabularyFirst: "まず語彙を生成してください",
        dictionaryInitConsole: "辞書の初期化に失敗しました",
        dictionaryLoadFailed: "辞書を読み込めません。もう一度お試しください",
        dictionaryNotSelected: "まず設定で語彙辞書を選択してください",
        dictionaryInitFailed: "語彙マネージャーの初期化に失敗しました",
        dictionaryInitUnable: "語彙辞書を読み込めません。もう一度お試しください",
        chosenExplanationFailed: "説明の生成に失敗しました",
        chosenExplanationMissing: "説明を生成できません: 語彙が準備できていません。",
        chosenExplanationUnauthorized: "説明を生成するには、サインインしてください。",
      },
      practice: {
        noDictionary: "辞書なし",
        spellingSuffix: "スペリング",
      },
      practiceModes: {
        vocabulary: "語彙",
        toefl: "TOEFL",
        shadowing: "シャドーイング",
      },
      dialog: {
        title: "エピソードの進捗をリセット",
        description: "これにより、このエピソードのみのすべての習得済み単語がクリアされ、語彙練習が再開されます。他のエピソードの進捗には影響しません。この操作は元に戻せません。",
        cancel: "キャンセル",
        confirm: "進捗をリセット",
      },
    },
    exportDialog: {
      lockedBadge: "プレミアム機能",
      lockedDescription: "エクスポートはプレミアムプランで利用できます。アップグレードして、完全なエピソードコンテンツをダウンロード、コピー、または共有してください。",
      title: "エピソードをエクスポート",
      description: '「{title}」を希望の形式でエクスポート',
      quickActionsTitle: "クイックアクション",
      downloadTitle: "ファイルをダウンロード",
      quickActions: {
        copyMarkdown: "Markdownをコピー",
        copyText: "テキストをコピー",
        copyUrl: "URLをコピー",
        email: "メール",
      },
      options: {
        markdown: {
          title: "Markdownファイル",
          description: "フォーマットされたMarkdown（.md）としてダウンロード — ドキュメントとメモ取りに最適",
        },
        text: {
          title: "プレーンテキストファイル",
          description: "シンプルなテキスト（.txt）としてダウンロード — ユニバーサル互換性",
        },
        json: {
          title: "JSONデータ",
          description: "構造化データ（.json）としてダウンロード — 開発者と自動化用",
        },
      },
      episodeInfo: {
        episodeLabel: "エピソード",
        authorLabel: "著者",
        unknownAuthor: "不明",
        includesPrefix: "含む",
        includesSummary: "要約",
        includesChapters: "章",
        includesKeyPoints: "重要なポイント",
        includesTranscript: "完全なトランスクリプト",
      },
    },
    immersiveFloating: {
      subtitles: {
        show: "字幕を表示",
        hide: "字幕を非表示",
      },
      translation: {
        show: "カード翻訳を表示",
        hide: "カード翻訳を非表示",
      },
      translationReveal: {
        show: "すべてのカード翻訳を表示",
        hide: "すべてのカード翻訳を非表示",
      },
      exit: "没入モードを終了",
    },
    videoPlayer: {
      immersive: {
        enterFocus: "フォーカスモード",
        exitFocus: "フォーカスを終了",
        enterImmersive: "没入フォーカス",
        exitImmersive: "没入を終了",
        enterFocusTitle: "フォーカスモードに入る",
        exitFocusTitle: "フォーカスモードを終了",
        enterImmersiveTitle: "没入モードに入る",
        exitImmersiveTitle: "没入モードを終了（Esc）",
      },
      controls: {
        minimize: "プレーヤーを最小化",
        maximize: "プレーヤーを復元",
      },
    },
    player: {
      previewLock: {
        badge: "プレビュー制限",
        message: "無料プランのプレビューは最初の2分間に制限されています。聞き続けるにはアップグレードしてください。",
        messageShort: "プレビュー制限 — 続けるにはアップグレード",
        tooltip: "無料プランのプレビューは最初の2分間に制限",
      },
      freePreview: {
        toastTitle: "無料プレビュー制限に達しました",
        toastDescription: "最初の2分を超えて聞き続けるにはアップグレードしてください。",
        toastUpgradeCta: "アップグレード",
      },
    },
  },
  summary: {
    title: "要約",
    noSummary: "要約が利用できません",
    generate: "要約を生成",
    generating: "要約を生成中...",
    generateDesc: "このエピソードの包括的な概要を得るために、AI搭載の要約を生成します。",
    generatingDesc: "このエピソードのAI搭載の要約を作成している間、お待ちください。",
    aiOverview: "AI生成の概要",
    zhTranslation: "中国語翻訳",
    zhTranslationExtra: "中国語翻訳（追加）",
    translationLoading: "翻訳を読み込み中...",
  },
  home: {
    featured: {
      title: "注目",
      error: "注目のエピソードの読み込みに失敗しました",
      refresh: "ページを更新してもう一度お試しください",
    },
    following: {
      title: "フォロー中",
    },
    episodes: {
      title: "エピソード",
      viewAll: "すべて表示",
      empty: "現在エピソードは利用できません。",
    },
    channels: {
      title: "人気のチャンネル",
      viewAll: "すべて表示",
    },
    collections: {
      title: "コレクション",
      viewAll: "すべて表示",
      countSuffix: "エピソード",
      viewAllInline: "すべて表示",
      categories: {
        "1": "テクノロジーとAIのフロンティア",
        "2": "個人の成長とマインドセット",
        "3": "健康とウェルネス",
        "4": "ビジネスと金融",
        "5": "グローバルな問題と政治",
        "6": "人間関係とつながり",
        "7": "科学と発見",
        "8": "ライフスタイルとVlog",
        "9": "歴史と文化",
        "10": "環境と持続可能性",
      },
    },
    youtube: {
      title: "YouTube注目",
      missingThumbnail: "サムネイルが見つかりません",
      playVideo: "{title}を再生",
      defaultCreator: "YouTubeクリエイター",
    },
    free: "無料",
  },
  channel: {
    filters: {
      ariaLabel: "シーズンでエピソードをフィルター",
      allSeasons: "すべてのシーズン",
      season: "シーズン{season}",
    },
    badges: {
      episodes: "エピソード",
    },
    empty: {
      title: "エピソードが見つかりません",
      seasonFiltered: "選択したシーズンのエピソードが見つかりません。",
      generic: "このチャンネルにはまだエピソードがありません。",
    },
  },
  episodes: {
    title: "トレンドエピソード",
    loadingMore: "さらにエピソードを読み込み中...",
    loadMore: "さらに読み込む",
    noMore: "読み込むエピソードがありません",
    noneTitle: "エピソードが見つかりません",
    noneDesc: "現在トレンドのエピソードは利用できません。",
    errorTitle: "エピソードの読み込みエラー",
    tryAgain: "再試行",
  },
  library: {
    title: "マイライブラリ",
    tabs: {
      favorites: "お気に入り",
      playlists: "プレイリスト",
      podcasts: "ポッドキャスト",
    },
    emptyFavoritesTitle: "まだお気に入りがありません",
    emptyFavoritesDesc: "スターを付けたエピソードがここに表示されます",
    browseEpisodes: "エピソードを閲覧",
    playlistEmpty: "このプレイリストは空です",
    playlistsEmptyTitle: "まだプレイリストがありません",
    playlistsEmptyDesc: "エピソードを整理するためにプレイリストを作成",
    podcastsEmptyTitle: "まだポッドキャストの購読がありません",
    podcastsEmptyDesc: "ポッドキャストをフォローしてここで表示",
    discoverPodcasts: "ポッドキャストを発見",
    playlistDialog: {
      title: "プレイリストに追加",
      description: "このエピソードを追加するプレイリストを選択するか、新しいプレイリストを作成してください。",
      addingLabel: "追加中:",
      nameLabel: "プレイリスト名 *",
      namePlaceholder: "プレイリスト名を入力...",
      descriptionLabel: "説明（オプション）",
      descriptionPlaceholder: "説明を追加...",
      cancel: "キャンセル",
      creating: "作成中...",
      createAction: "プレイリストを作成",
      yourPlaylists: "あなたのプレイリスト",
      createNew: "新しいプレイリストを作成",
      createFirst: "最初のプレイリストを作成",
      episodesLabel: "エピソード",
    },
  },
  history: {
    title: "最近視聴",
    subtitle: "進捗を追跡し、中断したところから続ける",
    loading: "読み込み中...",
    loginRequiredTitle: "サインインが必要です",
    loginRequiredDesc: "視聴履歴を表示するには、サインインしてください。",
    noHistoryTitle: "履歴が見つかりません",
    noHistoryAll: "まだエピソードを聞いていません。",
    noHistoryFiltered: (filter: string) => `${filter}エピソードが見つかりません。`,
    completed: "完了",
    left: "残り",
    watchAgain: "もう一度見る",
    continue: "続ける",
    view: "表示",
  },
  course: {
    index: {
      title: "コース",
      subtitle: "キュレーションされたコースを探索して、より速くレベルアップ。",
      cards: {
        idioms: {
          provider: "Lingoflows",
          title: "最も一般的なアメリカのイディオム",
          description: "日常的な例と説明でイディオムをマスター。",
          coverAlt: "最も一般的なアメリカのイディオムのカバー",
        },
      },
    },
    detail: {
      headerTitle: "コース",
      menuButton: "メニューを開く",
      breadcrumb: {
        courses: "コース",
      },
      actions: {
        continue: "学習を続ける",
      },
      pagination: "ページ {page} / {totalPages} • {total} {unit}",
      paginationPrev: "前へ",
      paginationNext: "次へ",
      totalBadge: "{total} {unit}",
      idiomUnit: "イディオム",
      errors: {
        load: "イディオムの読み込みに失敗しました: {message}",
      },
      courses: {
        idioms: {
          title: "最も一般的なアメリカのイディオム",
          tagline: "私と一緒にイディオムをマスターしましょう！",
          provider: "Lingoflows",
          coverAlt: "最も一般的なアメリカのイディオムのカバー",
        },
        default: {
          title: "コース",
          tagline: "",
          provider: "",
          coverAlt: "コースカバー",
        },
      },
    },
    common: {
      previous: "前へ",
      next: "次へ",
    },
    progress: {
      headerLabel: "進捗",
      loading: "読み込み中",
      status: {
        completed: "完了",
        todo: "未完了",
      },
      completedTooltip: "完了",
      exampleAria: "例 {index} {status}",
      defaultCourseLabel: "コース",
    },
    freePlanLimit: {
      title: "このレッスンを解除するにはアップグレード",
      description:
        "無料プランユーザーは最初の10個のイディオムにアクセスできます。完全なコースを学習し続けるには、プランをアップグレードしてください。",
      lockedCardLabel: "このレッスンを解除するにはアップグレード",
      lockedBanner: "解除するにはアップグレード",
      lockedHeading: "学習を続けるにはアップグレード",
      lockedMessage:
        "無料プランユーザーは最初の10個のイディオムにアクセスできます。レッスン#{number}を含む完全なコースを解除するには、アップグレードしてください。",
      backToList: "コース概要に戻る",
    },
    idiomDetail: {
      breadcrumb: {
        courses: "コース",
      },
      sidebar: {
        idiomLabel: "イディオム",
        difficulty: "難易度",
        examples: "例",
        backToList: "リストに戻る",
      },
      translationToggle: "翻訳",
      untitled: "無題",
      examples: {
        title: "例",
        description: "各例をシャドーイングし、声を録音してこの章を完了してください。",
        empty: "このイディオムの例はまだ利用できません。",
      },
      errors: {
        load: "イディオムの詳細を読み込めません。後でもう一度お試しください。",
      },
    },
    examples: {
      transcribing: "転写中...",
    },
  },
  savedWords: {
    title: "復習",
    countSuffix: "保存された単語",
    selectedSuffix: "選択済み",
    blur: {
      show: "説明を表示",
      hide: "説明をぼかす",
    },
    play: {
      playing: "再生中...",
      playSelected: "選択したものを再生",
      playAll: "すべて再生",
    },
    export: {
      label: "エクスポート",
      json: "JSONをエクスポート",
      csv: "CSVをエクスポート",
      jsonShort: "JSON",
      csvShort: "CSV",
    },
    selectMode: {
      enter: "選択",
      exit: "完了",
      selectAll: "すべて選択",
      clear: "クリア",
    },
    filters: {
      all: "すべて",
    },
    sort: {
      newest: "最新順",
      oldest: "最古順",
      alphabetical: "あいうえお順",
    },
    reviewNow: "今すぐ復習",
    due: "期限",
    onDeck: "スタック内",
    status: {
      new: "新規",
      fuzzy: "曖昧",
      mastered: "習得済み",
    },
    next: "次へ",
    interval: "間隔",
    correct: "正解",
    wrong: "不正解",
    reviewed: "復習済み",
    explains: "説明",
    reviewAgain: "もう一度",
    reviewHard: "難しい",
    reviewGood: "良い",
    reviewEasy: "簡単",
    reviewAllCaughtUp: "すべて完了しました！",
    reviewAllCaughtUpHint: "現在復習が必要なカードはありません。後で確認するか、新しい単語を追加して学習してください。",
    empty: {
      title: "単語が見つかりません",
      filterHint: "検索またはフィルターを調整してみてください。",
      defaultHint: "エピソードを見ながら単語を保存し始めましょう！",
    },
    loginRequiredTitle: "サインインが必要です",
    loginRequiredDesc: "保存された単語を表示するには、サインインしてください。",
    searchPlaceholder: "単語を検索...",
    sources: {
      youdao: "Youdao",
      original: "オリジナル",
    },
    relative: {
      now: "今",
      dueSoon: "すぐに期限",
      justReviewed: "復習したばかり",
      in: "{value}{unit}後",
      ago: "{value}{unit}前",
      today: "今日",
      yesterday: "昨日",
    },
    selectionBar: {
      selectedSuffix: "選択済み",
      delete: "選択したものを削除",
    },
  },
  settings: {
    title: "設定",
    nativeLanguage: "母国語",
    nativeLanguageHint: "翻訳とインターフェースの母国語を選択",
    theme: {
      title: "外観",
      hint: "希望するカラーテーマを選択",
      light: "ライト",
      lightDesc: "ライトカラーテーマ",
      dark: "ダーク",
      darkDesc: "ダークカラーテーマ",
      system: "システム",
      systemDesc: "システム設定に従う",
    },
    cefrLevel: {
      title: "CEFRレベル",
      description: "コンテンツをパーソナライズするために、学習に最適なCEFRレベルを選択してください。",
      unset: "未設定",
      saving: "CEFRレベルを保存中...",
    },
    interfaceLanguage: "インターフェース言語",
    interfaceLanguageHint: "アプリのインターフェースの言語を選択します。これは母国語設定とは独立しています。",
    videoTitles: {
      title: "エピソードタイトル言語",
      description: "翻訳が利用可能な場合、ホームページに中国語のタイトルを表示します。",
      chineseLabel: "中国語タイトル",
      originalLabel: "オリジナルタイトル",
      saving: "設定を保存中...",
    },
    vocabularyDict: "語彙辞書",
    remove: "削除",
    removing: "削除中...",
    selectVocabularyHint: "ニーズに基づいて学習したい語彙を選択",
    dailyGoal: {
      title: "毎日のシャドーイング目標",
      desc: "毎日の練習時間を設定し、進捗をすばやく表示",
      targetMinutes: "目標分",
      minutesLabel: "分",
      progressPrefix: "今日の進捗:",
      remainingPrefix: "残り",
      minAbbr: "分",
      completedPrefix: "完了",
      todayWord: "今日",
      saving: "保存中...",
      saved: "保存済み",
      loadFailed: "目標の読み込みに失敗しました",
      saveFailed: "保存に失敗しました",
    },
    examVocab: {
      title: "試験語彙",
      subtitle: "専門的で標準化された試験語彙",
    },
  },
  user: {
    plan: {
      premium: "プレミアム",
      free: "無料プラン",
    },
    badge: {
      premiumMember: "プレミアムメンバー",
      unlimited: "無制限",
    },
    usage: {
      transcriptsLabel: "トランスクリプト",
      remaining: "今月残り",
      limitReached: "月間制限に達しました",
    },
    actions: {
      upgrade: "プレミアムにアップグレード",
      settings: "設定",
      help: "ヘルプとサポート",
      logout: "ログアウト",
    },
    dialog: {
      limitReached: "月間トランスクリプト制限に達しました。無制限アクセスのためにプレミアムにアップグレードしてください！",
      remaining: "今月残り{remaining}トランスクリプトがあります。無制限アクセスのためにアップグレードしてください！",
    },
  },
  auth: {
    headings: {
      welcome: "今日を大切に！",
      start: "学習の旅を始めましょう",
      createAccount: "アカウントを作成",
      signIn: "サインイン",
    },
    subheadings: {
      login: "おかえりなさい！詳細を入力してください",
      signup: "始めるために詳細を入力",
      leftLogin: "インタラクティブなポッドキャストとAI搭載機能で言語学習の冒険を続けましょう。",
      leftSignup: "没入型の動画と魅力的なポッドキャストコンテンツで効果的に英語を学びましょう。",
    },
    form: {
      email: "メールアドレス",
      emailPlaceholder: "メールアドレスを入力",
      password: "パスワード",
      createPassword: "パスワードを作成",
      passwordPlaceholderLogin: "パスワードを入力",
      passwordPlaceholderSignup: "強力なパスワードを作成",
      forgotPassword: "パスワードをお忘れですか？",
      signIn: "サインイン",
      createAccount: "アカウントを作成",
      signingIn: "サインイン中...",
      creatingAccount: "アカウント作成中...",
      haveAccount: "すでにアカウントをお持ちですか？",
      dontHaveAccount: "アカウントをお持ちでないですか？",
      signInInstead: "代わりにサインイン",
    },
    reset: {
      title: "パスワードをリセット",
      sentTitle: "メールを確認してください",
      sentDesc: "パスワードリセットリンクをメールアドレスに送信しました。",
      enterDesc: "メールアドレスを入力すると、パスワードをリセットするリンクを送信します。",
      sentTo: "パスワードリセットリンクを送信しました:",
      notReceived: "メールが届きませんでしたか？スパムフォルダを確認するか、もう一度お試しください。",
      backToSignIn: "サインインに戻る",
      sendLink: "リセットリンクを送信",
      sending: "リセットリンクを送信中...",
      cancel: "キャンセル",
    },
    errors: {
      invalidEmail: "有効なメールアドレスを入力してください。",
      shortPassword: "パスワードは少なくとも6文字である必要があります。",
    }
  },
  search: {
    search: "検索",
    trending: "トレンド",
    podcast: "ポッドキャスト",
    video: "動画",
    allCategories: "すべてのカテゴリ",
    categoryPrefix: "カテゴリ:",
    searchingPodcasts: "ポッドキャストを検索中...",
    loadingVideos: "動画を読み込み中...",
    errorTitle: "検索エラー",
    noResults: "結果が見つかりません",
    tryAdjustPodcasts: "検索語を調整するか、代わりにトレンドのポッドキャストを閲覧してください",
    tryAdjustVideos: "検索語を調整するか、代わりにトレンドの動画を閲覧してください",
    trendingVideos: "トレンド動画",
    loadingMorePodcasts: "さらにポッドキャストを読み込み中...",
    endOfResults: "最後に到達しました！これが私たちが見つけたすべてのポッドキャストです。",
    categories: {
      "Arts": "芸術",
      "Business": "ビジネス",
      "Comedy": "コメディ",
      "Education": "教育",
      "Fiction": "フィクション",
      "Government": "政府",
      "Health & Fitness": "健康とフィットネス",
      "History": "歴史",
      "Kids & Family": "子供と家族",
      "Leisure": "レジャー",
      "Music": "音楽",
      "News": "ニュース",
      "Religion & Spirituality": "宗教とスピリチュアリティ",
      "Science": "科学",
      "Society & Culture": "社会と文化",
      "Sports": "スポーツ",
      "Technology": "テクノロジー",
      "True Crime": "真実の犯罪",
      "TV & Film": "TVと映画",
    },
  },
};

// Korean (ko) translations
const ko: Dict = {
  hero: {
    goalLabel: "목표",
    minuteShort: "분",
    hour: "시간",
    hours: "시간",
  },
  analytics: {
    learnMore: "더 알아보기",
    chart: {
      practiceTime: "연습 시간",
      minutes: "분",
      current: "현재",
    },
    cards: {
      today: {
        title: "오늘",
        unit: "분",
        subtitle: "오늘의 연습 시간",
      },
      sessions: {
        title: "세션",
        unit: "회",
        subtitle: "오늘 섀도잉 연습",
      },
      streak: {
        title: "연속 기록",
        unitSingular: "일",
        unitPlural: "일",
        subtitle: "현재 연습 연속 기록",
      },
    },
  },
  upgradeDialog: {
    title: "프리미엄으로 업그레이드",
    subtitle: {
      withEpisode: '"{episodeTitle}"에 액세스하고 모든 프리미엄 콘텐츠 잠금 해제',
      default: "모든 프리미엄 콘텐츠 및 기능 잠금 해제",
    },
    features: {
      episodes: "모든 에피소드 액세스",
      ai: "AI 기반 학습 기능",
      support: "우선 지원",
    },
    actions: {
      upgrade: "지금 업그레이드",
      continueFree: "무료 액세스 계속",
    },
  },
  shadowing: {
    title: "섀도잉 연습",
    showTranslation: "번역 표시",
    showSentenceHint: "눈 아이콘을 클릭하여 문장 표시",
    replay: "다시 재생",
    continue: "계속",
    startShadowing: "섀도잉 시작",
    stopRecording: "녹음 중지",
    transcribeFailed: "전사 실패",
  },
  common: {
    loading: "로딩 중...",
    save: "저장",
    speak: "말하기",
    signIn: "로그인",
    backToHome: "홈으로 돌아가기",
    optional: "선택 사항",
  },
  installPrompt: {
    title: "기기에 Longoflows 설치",
    instructions: {
      tap: "탭",
      thenAdd: '한 다음 "홈 화면에 추가"',
    },
    actions: {
      close: "설치 프롬프트 닫기",
    },
  },
  navigation: {
    tagline: "언어 학습 플랫폼",
    actions: {
      collapse: "사이드바 접기",
      expand: "사이드바 확장",
    },
    links: {
      home: "홈",
      search: "검색",
      episodes: "에피소드",
      course: "코스",
      library: "라이브러리",
      review: "복습",
      history: "히스토리",
      settings: "설정",
    },
  },
  tabs: {
    summary: { name: "요약", desc: "AI 기반 에피소드 요약", mobile: "요약" },
    mindmap: { name: "마인드맵", desc: "시각적 개념 매핑", mobile: "맵" },
    transcript: { name: "전사", desc: "전체 에피소드 전사", mobile: "스크립트" },
    vocabulary: { name: "어휘", desc: "주요 용어 및 구문", mobile: "어휘" },
    phrase: { name: "사전", desc: "사전 보기의 어휘", mobile: "사전" },
    quiz: { name: "퀴즈", desc: "대화형 연습", mobile: "퀴즈" },
  },
  player: {
    freePreview: {
      toastTitle: "무료 미리보기 한도 도달",
      toastDescription: "처음 2분을 넘어 계속 들으려면 업그레이드하세요.",
      toastUpgradeCta: "업그레이드",
    },
  },
  phraseSection: {
    title: "사전의 어휘",
    subtitle: "전사에 나타나는 각 사전 단어를 정확히 확인하세요.",
    truncateNotice: "처음 {count}개 일치 항목 표시",
    actions: {
      openSettings: "설정 열기",
    },
    badges: {
      dictionaryMode: "사전 모드",
    },
    countLabel: {
      singular: "단어",
      plural: "단어",
    },
    indicators: {
      cardsLabel: "어휘",
      dictionaryLabel: "사전",
      dictionaryMissing: "사전이 선택되지 않음",
    },
    jump: "클립으로 이동",
    empty: {
      noVocabulary: {
        title: "어휘 필요",
        description: "컨텍스트 구문을 탐색하려면 어휘를 생성하거나 가져오세요.",
      },
      noMatches: {
        title: "아직 컨텍스트 구문 없음",
        description: "어휘 용어가 아직 전사에 나타나지 않았습니다.",
      },
      noDictionary: {
        title: "하이라이트를 잠금 해제할 사전 선택",
        description: "설정에서 어휘 사전을 선택하면 학습한 단어를 찾을 수 있습니다.",
      },
    },
  },
  quizSection: {
    title: "대화형 퀴즈",
    description: "이 에피소드에서 추출한 주요 용어를 연습하세요.",
    empty: {
      title: "아직 퀴즈를 사용할 수 없음",
      description: "어휘에 연습 항목이 연결되면 퀴즈 질문이 여기에 표시됩니다.",
    },
    stats: {
      questionSingular: "질문",
      questionPlural: "질문",
      countSingular: "질문",
      countPlural: "질문",
    },
    overview: {
      title: "퀴즈 개요",
      nextTerm: "다음 용어",
      progress: "진행률",
      accuracy: "정확도",
      allCompleted: "모든 퀴즈 항목 완료",
    },
    toast: {
      noSentence: "퀴즈 문장을 오디오 생성에 사용할 수 없습니다.",
      audioGenerationFailed: "오디오 생성 실패",
      audioGenerated: "퀴즈 오디오 생성됨",
    },
    messages: {
      completed: "훌륭합니다! 이 용어는 완전히 섀도잉되었습니다.",
    },
    status: {
      completed: "완료",
      todo: "할 일",
    },
    translation: {
      show: "번역 표시",
      hide: "번역 숨기기",
    },
    shadowing: {
      defaultTitle: "에피소드 퀴즈 연습",
      cta: "섀도잉",
      dialogTitle: "섀도잉 연습",
      instructions: "문장을 듣고 반복하세요",
      showTranslation: "번역 표시",
      hideTranslation: "번역 숨기기",
      audioUnavailable: "참조 오디오를 아직 사용할 수 없습니다. 듣기 위해 퀴즈 카드에서 생성하세요.",
      stopRecording: "녹음 중지",
      startShadowing: "섀도잉 시작",
      yourRecording: "녹음 내용",
      attemptsLabel: "시도",
    },
  },
  persona: {
    progress: {
      label: "단계 {current} / {total}",
    },
    actions: {
      skip: "지금 건너뛰기",
      back: "뒤로",
      continue: "계속",
      finish: "완료",
    },
    steps: {
      languages: {
        eyebrow: "단계 1 · 언어",
        title: "모국어와 목표 언어는 무엇인가요?",
        description: "번역을 개인화하기 위해 선호하는 언어를 알려주세요.",
      },
      level: {
        eyebrow: "단계 2 · 목표 수준",
        title: "목표 언어 수준을 어떻게 평가하시나요?",
        description: "대략적인 자가 평가만 하면 되며 테스트는 필요 없습니다.",
      },
      goals: {
        eyebrow: "단계 3 · 목표",
        title: "목표 언어를 배우는 주요 목표는 무엇인가요?",
        description: "여정에 가장 잘 맞는 목표를 선택하세요. 언제든지 조정할 수 있습니다.",
      },
      topics: {
        eyebrow: "단계 4 · 주제 / 관심사",
        title: "어떤 종류의 동영상을 가장 좋아하시나요?",
        description: "이 주제를 기반으로 영어 동영상을 추천하겠습니다.",
      },
      formats: {
        eyebrow: "단계 5 · 동영상 형식",
        title: "어떤 종류의 동영상 형식을 좋아하시나요?",
        description: "즐기는 스타일을 선택하세요. 추천에 혼합하겠습니다.",
      },
      session: {
        eyebrow: "단계 6 · 세션 길이",
        title: "일반적으로 한 세션에 얼마나 시간이 있으신가요?",
        description: "동영상 길이를 일정에 맞추겠습니다.",
      },
      subtitles: {
        eyebrow: "단계 7 · 자막 및 설명",
        title: "학습을 어떻게 지원해야 할까요?",
        description: "선호하는 자막과 설명을 선택하세요.",
      },
    },
    goals: {
      everyday: "일상 대화",
      watchYoutube: "자막 없이 YouTube 보기",
      jobInterview: "취업 면접 및 경력",
      studyExam: "학습 / 시험 (IELTS, TOEFL 등)",
      travelAbroad: "해외 여행 및 생활",
    },
    topics: {
      dailyVlog: "일상 vlog 및 라이프스타일",
      aiTech: "기술 및 AI",
      career: "경력 및 자기계발",
      comedy: "코미디 및 엔터테인먼트",
      moviesTv: "영화 및 TV 프로그램",
      news: "뉴스 및 시사",
      gaming: "게임",
      education: "교육 및 튜토리얼",
    },
    levels: {
      rangeHint: "{from}–{to} 범위의 동영상을 목표로 합니다.",
      a2: {
        title: "초급 – 일부 단어를 알고 있지만 대부분의 동영상을 따라갈 수 없습니다.",
        description: "기본 CEFR 수준",
      },
      b1: {
        title: "초중급 – 대부분의 콘텐츠를 따라가려면 자막이 필요합니다.",
        description: "초중급",
      },
      b2: {
        title: "중급 – 자막이 있으면 70–80%를 이해합니다.",
        description: "중상급",
      },
      c1: {
        title: "고급 – 자막 없이 대부분의 동영상을 따라갈 수 있습니다.",
        description: "고급",
      },
    },
    formats: {
      vlog: "Vlog / 일상 생활",
      interview: "인터뷰 및 팟캐스트",
      podcast: "팟캐스트 / 오디오 스토리",
      tutorial: "설명 / 튜토리얼",
      shorts: "짧은 클립 / 하이라이트",
      movieClip: "영화 / TV 프로그램 장면",
    },
    sessionLength: {
      label: "세션 길이",
      description: "{minutes}분 목표",
      ten: "5–10분",
      twenty: "10–20분",
      thirty: "20–30분",
      fortyFive: "30분 이상",
    },
    sessionFrequency: {
      prompt: "얼마나 자주 연습하고 싶으신가요?",
      three: "주에 몇 번",
      five: "거의 매일",
      seven: "매일",
    },
    subtitles: {
      alwaysTitle: "항상 영어 자막 표시",
      alwaysDescription: "모든 동영상에 대해 기본적으로 자막을 켜둡니다.",
      nativeTitle: "도움이 필요할 때 내 언어로 번역 표시",
      nativeDescription: "막힐 때마다 모국어 지원을 전환하세요.",
      nativeLanguageLabel: "선호하는 모국어",
      nativeLanguagePlaceholder: "언어 선택",
      nativeLanguageHint: "번역이나 설명을 표시할 때 이것을 사용합니다.",
      nativeLanguage: {
        auto: "자동 감지",
        zh: "중국어",
        es: "스페인어",
        ja: "일본어",
        ko: "한국어",
      },
    },
    languages: {
      nativeLabel: "모국어는 무엇인가요?",
      targetLabel: "어떤 언어를 배우고 있나요?",
      targetPlaceholder: "목표 언어 선택",
      hint: "이것을 사용하여 레슨과 수준 안내를 일치시킵니다.",
      native: {
        en: "영어",
        zh: "중국어",
        es: "스페인어",
        ja: "일본어",
        ko: "한국어",
        fr: "프랑스어",
        de: "독일어",
        pt: "포르투갈어",
        ru: "러시아어",
      },
      target: {
        en: "영어",
        zh: "중국어",
        es: "스페인어",
        ja: "일본어",
        ko: "한국어",
        fr: "프랑스어",
        de: "독일어",
        pt: "포르투갈어",
        ru: "러시아어",
      },
    },
    explanations: {
      title: "보고 싶은 설명",
      keyVocab: "각 동영상의 주요 어휘 표시",
      showPhrases: "유용한 구문 및 표현 강조",
      translateSentences: "어려운 문장을 모국어로 번역",
      pronunciation: "발음 및 섀도잉에 집중",
    },
  },
  vocabQuiz: {
    title: "어휘 퀴즈",
    translationUnavailable: "이 세그먼트에 대한 번역을 사용할 수 없습니다",
    loading: "로딩 중...",
    translation: {
      show: "번역 표시",
      hide: "번역 숨기기",
    },
    actions: {
      replay: "다시 재생",
      continue: "계속",
    },
  },
  mobileImmersive: {
    subtitles: {
      show: "자막 표시",
      hide: "자막 숨기기",
    },
    translation: {
      show: "번역 표시",
      hide: "번역 숨기기",
    },
    narration: {
      enable: "내레이션 모드 활성화",
      disable: "내레이션 모드 비활성화",
      tooltipEnable: "재생 후 어휘 설명 자동 재생",
      tooltipDisable: "어휘 내레이션 비활성화",
      generating: "내레이션 오디오 생성 중...",
      failed: "내레이션 생성 실패",
      modeActive: "내레이션 모드 활성",
      playing: "내레이션 재생 중...",
      ready: "내레이션 준비됨",
      skip: "건너뛰기",
      skipTooltip: "내레이션 건너뛰고 동영상 계속",
      currentTerm: "현재 용어",
      pause: "일시 정지",
      resume: "재개",
      repeat: "다시 재생",
    },
    status: {
      new: "새로운",
      newDescription: "방금 추가됨, 첫 번째 복습 필요",
      fuzzy: "애매함",
      fuzzyDescription: "곧 더 많은 연습 필요",
      mastered: "완전히 습득",
      masteredDescription: "자신 있고 장기 복습 중",
    },
    shadowing: {
      tooltip: "섀도잉 연습",
      dialogTitle: "섀도잉 연습",
      dialogDescription: "현재 자막을 소리 내어 반복하세요.",
      attemptLabel: "시도",
      hideText: "텍스트 숨기기",
      showText: "텍스트 표시",
      hideTranslationShort: "번역 숨기기",
      showTranslationShort: "번역 표시",
      transcriptionLabel: "전사 내용",
      startRecording: "섀도잉 시작",
      stopRecording: "녹음 중지",
      start: "섀도잉 시작",
      stop: "섀도잉 중지",
      unknownEpisode: "알 수 없는 에피소드",
    },
  },
  vocabulary: {
    status: {
      label: "어휘 분류",
      new: "새로운",
      newDescription: "방금 추가됨, 첫 번째 복습 필요",
      fuzzy: "애매함",
      fuzzyDescription: "곧 더 많은 연습 필요",
      mastered: "완전히 습득",
      masteredDescription: "자신 있고 장기 복습 중",
    },
    section: {
      title: "키워드 및 주제",
      topicCountSingular: "{count}개 주제",
      topicCountPlural: "{count}개 주제",
      revealAllButton: "모두 표시",
      revealAllTitle: "모든 설명 표시",
      restoreBlurButton: "블러 복원",
      restoreBlurTitle: "블러 복원",
      levelFilterLabel: "CEFR 수준별 필터",
      levelFilterUserLevel: "귀하의 수준: {level}",
      levelFilterAll: "모두",
      noTermsMatchFilter: "선택한 CEFR 수준과 일치하는 용어가 아직 없습니다. 추가 수준을 선택해 보세요.",
    },
    swipeTip: {
      badge: "팁",
      heading: "스와이프하여 이 스택 관리",
      description: "왼쪽으로 스와이프하여 아는 단어를 숨기거나 오른쪽으로 스와이프하여 연습 흐름에 유지하세요.",
    },
    desktopActions: {
      markKnown: "알고 있음으로 표시",
      keepLearning: "계속 연습",
    },
    card: {
      examplesLabel: "예",
    },
    stack: {
      viewDetails: "세부 정보 보기",
      closeDetails: "닫기",
    },
  },
  transcript: {
    title: "전사",
    generate: "전사 생성",
    generating: "전사 생성 중...",
    generateDesc: "타임스탬프와 대화형 기능이 있는 전체 텍스트를 보려면 전사를 생성하세요.",
    generatingDesc: "이 에피소드의 전사를 생성하는 동안 기다려 주세요.",
    segments: "세그먼트",
    usageDeducted: "전사 크레딧 사용됨",
    usageRemaining: "이번 달 남은 {remaining}/{limit}",
    usageRemainingUnlimited: "귀하의 플랜으로 무제한 전사",
    dynamicIslandSettings: "Dynamic Island 설정",
    dynamicIslandTitle: "Dynamic Island",
    dynamicIslandDescription: "학습 중 Dynamic Island에 표시되는 내용을 제어합니다.",
    dynamicIslandVocabulary: "어휘 팝업",
    dynamicIslandVocabularyHint: "팝업 오버레이 내에 어휘 프롬프트 표시",
    toggleDynamicIslandVocabulary: "어휘 팝업 전환",
    wordMask: "자막 애니메이션",
    toggleWordMask: "자막 애니메이션 전환",
  },
  premiumPrompt: {
    guest: {
      title: "로그인 필요",
      description: "이 콘텐츠에 액세스하고 학습 기능을 잠금 해제하려면 로그인하세요.",
    },
    free: {
      title: "프리미엄 콘텐츠",
      description: "이 에피소드는 모든 기능에 액세스하려면 프리미엄 구독이 필요합니다.",
      secondary: "또는 무료 액세스를 위해 추천 에피소드를 확인하세요.",
    },
    featured: {
      title: "무료 추천 콘텐츠",
      description: "이 무료 에피소드를 즐기고 계신가요? 프리미엄으로 업그레이드하여 전체 콘텐츠 라이브러리를 잠금 해제하세요!",
      cta: "프리미엄 알아보기",
    },
    fallback: "액세스 제한됨",
  },
  contentRenderer: {
    alerts: {
      vocabFallback: "어휘 생성 실패: {error}. 대신 데모 데이터를 사용합니다.",
    },
  },
  upgradePromptDialog: {
    titles: {
      limitReached: "월간 한도 도달",
      upgrade: "프리미엄으로 업그레이드",
      error: "생성 실패",
    },
    usage: {
      label: "월간 사용량",
      summary: "이번 달 사용된 전사 {used}/{limit}",
      remaining: "남은 {remaining}/{limit}",
      unlimited: "프리미엄 플랜으로 무제한 전사",
    },
    benefits: {
      title: "프리미엄으로 업그레이드",
      items: {
        unlimitedTranscripts: "무제한 전사 생성",
        aiSummaries: "모든 Ready 전사 비디오 시청 가능",
        vocabulary: "전체 어휘 분석",
        priority: "우선 처리",
        export: "모든 형식으로 내보내기",
      },
    },
    plans: {
      basic: {
        name: "베이직 플랜",
        short: "베이직",
        badge: "인기",
        price: "$12.99",
        period: "/월",
        description: "개인 학습자에게 완벽",
      },
      premium: {
        name: "프리미엄 플랜",
        short: "프리미엄",
        badge: "최고의 가치",
        price: "$19.99",
        period: "/월",
        description: "고급 기능 및 우선 지원",
      },
    },
    actions: {
      upgradeToPlan: "{plan}로 업그레이드",
      continueWithPlan: "{plan} 계속",
      later: "나중에",
      close: "닫기",
      tryAgain: "다시 시도",
    },
    status: {
      processing: "처리 중...",
    },
    alerts: {
      checkoutFailed: "결제 시작 실패. 다시 시도해 주세요.",
    },
    error: {
      generic: "전사를 생성할 수 없습니다",
      loginRequired: "전사를 생성하려면 로그인하세요. 진행 상황이 저장됩니다.",
    },
    premiumStatus: {
      title: "프리미엄 플랜을 보유하고 있습니다",
      description: "무제한 전사 생성 및 모든 프리미엄 기능을 즐기세요.",
    },
  },
  episode: {
    floatingActions: {
      buttons: {
        open: "빠른 작업 열기",
        collapse: "빠른 작업 접기",
        showTranslation: "번역 표시",
        hideTranslation: "번역 숨기기",
        generateTranslation: "번역 생성",
        generatingTranslation: "번역 생성 중...",
        generateChosenExplanation: "AI 설명 생성",
        generatingChosenExplanation: "설명 생성 중",
        enableWordHighlight: "단어 강조 표시 활성화",
        disableWordHighlight: "단어 강조 표시 비활성화",
        adjustPracticeMode: "연습 모드 조정",
        enablePracticeMode: "연습 모드 활성화",
        resetProgress: "에피소드 진행 상황 재설정",
        backToTop: "맨 위로",
      },
      tooltips: {
        open: "빠른 작업 열기",
        collapse: "빠른 작업 접기",
        showTranslation: "번역 표시",
        hideTranslation: "번역 숨기기",
        generateTranslation: "번역 생성",
        generatingTranslation: "생성 중...",
        generateChosenExplanation: "예제 기반 설명 생성",
        generatingChosenExplanation: "설명 생성 중...",
        dictionaryRequiredTitle: "사전 필요",
        dictionaryRequiredDescription: "강조 표시를 잠금 해제하려면 설정에서 사전을 설정하세요.",
        enableWordHighlight: "단어 강조 표시 활성화",
        disableWordHighlight: "단어 강조 표시 비활성화",
        practiceSetupTitle: "설정 필요",
        practiceSetupDescription: "연습 모드를 시작하려면 어휘를 생성하거나 사전을 선택하세요.",
        practiceActiveTitle: "연습 모드 활성",
        practiceOpenTitle: "연습 모드 열기",
        practiceActiveDescription: "현재 {mode} 모드를 사용 중입니다.",
        practiceOpenDescription: "목표에 맞는 학습 모드를 선택하세요.",
        resetProgressTitle: "에피소드 진행 상황 재설정",
        resetProgressDescription: "이 에피소드에 대한 완전히 습득한 단어만 지웁니다.",
        backToTop: "맨 위로",
      },
      success: {
        translateGenerated: "번역이 성공적으로 생성되었습니다!",
        chosenExplanationGenerated: "설명이 성공적으로 생성되었습니다!",
      },
      alerts: {
        translateMissingData: "번역을 생성할 수 없습니다: 필요한 데이터가 누락되었습니다.",
      },
      errors: {
        translateRequestFailed: "번역 요청 실패",
        translateResponseInvalid: "번역 응답 형식 오류",
        translateConsoleError: "번역 생성 실패",
        translateFailed: "번역 생성 실패, 다시 시도해 주세요.",
        generateVocabularyFirst: "먼저 어휘를 생성하세요",
        dictionaryInitConsole: "사전 초기화 실패",
        dictionaryLoadFailed: "사전을 로드할 수 없습니다. 다시 시도해 주세요",
        dictionaryNotSelected: "먼저 설정에서 어휘 사전을 선택하세요",
        dictionaryInitFailed: "어휘 관리자 초기화 실패",
        dictionaryInitUnable: "어휘 사전을 로드할 수 없습니다. 다시 시도해 주세요",
        chosenExplanationFailed: "설명 생성 실패",
        chosenExplanationMissing: "설명을 생성할 수 없습니다: 어휘가 준비되지 않았습니다.",
        chosenExplanationUnauthorized: "설명을 생성하려면 로그인하세요.",
      },
      practice: {
        noDictionary: "사전 없음",
        spellingSuffix: "철자",
      },
      practiceModes: {
        vocabulary: "어휘",
        toefl: "TOEFL",
        shadowing: "섀도잉",
      },
      dialog: {
        title: "에피소드 진행 상황 재설정",
        description: "이렇게 하면 이 에피소드에 대한 완전히 습득한 단어만 지우고 어휘 연습을 다시 시작합니다. 다른 에피소드의 진행 상황에는 영향을 주지 않습니다. 이 작업은 실행 취소할 수 없습니다.",
        cancel: "취소",
        confirm: "진행 상황 재설정",
      },
    },
    exportDialog: {
      lockedBadge: "프리미엄 기능",
      lockedDescription: "내보내기는 프리미엄 플랜에서 사용할 수 있습니다. 업그레이드하여 전체 에피소드 콘텐츠를 다운로드, 복사 또는 공유하세요.",
      title: "에피소드 내보내기",
      description: '"{title}"을(를) 선호하는 형식으로 내보내기',
      quickActionsTitle: "빠른 작업",
      downloadTitle: "파일 다운로드",
      quickActions: {
        copyMarkdown: "Markdown 복사",
        copyText: "텍스트 복사",
        copyUrl: "URL 복사",
        email: "이메일",
      },
      options: {
        markdown: {
          title: "Markdown 파일",
          description: "형식이 지정된 Markdown(.md)으로 다운로드 — 문서화 및 메모 작성에 완벽",
        },
        text: {
          title: "일반 텍스트 파일",
          description: "간단한 텍스트(.txt)로 다운로드 — 범용 호환성",
        },
        json: {
          title: "JSON 데이터",
          description: "구조화된 데이터(.json)로 다운로드 — 개발자 및 자동화용",
        },
      },
      episodeInfo: {
        episodeLabel: "에피소드",
        authorLabel: "작가",
        unknownAuthor: "알 수 없음",
        includesPrefix: "포함",
        includesSummary: "요약",
        includesChapters: "챕터",
        includesKeyPoints: "주요 포인트",
        includesTranscript: "전체 전사",
      },
    },
    immersiveFloating: {
      subtitles: {
        show: "자막 표시",
        hide: "자막 숨기기",
      },
      translation: {
        show: "카드 번역 표시",
        hide: "카드 번역 숨기기",
      },
      translationReveal: {
        show: "모든 카드 번역 표시",
        hide: "모든 카드 번역 숨기기",
      },
      exit: "몰입 모드 종료",
    },
    videoPlayer: {
      immersive: {
        enterFocus: "포커스 모드",
        exitFocus: "포커스 종료",
        enterImmersive: "몰입 포커스",
        exitImmersive: "몰입 종료",
        enterFocusTitle: "포커스 모드 진입",
        exitFocusTitle: "포커스 모드 종료",
        enterImmersiveTitle: "몰입 모드 진입",
        exitImmersiveTitle: "몰입 모드 종료(Esc)",
      },
      controls: {
        minimize: "플레이어 최소화",
        maximize: "플레이어 복원",
      },
    },
    player: {
      previewLock: {
        badge: "미리보기 한도",
        message: "무료 플랜 미리보기는 처음 2분으로 제한됩니다. 계속 들으려면 업그레이드하세요.",
        messageShort: "미리보기 한도 — 계속하려면 업그레이드",
        tooltip: "무료 플랜 미리보기는 처음 2분으로 제한",
      },
      freePreview: {
        toastTitle: "무료 미리보기 한도 도달",
        toastDescription: "처음 2분을 넘어 계속 들으려면 업그레이드하세요.",
        toastUpgradeCta: "업그레이드",
      },
    },
  },
  summary: {
    title: "요약",
    noSummary: "요약을 사용할 수 없음",
    generate: "요약 생성",
    generating: "요약 생성 중...",
    generateDesc: "이 에피소드의 포괄적인 개요를 얻기 위해 AI 기반 요약을 생성하세요.",
    generatingDesc: "이 에피소드의 AI 기반 요약을 만드는 동안 기다려 주세요.",
    aiOverview: "AI 생성 개요",
    zhTranslation: "중국어 번역",
    zhTranslationExtra: "중국어 번역(추가)",
    translationLoading: "번역 로딩 중...",
  },
  home: {
    featured: {
      title: "추천",
      error: "추천 에피소드 로드 실패",
      refresh: "페이지를 새로고침하여 다시 시도",
    },
    following: {
      title: "팔로우 중",
    },
    episodes: {
      title: "에피소드",
      viewAll: "모두 보기",
      empty: "현재 사용할 수 있는 에피소드가 없습니다.",
    },
    channels: {
      title: "인기 채널",
      viewAll: "모두 보기",
    },
    collections: {
      title: "컬렉션",
      viewAll: "모두 보기",
      countSuffix: "에피소드",
      viewAllInline: "모두 보기",
      categories: {
        "1": "기술 및 AI 프론티어",
        "2": "개인 성장 및 마인드셋",
        "3": "건강 및 웰니스",
        "4": "비즈니스 및 금융",
        "5": "글로벌 이슈 및 정치",
        "6": "관계 및 인간 연결",
        "7": "과학 및 발견",
        "8": "라이프스타일 및 Vlog",
        "9": "역사 및 문화",
        "10": "환경 및 지속가능성",
      },
    },
    youtube: {
      title: "YouTube 추천",
      missingThumbnail: "썸네일 누락",
      playVideo: "{title} 재생",
      defaultCreator: "YouTube 크리에이터",
    },
    free: "무료",
  },
  channel: {
    filters: {
      ariaLabel: "시즌별 에피소드 필터",
      allSeasons: "모든 시즌",
      season: "시즌 {season}",
    },
    badges: {
      episodes: "에피소드",
    },
    empty: {
      title: "에피소드를 찾을 수 없음",
      seasonFiltered: "선택한 시즌에 대한 에피소드를 찾을 수 없습니다.",
      generic: "이 채널에는 아직 에피소드가 없습니다.",
    },
  },
  episodes: {
    title: "인기 에피소드",
    loadingMore: "더 많은 에피소드 로딩 중...",
    loadMore: "더 로드",
    noMore: "로드할 에피소드가 더 이상 없음",
    noneTitle: "에피소드를 찾을 수 없음",
    noneDesc: "현재 인기 에피소드를 사용할 수 없습니다.",
    errorTitle: "에피소드 로드 오류",
    tryAgain: "다시 시도",
  },
  library: {
    title: "내 라이브러리",
    tabs: {
      favorites: "즐겨찾기",
      playlists: "재생 목록",
      podcasts: "팟캐스트",
    },
    emptyFavoritesTitle: "아직 즐겨찾기가 없음",
    emptyFavoritesDesc: "별표를 표시한 에피소드가 여기에 표시됩니다",
    browseEpisodes: "에피소드 찾아보기",
    playlistEmpty: "이 재생 목록이 비어 있음",
    playlistsEmptyTitle: "아직 재생 목록이 없음",
    playlistsEmptyDesc: "에피소드를 정리하기 위해 재생 목록 만들기",
    podcastsEmptyTitle: "아직 팟캐스트 구독이 없음",
    podcastsEmptyDesc: "팟캐스트를 팔로우하여 여기에서 보기",
    discoverPodcasts: "팟캐스트 발견",
    playlistDialog: {
      title: "재생 목록에 추가",
      description: "이 에피소드를 추가할 재생 목록을 선택하거나 새 재생 목록을 만드세요.",
      addingLabel: "추가 중:",
      nameLabel: "재생 목록 이름 *",
      namePlaceholder: "재생 목록 이름 입력...",
      descriptionLabel: "설명(선택 사항)",
      descriptionPlaceholder: "설명 추가...",
      cancel: "취소",
      creating: "만드는 중...",
      createAction: "재생 목록 만들기",
      yourPlaylists: "내 재생 목록",
      createNew: "새 재생 목록 만들기",
      createFirst: "첫 번째 재생 목록 만들기",
      episodesLabel: "에피소드",
    },
  },
  history: {
    title: "최근 시청",
    subtitle: "진행 상황을 추적하고 중단한 곳에서 계속",
    loading: "로딩 중...",
    loginRequiredTitle: "로그인 필요",
    loginRequiredDesc: "시청 기록을 보려면 로그인하세요.",
    noHistoryTitle: "기록을 찾을 수 없음",
    noHistoryAll: "아직 에피소드를 듣지 않았습니다.",
    noHistoryFiltered: (filter: string) => `${filter} 에피소드를 찾을 수 없습니다.`,
    completed: "완료",
    left: "남음",
    watchAgain: "다시 보기",
    continue: "계속",
    view: "보기",
  },
  course: {
    index: {
      title: "코스",
      subtitle: "큐레이션된 코스를 탐색하여 더 빠르게 레벨업하세요.",
      cards: {
        idioms: {
          provider: "Lingoflows",
          title: "가장 일반적인 미국 관용어",
          description: "일상적인 예와 설명으로 관용어를 마스터하세요.",
          coverAlt: "가장 일반적인 미국 관용어 표지",
        },
      },
    },
    detail: {
      headerTitle: "코스",
      menuButton: "메뉴 열기",
      breadcrumb: {
        courses: "코스",
      },
      actions: {
        continue: "학습 계속",
      },
      pagination: "페이지 {page} / {totalPages} • {total} {unit}",
      paginationPrev: "이전",
      paginationNext: "다음",
      totalBadge: "{total} {unit}",
      idiomUnit: "관용어",
      errors: {
        load: "관용어 로드 실패: {message}",
      },
      courses: {
        idioms: {
          title: "가장 일반적인 미국 관용어",
          tagline: "나와 함께 관용어를 마스터하세요!",
          provider: "Lingoflows",
          coverAlt: "가장 일반적인 미국 관용어 표지",
        },
        default: {
          title: "코스",
          tagline: "",
          provider: "",
          coverAlt: "코스 표지",
        },
      },
    },
    common: {
      previous: "이전",
      next: "다음",
    },
    progress: {
      headerLabel: "진행 상황",
      loading: "로딩 중",
      status: {
        completed: "완료",
        todo: "할 일",
      },
      completedTooltip: "완료",
      exampleAria: "예 {index} {status}",
      defaultCourseLabel: "코스",
    },
    freePlanLimit: {
      title: "이 레슨을 잠금 해제하려면 업그레이드",
      description:
        "무료 플랜 사용자는 처음 10개의 관용어에 액세스할 수 있습니다. 전체 코스를 계속 학습하려면 플랜을 업그레이드하세요.",
      lockedCardLabel: "이 레슨을 잠금 해제하려면 업그레이드",
      lockedBanner: "잠금 해제하려면 업그레이드",
      lockedHeading: "학습을 계속하려면 업그레이드",
      lockedMessage:
        "무료 플랜 사용자는 처음 10개의 관용어에 액세스할 수 있습니다. 레슨 #{number}을(를) 포함한 전체 코스를 잠금 해제하려면 업그레이드하세요.",
      backToList: "코스 개요로 돌아가기",
    },
    idiomDetail: {
      breadcrumb: {
        courses: "코스",
      },
      sidebar: {
        idiomLabel: "관용어",
        difficulty: "난이도",
        examples: "예",
        backToList: "목록으로 돌아가기",
      },
      translationToggle: "번역",
      untitled: "제목 없음",
      examples: {
        title: "예",
        description: "각 예를 섀도잉하고 목소리를 녹음하여 이 챕터를 완료하세요.",
        empty: "이 관용어에 대한 예를 아직 사용할 수 없습니다.",
      },
      errors: {
        load: "관용어 세부 정보를 로드할 수 없습니다. 나중에 다시 시도해 주세요.",
      },
    },
    examples: {
      transcribing: "전사 중...",
    },
  },
  savedWords: {
    title: "복습",
    countSuffix: "저장된 단어",
    selectedSuffix: "선택됨",
    blur: {
      show: "설명 표시",
      hide: "설명 흐리게",
    },
    play: {
      playing: "재생 중...",
      playSelected: "선택한 항목 재생",
      playAll: "모두 재생",
    },
    export: {
      label: "내보내기",
      json: "JSON 내보내기",
      csv: "CSV 내보내기",
      jsonShort: "JSON",
      csvShort: "CSV",
    },
    selectMode: {
      enter: "선택",
      exit: "완료",
      selectAll: "모두 선택",
      clear: "지우기",
    },
    filters: {
      all: "모두",
    },
    sort: {
      newest: "최신순",
      oldest: "오래된순",
      alphabetical: "가나다순",
    },
    reviewNow: "지금 복습",
    due: "예정",
    onDeck: "스택에 있음",
    status: {
      new: "새로운",
      fuzzy: "애매함",
      mastered: "완전히 습득",
    },
    next: "다음",
    interval: "간격",
    correct: "정답",
    wrong: "오답",
    reviewed: "복습됨",
    explains: "설명",
    reviewAgain: "다시",
    reviewHard: "어려움",
    reviewGood: "좋음",
    reviewEasy: "쉬움",
    reviewAllCaughtUp: "모두 완료했습니다!",
    reviewAllCaughtUpHint: "지금 복습이 필요한 카드가 없습니다. 나중에 다시 확인하거나 학습할 새 단어를 추가하세요.",
    empty: {
      title: "단어를 찾을 수 없음",
      filterHint: "검색 또는 필터를 조정해 보세요.",
      defaultHint: "에피소드를 보면서 단어 저장을 시작하세요!",
    },
    loginRequiredTitle: "로그인 필요",
    loginRequiredDesc: "저장된 단어를 보려면 로그인하세요.",
    searchPlaceholder: "단어 검색...",
    sources: {
      youdao: "Youdao",
      original: "원본",
    },
    relative: {
      now: "지금",
      dueSoon: "곧 예정",
      justReviewed: "방금 복습함",
      in: "{value}{unit} 후",
      ago: "{value}{unit} 전",
      today: "오늘",
      yesterday: "어제",
    },
    selectionBar: {
      selectedSuffix: "선택됨",
      delete: "선택한 항목 삭제",
    },
  },
  settings: {
    title: "설정",
    nativeLanguage: "모국어",
    nativeLanguageHint: "번역 및 인터페이스를 위한 모국어 선택",
    theme: {
      title: "모양",
      hint: "선호하는 색상 테마 선택",
      light: "라이트",
      lightDesc: "라이트 색상 테마",
      dark: "다크",
      darkDesc: "다크 색상 테마",
      system: "시스템",
      systemDesc: "시스템 기본 설정 따르기",
    },
    cefrLevel: {
      title: "CEFR 수준",
      description: "콘텐츠를 맞춤화할 수 있도록 학습에 가장 적합한 CEFR 수준을 선택하세요.",
      unset: "설정 안 됨",
      saving: "CEFR 수준 저장 중...",
    },
    interfaceLanguage: "인터페이스 언어",
    interfaceLanguageHint: "앱 인터페이스의 언어를 선택하세요. 이는 모국어 설정과 독립적입니다.",
    videoTitles: {
      title: "에피소드 제목 언어",
      description: "번역을 사용할 수 있을 때 홈페이지에 중국어 제목 표시.",
      chineseLabel: "중국어 제목",
      originalLabel: "원본 제목",
      saving: "기본 설정 저장 중...",
    },
    vocabularyDict: "어휘 사전",
    remove: "제거",
    removing: "제거 중...",
    selectVocabularyHint: "필요에 따라 학습하고 싶은 어휘 선택",
    dailyGoal: {
      title: "일일 섀도잉 목표",
      desc: "일일 연습 시간을 설정하고 진행 상황을 빠르게 보기",
      targetMinutes: "목표 분",
      minutesLabel: "분",
      progressPrefix: "오늘의 진행 상황:",
      remainingPrefix: "남음",
      minAbbr: "분",
      completedPrefix: "완료",
      todayWord: "오늘",
      saving: "저장 중...",
      saved: "저장됨",
      loadFailed: "목표 로드 실패",
      saveFailed: "저장 실패",
    },
    examVocab: {
      title: "시험 어휘",
      subtitle: "전문적이고 표준화된 시험 어휘",
    },
  },
  user: {
    plan: {
      premium: "프리미엄",
      free: "무료 플랜",
    },
    badge: {
      premiumMember: "프리미엄 회원",
      unlimited: "무제한",
    },
    usage: {
      transcriptsLabel: "전사",
      remaining: "이번 달 남음",
      limitReached: "월간 한도 도달",
    },
    actions: {
      upgrade: "프리미엄으로 업그레이드",
      settings: "설정",
      help: "도움말 및 지원",
      logout: "로그아웃",
    },
    dialog: {
      limitReached: "월간 전사 한도에 도달했습니다. 무제한 액세스를 위해 프리미엄으로 업그레이드하세요!",
      remaining: "이번 달 남은 전사가 {remaining}개 있습니다. 무제한 액세스를 위해 업그레이드하세요!",
    },
  },
  auth: {
    headings: {
      welcome: "오늘을 의미 있게!",
      start: "학습 여정 시작",
      createAccount: "계정 만들기",
      signIn: "로그인",
    },
    subheadings: {
      login: "다시 오신 것을 환영합니다! 세부 정보를 입력하세요",
      signup: "시작하려면 세부 정보 입력",
      leftLogin: "대화형 팟캐스트 및 AI 기반 기능으로 언어 학습 모험을 계속하세요.",
      leftSignup: "몰입형 동영상 및 매력적인 팟캐스트 콘텐츠로 효과적으로 영어를 배우세요.",
    },
    form: {
      email: "이메일 주소",
      emailPlaceholder: "이메일 입력",
      password: "비밀번호",
      createPassword: "비밀번호 만들기",
      passwordPlaceholderLogin: "비밀번호 입력",
      passwordPlaceholderSignup: "강력한 비밀번호 만들기",
      forgotPassword: "비밀번호를 잊으셨나요?",
      signIn: "로그인",
      createAccount: "계정 만들기",
      signingIn: "로그인 중...",
      creatingAccount: "계정 만드는 중...",
      haveAccount: "이미 계정이 있으신가요?",
      dontHaveAccount: "계정이 없으신가요?",
      signInInstead: "대신 로그인",
    },
    reset: {
      title: "비밀번호 재설정",
      sentTitle: "이메일 확인",
      sentDesc: "비밀번호 재설정 링크를 이메일 주소로 보냈습니다.",
      enterDesc: "이메일 주소를 입력하면 비밀번호를 재설정하는 링크를 보내드립니다.",
      sentTo: "비밀번호 재설정 링크를 다음으로 보냈습니다:",
      notReceived: "이메일을 받지 못하셨나요? 스팸 폴더를 확인하거나 다시 시도하세요.",
      backToSignIn: "로그인으로 돌아가기",
      sendLink: "재설정 링크 보내기",
      sending: "재설정 링크 보내는 중...",
      cancel: "취소",
    },
    errors: {
      invalidEmail: "유효한 이메일 주소를 입력하세요.",
      shortPassword: "비밀번호는 최소 6자 이상이어야 합니다.",
    }
  },
  search: {
    search: "검색",
    trending: "인기",
    podcast: "팟캐스트",
    video: "동영상",
    allCategories: "모든 카테고리",
    categoryPrefix: "카테고리:",
    searchingPodcasts: "팟캐스트 검색 중...",
    loadingVideos: "동영상 로딩 중...",
    errorTitle: "검색 오류",
    noResults: "결과를 찾을 수 없음",
    tryAdjustPodcasts: "검색어를 조정하거나 대신 인기 팟캐스트를 찾아보세요",
    tryAdjustVideos: "검색어를 조정하거나 대신 인기 동영상을 찾아보세요",
    trendingVideos: "인기 동영상",
    loadingMorePodcasts: "더 많은 팟캐스트 로딩 중...",
    endOfResults: "끝에 도달했습니다! 찾은 모든 팟캐스트입니다.",
    categories: {
      "Arts": "예술",
      "Business": "비즈니스",
      "Comedy": "코미디",
      "Education": "교육",
      "Fiction": "소설",
      "Government": "정부",
      "Health & Fitness": "건강 및 피트니스",
      "History": "역사",
      "Kids & Family": "어린이 및 가족",
      "Leisure": "여가",
      "Music": "음악",
      "News": "뉴스",
      "Religion & Spirituality": "종교 및 영성",
      "Science": "과학",
      "Society & Culture": "사회 및 문화",
      "Sports": "스포츠",
      "Technology": "기술",
      "True Crime": "실제 범죄",
      "TV & Film": "TV 및 영화",
    },
  },
};

// French (fr) translations
const fr: Dict = {
  hero: {
    goalLabel: "Objectif",
    minuteShort: "min",
    hour: "heure",
    hours: "heures",
  },
  analytics: {
    learnMore: "En savoir plus",
    chart: {
      practiceTime: "Temps de pratique",
      minutes: "minutes",
      current: "Actuel",
    },
    cards: {
      today: {
        title: "Aujourd'hui",
        unit: "min",
        subtitle: "Temps de pratique d'aujourd'hui",
      },
      sessions: {
        title: "Sessions",
        unit: "pratiques",
        subtitle: "Pratique du shadowing aujourd'hui",
      },
      streak: {
        title: "Série",
        unitSingular: "jour",
        unitPlural: "jours",
        subtitle: "Série de pratique actuelle",
      },
    },
  },
  upgradeDialog: {
    title: "Passer à Premium",
    subtitle: {
      withEpisode: 'Accéder à "{episodeTitle}" et débloquer tout le contenu premium',
      default: "Débloquer tout le contenu et les fonctionnalités premium",
    },
    features: {
      episodes: "Accès à tous les épisodes",
      ai: "Fonctionnalités d'apprentissage alimentées par l'IA",
      support: "Support prioritaire",
    },
    actions: {
      upgrade: "Passer à Premium maintenant",
      continueFree: "Continuer avec l'accès gratuit",
    },
  },
  shadowing: {
    title: "Pratique du Shadowing",
    showTranslation: "Afficher la traduction",
    showSentenceHint: "Cliquez sur l'icône de l'œil pour afficher la phrase",
    replay: "Rejouer",
    continue: "Continuer",
    startShadowing: "Commencer le Shadowing",
    stopRecording: "Arrêter l'enregistrement",
    transcribeFailed: "Échec de la transcription",
  },
  common: {
    loading: "Chargement...",
    save: "Enregistrer",
    speak: "Parler",
    signIn: "Se connecter",
    backToHome: "Retour à l'accueil",
    optional: "Optionnel",
  },
  installPrompt: {
    title: "Installer Longoflows sur votre appareil",
    instructions: {
      tap: "Appuyez",
      thenAdd: 'puis "Ajouter à l\'écran d\'accueil"',
    },
    actions: {
      close: "Fermer l'invite d'installation",
    },
  },
  navigation: {
    tagline: "Votre plateforme d'apprentissage des langues",
    actions: {
      collapse: "Réduire la barre latérale",
      expand: "Développer la barre latérale",
    },
    links: {
      home: "Accueil",
      search: "Rechercher",
      episodes: "Épisodes",
      course: "Cours",
      library: "Bibliothèque",
      review: "Révision",
      history: "Historique",
      settings: "Paramètres",
    },
  },
  tabs: {
    summary: { name: "Résumé", desc: "Résumé d'épisode alimenté par l'IA", mobile: "Résumé" },
    mindmap: { name: "Carte mentale", desc: "Cartographie visuelle des concepts", mobile: "Carte" },
    transcript: { name: "Transcription", desc: "Transcription complète de l'épisode", mobile: "Script" },
    vocabulary: { name: "Vocabulaire", desc: "Termes et phrases clés", mobile: "Vocab" },
    phrase: { name: "Dictionnaire", desc: "Vocabulaire en vue dictionnaire", mobile: "Dict" },
    quiz: { name: "Quiz", desc: "Pratique interactive", mobile: "Quiz" },
  },
  player: {
    freePreview: {
      toastTitle: "Limite d'aperçu gratuit atteinte",
      toastDescription: "Passez à Premium pour continuer à écouter au-delà des 2 premières minutes.",
      toastUpgradeCta: "Passer à Premium",
    },
  },
  phraseSection: {
    title: "Vocabulaire dans le Dictionnaire",
    subtitle: "Voir chaque mot du dictionnaire exactement où il apparaît dans la transcription.",
    truncateNotice: "Affichage des {count} premières correspondances",
    actions: {
      openSettings: "Ouvrir les paramètres",
    },
    badges: {
      dictionaryMode: "Mode dictionnaire",
    },
    countLabel: {
      singular: "mots",
      plural: "mots",
    },
    indicators: {
      cardsLabel: "Vocabulaire",
      dictionaryLabel: "Dictionnaire",
      dictionaryMissing: "Dictionnaire non sélectionné",
    },
    jump: "Aller au clip",
    empty: {
      noVocabulary: {
        title: "Vocabulaire requis",
        description: "Générez ou importez du vocabulaire pour explorer les phrases contextuelles.",
      },
      noMatches: {
        title: "Pas encore de phrases contextuelles",
        description: "Les termes du vocabulaire n'ont pas encore apparu dans la transcription.",
      },
      noDictionary: {
        title: "Choisissez un dictionnaire pour débloquer les surlignages",
        description: "Sélectionnez un dictionnaire de vocabulaire dans les paramètres pour que nous puissions repérer les mots que vous avez appris.",
      },
    },
  },
  quizSection: {
    title: "Quiz Interactif",
    description: "Pratiquez les termes clés extraits de cet épisode.",
    empty: {
      title: "Pas encore de quiz disponible",
      description: "Les questions du quiz apparaîtront ici une fois que le vocabulaire aura des éléments de pratique liés.",
    },
    stats: {
      questionSingular: "question",
      questionPlural: "questions",
      countSingular: "Question",
      countPlural: "Questions",
    },
    overview: {
      title: "Aperçu du Quiz",
      nextTerm: "Terme suivant",
      progress: "Progrès",
      accuracy: "Précision",
      allCompleted: "Tous les éléments du quiz terminés",
    },
    toast: {
      noSentence: "Phrase du quiz non disponible pour la génération audio.",
      audioGenerationFailed: "Échec de la génération audio",
      audioGenerated: "Audio du quiz généré",
    },
    messages: {
      completed: "Excellent travail ! Ce terme est entièrement pratiqué en shadowing.",
    },
    status: {
      completed: "Terminé",
      todo: "À faire",
    },
    translation: {
      show: "Afficher la traduction",
      hide: "Masquer la traduction",
    },
    shadowing: {
      defaultTitle: "Pratique du Quiz d'Épisode",
      cta: "Shadowing",
      dialogTitle: "Pratique du Shadowing",
      instructions: "Écoutez et répétez la phrase",
      showTranslation: "Afficher la traduction",
      hideTranslation: "Masquer la traduction",
      audioUnavailable: "Audio de référence pas encore disponible. Générez-le depuis la carte du quiz pour écouter.",
      stopRecording: "Arrêter l'enregistrement",
      startShadowing: "Commencer le Shadowing",
      yourRecording: "Votre enregistrement",
      attemptsLabel: "Tentatives",
    },
  },
  persona: {
    progress: {
      label: "Étape {current} sur {total}",
    },
    actions: {
      skip: "Passer pour l'instant",
      back: "Retour",
      continue: "Continuer",
      finish: "Terminer",
    },
    steps: {
      languages: {
        eyebrow: "Étape 1 · Langues",
        title: "Quelles sont vos langues maternelle et cible ?",
        description: "Indiquez-nous vos langues préférées pour personnaliser les traductions.",
      },
      level: {
        eyebrow: "Étape 2 · Niveau Cible",
        title: "Comment évaluez-vous votre niveau en langue cible ?",
        description: "Juste une auto-évaluation approximative, aucun test nécessaire.",
      },
      goals: {
        eyebrow: "Étape 3 · Objectifs",
        title: "Quel est votre objectif principal pour apprendre votre langue cible ?",
        description: "Choisissez les objectifs qui correspondent le mieux à votre parcours. Vous pouvez les ajuster à tout moment.",
      },
      topics: {
        eyebrow: "Étape 4 · Sujets / Intérêts",
        title: "Quel type de vidéos appréciez-vous le plus ?",
        description: "Nous recommanderons des vidéos en anglais basées sur ces sujets.",
      },
      formats: {
        eyebrow: "Étape 5 · Formats Vidéo",
        title: "Quel type de format vidéo aimez-vous ?",
        description: "Choisissez les styles que vous appréciez. Nous les mélangerons dans vos recommandations.",
      },
      session: {
        eyebrow: "Étape 6 · Durée de Session",
        title: "Combien de temps avez-vous généralement pour une session ?",
        description: "Nous adapterons la longueur de la vidéo à votre emploi du temps.",
      },
      subtitles: {
        eyebrow: "Étape 7 · Sous-titres et Explication",
        title: "Comment devrions-nous soutenir votre apprentissage ?",
        description: "Choisissez les sous-titres et explications que vous préférez.",
      },
    },
    goals: {
      everyday: "Conversation quotidienne",
      watchYoutube: "Regarder YouTube sans sous-titres",
      jobInterview: "Entretiens d'embauche et carrière",
      studyExam: "Études / examens (IELTS, TOEFL…)",
      travelAbroad: "Voyage et vie à l'étranger",
    },
    topics: {
      dailyVlog: "Vlog quotidien et style de vie",
      aiTech: "Technologie et IA",
      career: "Carrière et développement personnel",
      comedy: "Comédie et divertissement",
      moviesTv: "Films et émissions TV",
      news: "Actualités et événements",
      gaming: "Jeux vidéo",
      education: "Éducation et tutoriels",
    },
    levels: {
      rangeHint: "Nous viserons des vidéos dans la plage {from}–{to}.",
      a2: {
        title: "Débutant – Je connais quelques mots mais ne peux pas suivre la plupart des vidéos.",
        description: "Niveau CEFR de base",
      },
      b1: {
        title: "Pré-intermédiaire – J'ai besoin de sous-titres pour suivre la plupart du contenu.",
        description: "Pré-intermédiaire",
      },
      b2: {
        title: "Intermédiaire – Avec sous-titres je comprends 70–80%.",
        description: "Intermédiaire supérieur",
      },
      c1: {
        title: "Avancé – Je peux suivre la plupart des vidéos sans sous-titres.",
        description: "Avancé",
      },
    },
    formats: {
      vlog: "Vlogs / vie quotidienne",
      interview: "Interviews et podcasts",
      podcast: "Podcasts / histoires audio",
      tutorial: "Explications / tutoriels",
      shorts: "Clips courts / points forts",
      movieClip: "Scènes de films / émissions TV",
    },
    sessionLength: {
      label: "Durée de session",
      description: "Objectif de {minutes} min",
      ten: "5–10 minutes",
      twenty: "10–20 minutes",
      thirty: "20–30 minutes",
      fortyFive: "30+ minutes",
    },
    sessionFrequency: {
      prompt: "À quelle fréquence voulez-vous pratiquer ?",
      three: "Quelques fois par semaine",
      five: "Presque tous les jours",
      seven: "Tous les jours",
    },
    subtitles: {
      alwaysTitle: "Toujours afficher les sous-titres en anglais",
      alwaysDescription: "Gardez les sous-titres activés par défaut pour chaque vidéo.",
      nativeTitle: "Afficher la traduction dans ma langue quand j'ai besoin d'aide",
      nativeDescription: "Activez le support en langue maternelle chaque fois que vous êtes bloqué.",
      nativeLanguageLabel: "Langue maternelle préférée",
      nativeLanguagePlaceholder: "Sélectionner la langue",
      nativeLanguageHint: "Nous l'utiliserons lors de l'affichage de traductions ou d'explications.",
      nativeLanguage: {
        auto: "Détection automatique",
        zh: "Chinois",
        es: "Espagnol",
        ja: "Japonais",
        ko: "Coréen",
      },
    },
    languages: {
      nativeLabel: "Quelle est votre langue maternelle ?",
      targetLabel: "Quelle langue apprenez-vous ?",
      targetPlaceholder: "Sélectionner la langue cible",
      hint: "Nous l'utiliserons pour faire correspondre les leçons et les conseils de niveau.",
      native: {
        en: "Anglais",
        zh: "Chinois",
        es: "Espagnol",
        ja: "Japonais",
        ko: "Coréen",
        fr: "Français",
        de: "Allemand",
        pt: "Portugais",
        ru: "Russe",
      },
      target: {
        en: "Anglais",
        zh: "Chinois",
        es: "Espagnol",
        ja: "Japonais",
        ko: "Coréen",
        fr: "Français",
        de: "Allemand",
        pt: "Portugais",
        ru: "Russe",
      },
    },
    explanations: {
      title: "Explications que vous voulez voir",
      keyVocab: "Afficher le vocabulaire clé pour chaque vidéo",
      showPhrases: "Mettre en évidence les phrases et expressions utiles",
      translateSentences: "Traduire les phrases difficiles dans votre langue maternelle",
      pronunciation: "Se concentrer sur la prononciation et le shadowing",
    },
  },
  vocabQuiz: {
    title: "Quiz de Vocabulaire",
    translationUnavailable: "Traduction non disponible pour ce segment",
    loading: "Chargement...",
    translation: {
      show: "Afficher la traduction",
      hide: "Masquer la traduction",
    },
    actions: {
      replay: "Rejouer",
      continue: "Continuer",
    },
  },
  mobileImmersive: {
    subtitles: {
      show: "Afficher les sous-titres",
      hide: "Masquer les sous-titres",
    },
    translation: {
      show: "Afficher la traduction",
      hide: "Masquer la traduction",
    },
    narration: {
      enable: "Activer le Mode Narration",
      disable: "Désactiver le Mode Narration",
      tooltipEnable: "Lecture automatique des explications de vocabulaire après la lecture",
      tooltipDisable: "Désactiver la narration de vocabulaire",
      generating: "Génération de l'audio de narration...",
      failed: "Échec de la génération de narration",
      modeActive: "Mode Narration Actif",
      playing: "Lecture de la narration...",
      ready: "Narration prête",
      skip: "Passer",
      skipTooltip: "Passer la narration et continuer la vidéo",
      currentTerm: "Terme actuel",
      pause: "Pause",
      resume: "Reprendre",
      repeat: "Rejouer",
    },
    status: {
      new: "Nouveau",
      newDescription: "Vient d'être ajouté, nécessite une première révision",
      fuzzy: "Flou",
      fuzzyDescription: "Nécessite plus de pratique bientôt",
      mastered: "Maîtrisé",
      masteredDescription: "Confiant et en révision longue",
    },
    shadowing: {
      tooltip: "Pratique du shadowing",
      dialogTitle: "Pratique du Shadowing",
      dialogDescription: "Répétez le sous-titre actuel à voix haute.",
      attemptLabel: "Tentative",
      hideText: "Masquer le texte",
      showText: "Afficher le texte",
      hideTranslationShort: "Masquer la traduction",
      showTranslationShort: "Afficher la traduction",
      transcriptionLabel: "Votre transcription",
      startRecording: "Commencer le shadowing",
      stopRecording: "Arrêter l'enregistrement",
      start: "Commencer le shadowing",
      stop: "Arrêter le shadowing",
      unknownEpisode: "Épisode inconnu",
    },
  },
  vocabulary: {
    status: {
      label: "Classification du vocabulaire",
      new: "Nouveau",
      newDescription: "Vient d'être ajouté, nécessite une première révision",
      fuzzy: "Flou",
      fuzzyDescription: "Nécessite plus de pratique bientôt",
      mastered: "Maîtrisé",
      masteredDescription: "Confiant et en révision longue",
    },
    section: {
      title: "Mots-clés et Sujets",
      topicCountSingular: "{count} sujet",
      topicCountPlural: "{count} sujets",
      revealAllButton: "Tout révéler",
      revealAllTitle: "Révéler toutes les explications",
      restoreBlurButton: "Restaurer le flou",
      restoreBlurTitle: "Restaurer le flou",
      levelFilterLabel: "Filtrer par niveau CEFR",
      levelFilterUserLevel: "Votre niveau : {level}",
      levelFilterAll: "Tous",
      noTermsMatchFilter: "Aucun terme ne correspond encore aux niveaux CEFR sélectionnés. Essayez de sélectionner des niveaux supplémentaires.",
    },
    swipeTip: {
      badge: "Astuce",
      heading: "Glissez pour gérer cette pile",
      description: "Glissez vers la gauche pour masquer les mots que vous connaissez, ou vers la droite pour les garder dans votre flux de pratique.",
    },
    desktopActions: {
      markKnown: "Marquer comme connu",
      keepLearning: "Continuer à pratiquer",
    },
    card: {
      examplesLabel: "Exemples",
    },
    stack: {
      viewDetails: "Voir les détails",
      closeDetails: "Fermer",
    },
  },
  transcript: {
    title: "Transcription",
    generate: "Générer la Transcription",
    generating: "Génération de la Transcription...",
    generateDesc: "Générez une transcription pour voir le texte complet avec horodatage et fonctionnalités interactives.",
    generatingDesc: "Veuillez patienter pendant que nous générons la transcription pour cet épisode.",
    segments: "segments",
    usageDeducted: "Crédit de transcription utilisé",
    usageRemaining: "{remaining}/{limit} restants ce mois",
    usageRemainingUnlimited: "Transcriptions illimitées avec votre plan",
    dynamicIslandSettings: "Paramètres Dynamic Island",
    dynamicIslandTitle: "Dynamic Island",
    dynamicIslandDescription: "Contrôlez ce qui apparaît dans le Dynamic Island pendant l'apprentissage.",
    dynamicIslandVocabulary: "Popup de vocabulaire",
    dynamicIslandVocabularyHint: "Afficher les invites de vocabulaire dans la superposition popup",
    toggleDynamicIslandVocabulary: "Basculer le popup de vocabulaire",
    wordMask: "Animation des sous-titres",
    toggleWordMask: "Basculer l'animation des sous-titres",
  },
  premiumPrompt: {
    guest: {
      title: "Connexion requise",
      description: "Veuillez vous connecter pour accéder à ce contenu et débloquer les fonctionnalités d'apprentissage.",
    },
    free: {
      title: "Contenu premium",
      description: "Cet épisode nécessite un abonnement premium pour accéder à toutes les fonctionnalités.",
      secondary: "Ou consultez nos épisodes en vedette pour un accès gratuit.",
    },
    featured: {
      title: "Contenu en vedette gratuit",
      description: "Vous appréciez cet épisode gratuit ? Passez à Premium pour débloquer notre bibliothèque complète de contenu !",
      cta: "En savoir plus sur Premium",
    },
    fallback: "Accès restreint",
  },
  contentRenderer: {
    alerts: {
      vocabFallback: "Échec de la génération du vocabulaire : {error}. Utilisation de données de démonstration à la place.",
    },
  },
  upgradePromptDialog: {
    titles: {
      limitReached: "Limite Mensuelle Atteinte",
      upgrade: "Passer à Premium",
      error: "Échec de la Génération",
    },
    usage: {
      label: "Utilisation Mensuelle",
      summary: "{used}/{limit} transcriptions utilisées ce mois",
      remaining: "{remaining}/{limit} restantes",
      unlimited: "Transcriptions illimitées avec votre plan premium",
    },
    benefits: {
      title: "Passer à Premium",
      items: {
        unlimitedTranscripts: "Génération illimitée de transcriptions",
        aiSummaries: "Regarder toutes les vidéos transcrites Ready",
        vocabulary: "Analyse complète du vocabulaire",
        priority: "Traitement prioritaire",
        export: "Exporter vers tous les formats",
      },
    },
    plans: {
      basic: {
        name: "Plan Basique",
        short: "Basique",
        badge: "Populaire",
        price: "$12.99",
        period: "/mois",
        description: "Parfait pour les apprenants individuels",
      },
      premium: {
        name: "Plan Premium",
        short: "Premium",
        badge: "Meilleure Valeur",
        price: "$19.99",
        period: "/mois",
        description: "Fonctionnalités avancées et support prioritaire",
      },
    },
    actions: {
      upgradeToPlan: "Passer à {plan}",
      continueWithPlan: "Continuer avec {plan}",
      later: "Plus tard",
      close: "Fermer",
      tryAgain: "Réessayer",
    },
    status: {
      processing: "Traitement...",
    },
    alerts: {
      checkoutFailed: "Échec du démarrage du paiement. Veuillez réessayer.",
    },
    error: {
      generic: "Impossible de générer la transcription",
      loginRequired: "Veuillez vous connecter pour générer des transcriptions. Votre progression sera enregistrée.",
    },
    premiumStatus: {
      title: "Vous avez un Plan Premium",
      description: "Profitez d'une génération illimitée de transcriptions et de toutes les fonctionnalités premium.",
    },
  },
  episode: {
    floatingActions: {
      buttons: {
        open: "Ouvrir les actions rapides",
        collapse: "Réduire les actions rapides",
        showTranslation: "Afficher la traduction",
        hideTranslation: "Masquer la traduction",
        generateTranslation: "Générer la traduction",
        generatingTranslation: "Génération de la traduction...",
        generateChosenExplanation: "Générer les explications IA",
        generatingChosenExplanation: "Génération des explications",
        enableWordHighlight: "Activer le surlignage des mots",
        disableWordHighlight: "Désactiver le surlignage des mots",
        adjustPracticeMode: "Ajuster le mode de pratique",
        enablePracticeMode: "Activer le mode de pratique",
        resetProgress: "Réinitialiser la progression de l'épisode",
        backToTop: "Retour en haut",
      },
      tooltips: {
        open: "Ouvrir les Actions Rapides",
        collapse: "Réduire les Actions Rapides",
        showTranslation: "Afficher la Traduction",
        hideTranslation: "Masquer la Traduction",
        generateTranslation: "Générer la Traduction",
        generatingTranslation: "Génération...",
        generateChosenExplanation: "Générer l'explication basée sur des exemples",
        generatingChosenExplanation: "Génération des explications...",
        dictionaryRequiredTitle: "Dictionnaire requis",
        dictionaryRequiredDescription: "Configurez un dictionnaire dans les Paramètres pour débloquer les surlignages.",
        enableWordHighlight: "Activer le Surlignage des Mots",
        disableWordHighlight: "Désactiver le Surlignage des Mots",
        practiceSetupTitle: "Configuration requise",
        practiceSetupDescription: "Générez du vocabulaire ou choisissez un dictionnaire pour commencer le mode de pratique.",
        practiceActiveTitle: "Mode de pratique actif",
        practiceOpenTitle: "Ouvrir les modes de pratique",
        practiceActiveDescription: "Utilisation actuelle du mode {mode}.",
        practiceOpenDescription: "Choisissez un mode d'apprentissage adapté à votre objectif.",
        resetProgressTitle: "Réinitialiser la progression de l'épisode",
        resetProgressDescription: "Effacer les mots maîtrisés pour cet épisode uniquement.",
        backToTop: "Retour en haut",
      },
      success: {
        translateGenerated: "Traduction générée avec succès !",
        chosenExplanationGenerated: "Explications générées avec succès !",
      },
      alerts: {
        translateMissingData: "Impossible de générer la traduction : données requises manquantes.",
      },
      errors: {
        translateRequestFailed: "Échec de la demande de traduction",
        translateResponseInvalid: "Erreur de format de réponse de traduction",
        translateConsoleError: "Échec de la génération de traduction",
        translateFailed: "Échec de la génération de traduction, veuillez réessayer.",
        generateVocabularyFirst: "Veuillez d'abord générer du vocabulaire",
        dictionaryInitConsole: "Échec de l'initialisation du dictionnaire",
        dictionaryLoadFailed: "Impossible de charger le dictionnaire, veuillez réessayer",
        dictionaryNotSelected: "Veuillez d'abord sélectionner un dictionnaire de vocabulaire dans les Paramètres",
        dictionaryInitFailed: "Échec de l'initialisation du gestionnaire de vocabulaire",
        dictionaryInitUnable: "Impossible de charger le dictionnaire de vocabulaire, veuillez réessayer",
        chosenExplanationFailed: "Échec de la génération des explications",
        chosenExplanationMissing: "Impossible de générer les explications : le vocabulaire n'est pas prêt.",
        chosenExplanationUnauthorized: "Veuillez vous connecter pour générer des explications.",
      },
      practice: {
        noDictionary: "Pas de Dictionnaire",
        spellingSuffix: "Orthographe",
      },
      practiceModes: {
        vocabulary: "Vocabulaire",
        toefl: "TOEFL",
        shadowing: "Shadowing",
      },
      dialog: {
        title: "Réinitialiser la Progression de l'Épisode",
        description: "Cela effacera tous les mots maîtrisés pour cet épisode uniquement et redémarrera votre pratique du vocabulaire. La progression des autres épisodes ne sera pas affectée. Cette action ne peut pas être annulée.",
        cancel: "Annuler",
        confirm: "Réinitialiser la Progression",
      },
    },
    exportDialog: {
      lockedBadge: "Fonctionnalité premium",
      lockedDescription: "L'exportation est disponible sur les plans premium. Passez à Premium pour télécharger, copier ou partager le contenu complet de l'épisode.",
      title: "Exporter l'Épisode",
      description: 'Exporter "{title}" dans votre format préféré',
      quickActionsTitle: "Actions Rapides",
      downloadTitle: "Télécharger les Fichiers",
      quickActions: {
        copyMarkdown: "Copier Markdown",
        copyText: "Copier le Texte",
        copyUrl: "Copier l'URL",
        email: "E-mail",
      },
      options: {
        markdown: {
          title: "Fichier Markdown",
          description: "Télécharger en Markdown formaté (.md) — Parfait pour la documentation et la prise de notes",
        },
        text: {
          title: "Fichier Texte Brut",
          description: "Télécharger en texte simple (.txt) — Compatibilité universelle",
        },
        json: {
          title: "Données JSON",
          description: "Télécharger en données structurées (.json) — Pour les développeurs et l'automatisation",
        },
      },
      episodeInfo: {
        episodeLabel: "Épisode",
        authorLabel: "Auteur",
        unknownAuthor: "Inconnu",
        includesPrefix: "Inclut",
        includesSummary: "Résumé",
        includesChapters: "Chapitres",
        includesKeyPoints: "Points Clés",
        includesTranscript: "Transcription Complète",
      },
    },
    immersiveFloating: {
      subtitles: {
        show: "Afficher les sous-titres",
        hide: "Masquer les sous-titres",
      },
      translation: {
        show: "Afficher les traductions des cartes",
        hide: "Masquer les traductions des cartes",
      },
      translationReveal: {
        show: "Révéler toutes les traductions des cartes",
        hide: "Masquer toutes les traductions des cartes",
      },
      exit: "Quitter le mode immersif",
    },
    videoPlayer: {
      immersive: {
        enterFocus: "Mode focus",
        exitFocus: "Quitter le focus",
        enterImmersive: "Focus immersif",
        exitImmersive: "Quitter l'immersif",
        enterFocusTitle: "Entrer en mode focus",
        exitFocusTitle: "Quitter le mode focus",
        enterImmersiveTitle: "Entrer en mode immersif",
        exitImmersiveTitle: "Quitter le mode immersif (Esc)",
      },
      controls: {
        minimize: "Réduire le lecteur",
        maximize: "Restaurer le lecteur",
      },
    },
    player: {
      previewLock: {
        badge: "Limite d'aperçu",
        message: "L'aperçu du plan gratuit est limité aux 2 premières minutes. Passez à Premium pour continuer à écouter.",
        messageShort: "Limite d'aperçu — passez à Premium pour continuer",
        tooltip: "Aperçu du plan gratuit limité aux 2 premières minutes",
      },
      freePreview: {
        toastTitle: "Limite d'aperçu gratuit atteinte",
        toastDescription: "Passez à Premium pour continuer à écouter au-delà des 2 premières minutes.",
        toastUpgradeCta: "Passer à Premium",
      },
    },
  },
  summary: {
    title: "Résumé",
    noSummary: "Aucun Résumé Disponible",
    generate: "Générer le Résumé",
    generating: "Génération du Résumé...",
    generateDesc: "Générez un résumé alimenté par l'IA pour obtenir un aperçu complet de cet épisode.",
    generatingDesc: "Veuillez patienter pendant que nous créons un résumé alimenté par l'IA de cet épisode.",
    aiOverview: "Aperçu généré par l'IA",
    zhTranslation: "Traduction Chinoise",
    zhTranslationExtra: "Traduction Chinoise (extra)",
    translationLoading: "Chargement de la traduction...",
  },
  home: {
    featured: {
      title: "En Vedette",
      error: "Échec du chargement des épisodes en vedette",
      refresh: "Actualiser la page pour réessayer",
    },
    following: {
      title: "Suivi",
    },
    episodes: {
      title: "Épisodes",
      viewAll: "Voir Tout",
      empty: "Aucun épisode disponible pour le moment.",
    },
    channels: {
      title: "Chaînes Populaires",
      viewAll: "Voir Tout",
    },
    collections: {
      title: "Collections",
      viewAll: "Voir Tout",
      countSuffix: "épisodes",
      viewAllInline: "Voir tout",
      categories: {
        "1": "Frontières Technologiques et IA",
        "2": "Croissance Personnelle et État d'Esprit",
        "3": "Santé et Bien-être",
        "4": "Entreprise et Finance",
        "5": "Problèmes Mondiaux et Politique",
        "6": "Relations et Connexion Humaine",
        "7": "Science et Découverte",
        "8": "Style de Vie et Vlogs",
        "9": "Histoire et Culture",
        "10": "Environnement et Durabilité",
      },
    },
    youtube: {
      title: "YouTube en Vedette",
      missingThumbnail: "Miniature manquante",
      playVideo: "Lire {title}",
      defaultCreator: "Créateur YouTube",
    },
    free: "GRATUIT",
  },
  channel: {
    filters: {
      ariaLabel: "Filtrer les épisodes par saison",
      allSeasons: "Toutes les Saisons",
      season: "Saison {season}",
    },
    badges: {
      episodes: "Épisodes",
    },
    empty: {
      title: "Aucun Épisode Trouvé",
      seasonFiltered: "Aucun épisode trouvé pour la saison sélectionnée.",
      generic: "Cette chaîne n'a pas encore d'épisodes.",
    },
  },
  episodes: {
    title: "Épisodes Tendances",
    loadingMore: "Chargement de plus d'épisodes...",
    loadMore: "Charger Plus",
    noMore: "Plus d'épisodes à charger",
    noneTitle: "Aucun Épisode Trouvé",
    noneDesc: "Aucun épisode tendance disponible pour le moment.",
    errorTitle: "Erreur de Chargement des Épisodes",
    tryAgain: "Réessayer",
  },
  library: {
    title: "Ma Bibliothèque",
    tabs: {
      favorites: "Favoris",
      playlists: "Listes de Lecture",
      podcasts: "Podcasts",
    },
    emptyFavoritesTitle: "Pas encore de favoris",
    emptyFavoritesDesc: "Les épisodes que vous marquez apparaîtront ici",
    browseEpisodes: "Parcourir les Épisodes",
    playlistEmpty: "Cette liste de lecture est vide",
    playlistsEmptyTitle: "Pas encore de listes de lecture",
    playlistsEmptyDesc: "Créez des listes de lecture pour organiser vos épisodes",
    podcastsEmptyTitle: "Pas encore d'abonnements de podcasts",
    podcastsEmptyDesc: "Suivez des podcasts pour les voir ici",
    discoverPodcasts: "Découvrir des Podcasts",
    playlistDialog: {
      title: "Ajouter à la Liste de Lecture",
      description: "Sélectionnez une liste de lecture pour ajouter cet épisode, ou créez une nouvelle liste de lecture.",
      addingLabel: "Ajout :",
      nameLabel: "Nom de la Liste *",
      namePlaceholder: "Entrez le nom de la liste...",
      descriptionLabel: "Description (optionnelle)",
      descriptionPlaceholder: "Ajoutez une description...",
      cancel: "Annuler",
      creating: "Création...",
      createAction: "Créer la Liste de Lecture",
      yourPlaylists: "Vos Listes de Lecture",
      createNew: "Créer une Nouvelle Liste de Lecture",
      createFirst: "Créez Votre Première Liste de Lecture",
      episodesLabel: "épisodes",
    },
  },
  history: {
    title: "Récemment Regardé",
    subtitle: "Suivez votre progression et continuez où vous vous êtes arrêté",
    loading: "Chargement...",
    loginRequiredTitle: "Connexion Requise",
    loginRequiredDesc: "Veuillez vous connecter pour voir votre historique d'écoute.",
    noHistoryTitle: "Aucun Historique Trouvé",
    noHistoryAll: "Vous n'avez pas encore écouté d'épisodes.",
    noHistoryFiltered: (filter: string) => `Aucun épisode ${filter} trouvé.`,
    completed: "Terminé",
    left: "restant",
    watchAgain: "Regarder à Nouveau",
    continue: "Continuer",
    view: "Voir",
  },
  course: {
    index: {
      title: "Cours",
      subtitle: "Explorez des cours sélectionnés pour progresser plus rapidement.",
      cards: {
        idioms: {
          provider: "Lingoflows",
          title: "Idiomes Américains les Plus Courants",
          description: "Maîtrisez les idiomes avec des exemples et explications quotidiens.",
          coverAlt: "Couverture des Idiomes Américains les Plus Courants",
        },
      },
    },
    detail: {
      headerTitle: "Cours",
      menuButton: "Ouvrir le menu",
      breadcrumb: {
        courses: "Cours",
      },
      actions: {
        continue: "Continuer l'Apprentissage",
      },
      pagination: "Page {page} / {totalPages} • {total} {unit}",
      paginationPrev: "Précédent",
      paginationNext: "Suivant",
      totalBadge: "{total} {unit}",
      idiomUnit: "idiomes",
      errors: {
        load: "Échec du chargement des idiomes : {message}",
      },
      courses: {
        idioms: {
          title: "Idiomes Américains les Plus Courants",
          tagline: "Maîtrisez les idiomes avec moi !",
          provider: "Lingoflows",
          coverAlt: "Couverture des Idiomes Américains les Plus Courants",
        },
        default: {
          title: "Cours",
          tagline: "",
          provider: "",
          coverAlt: "Couverture du cours",
        },
      },
    },
    common: {
      previous: "Précédent",
      next: "Suivant",
    },
    progress: {
      headerLabel: "Progression",
      loading: "Chargement",
      status: {
        completed: "Terminé",
        todo: "À faire",
      },
      completedTooltip: "Terminé",
      exampleAria: "Exemple {index} {status}",
      defaultCourseLabel: "Cours",
    },
    freePlanLimit: {
      title: "Passez à Premium pour débloquer cette leçon",
      description:
        "Les utilisateurs du plan gratuit peuvent accéder aux 10 premiers idiomes. Passez à Premium pour continuer à apprendre le cours complet.",
      lockedCardLabel: "Passez à Premium pour débloquer cette leçon",
      lockedBanner: "Passez à Premium pour débloquer",
      lockedHeading: "Passez à Premium pour continuer l'apprentissage",
      lockedMessage:
        "Les utilisateurs du plan gratuit peuvent accéder aux 10 premiers idiomes. Passez à Premium pour débloquer le cours complet, y compris la leçon #{number}.",
      backToList: "Retour à l'aperçu du cours",
    },
    idiomDetail: {
      breadcrumb: {
        courses: "Cours",
      },
      sidebar: {
        idiomLabel: "Idiome",
        difficulty: "Difficulté",
        examples: "Exemples",
        backToList: "Retour à la liste",
      },
      translationToggle: "Traduction",
      untitled: "Sans titre",
      examples: {
        title: "Exemples",
        description: "Pratiquez chaque exemple et enregistrez votre voix pour terminer ce chapitre.",
        empty: "Aucun exemple disponible pour cet idiome pour le moment.",
      },
      errors: {
        load: "Impossible de charger les détails de l'idiome. Veuillez réessayer plus tard.",
      },
    },
    examples: {
      transcribing: "Transcription...",
    },
  },
  savedWords: {
    title: "Révision",
    countSuffix: "mots sauvegardés",
    selectedSuffix: "sélectionnés",
    blur: {
      show: "Afficher les Explications",
      hide: "Masquer les Explications",
    },
    play: {
      playing: "Lecture...",
      playSelected: "Lire les Sélectionnés",
      playAll: "Tout Lire",
    },
    export: {
      label: "Exporter",
      json: "Exporter JSON",
      csv: "Exporter CSV",
      jsonShort: "JSON",
      csvShort: "CSV",
    },
    selectMode: {
      enter: "Sélectionner",
      exit: "Terminé",
      selectAll: "Tout Sélectionner",
      clear: "Effacer",
    },
    filters: {
      all: "Tous",
    },
    sort: {
      newest: "Plus Récent en Premier",
      oldest: "Plus Ancien en Premier",
      alphabetical: "A-Z",
    },
    reviewNow: "Réviser Maintenant",
    due: "dû",
    onDeck: "en pile",
    status: {
      new: "Nouveau",
      fuzzy: "Flou",
      mastered: "Maîtrisé",
    },
    next: "Suivant",
    interval: "Intervalle",
    correct: "Correct",
    wrong: "Incorrect",
    reviewed: "Révisé",
    explains: "Explications",
    reviewAgain: "Encore",
    reviewHard: "Difficile",
    reviewGood: "Bien",
    reviewEasy: "Facile",
    reviewAllCaughtUp: "Tout à jour !",
    reviewAllCaughtUpHint: "Aucune carte ne nécessite de révision pour le moment. Revenez plus tard ou ajoutez de nouveaux mots à étudier.",
    empty: {
      title: "Aucun mot trouvé",
      filterHint: "Essayez d'ajuster votre recherche ou vos filtres.",
      defaultHint: "Commencez à sauvegarder des mots en regardant des épisodes !",
    },
    loginRequiredTitle: "Connexion Requise",
    loginRequiredDesc: "Veuillez vous connecter pour voir vos mots sauvegardés.",
    searchPlaceholder: "Rechercher des mots...",
    sources: {
      youdao: "Youdao",
      original: "Original",
    },
    relative: {
      now: "maintenant",
      dueSoon: "bientôt dû",
      justReviewed: "vient d'être révisé",
      in: "dans {value}{unit}",
      ago: "il y a {value}{unit}",
      today: "Aujourd'hui",
      yesterday: "Hier",
    },
    selectionBar: {
      selectedSuffix: "sélectionnés",
      delete: "Supprimer les Sélectionnés",
    },
  },
  settings: {
    title: "Paramètres",
    nativeLanguage: "Langue maternelle",
    nativeLanguageHint: "Choisissez votre langue maternelle pour les traductions et l'interface",
    theme: {
      title: "Apparence",
      hint: "Choisissez votre thème de couleur préféré",
      light: "Clair",
      lightDesc: "Thème de couleur clair",
      dark: "Sombre",
      darkDesc: "Thème de couleur sombre",
      system: "Système",
      systemDesc: "Suivre la préférence du système",
    },
    cefrLevel: {
      title: "Niveau CECR",
      description: "Choisissez le niveau CECR qui correspond le mieux à vos études pour personnaliser le contenu.",
      unset: "Non défini",
      saving: "Enregistrement du niveau CECR...",
    },
    interfaceLanguage: "Langue de l'Interface",
    interfaceLanguageHint: "Choisissez la langue de l'interface de l'application. Ceci est indépendant de votre paramètre de langue maternelle.",
    videoTitles: {
      title: "Langue du titre de l'épisode",
      description: "Afficher les titres en chinois sur la page d'accueil lorsque les traductions sont disponibles.",
      chineseLabel: "Titre en chinois",
      originalLabel: "Titre original",
      saving: "Enregistrement de la préférence...",
    },
    vocabularyDict: "Dictionnaire de Vocabulaire",
    remove: "Supprimer",
    removing: "Suppression...",
    selectVocabularyHint: "Sélectionnez le vocabulaire que vous souhaitez apprendre en fonction de vos besoins",
    dailyGoal: {
      title: "Objectif Quotidien de Shadowing",
      desc: "Définissez votre temps de pratique quotidien et visualisez rapidement votre progression",
      targetMinutes: "Minutes cibles",
      minutesLabel: "Minutes",
      progressPrefix: "Progression d'aujourd'hui :",
      remainingPrefix: "Restant",
      minAbbr: "min",
      completedPrefix: "Terminé",
      todayWord: "aujourd'hui",
      saving: "Enregistrement...",
      saved: "Enregistré",
      loadFailed: "Échec du chargement de l'objectif",
      saveFailed: "Échec de l'enregistrement",
    },
    examVocab: {
      title: "Vocabulaire d'Examen",
      subtitle: "Vocabulaire d'examens professionnels et standardisés",
    },
  },
  user: {
    plan: {
      premium: "Premium",
      free: "Plan Gratuit",
    },
    badge: {
      premiumMember: "Membre Premium",
      unlimited: "Illimité",
    },
    usage: {
      transcriptsLabel: "transcriptions",
      remaining: "restantes ce mois",
      limitReached: "Limite mensuelle atteinte",
    },
    actions: {
      upgrade: "Passer à Premium",
      settings: "Paramètres",
      help: "Aide et Support",
      logout: "Se déconnecter",
    },
    dialog: {
      limitReached: "Vous avez atteint votre limite mensuelle de transcriptions. Passez à Premium pour un accès illimité !",
      remaining: "Vous avez {remaining} transcriptions restantes ce mois. Passez à Premium pour un accès illimité !",
    },
  },
  auth: {
    headings: {
      welcome: "Faites que chaque jour compte !",
      start: "Commencez Votre Voyage d'Apprentissage",
      createAccount: "Créer un Compte",
      signIn: "Se Connecter",
    },
    subheadings: {
      login: "Bon retour ! Veuillez entrer vos informations",
      signup: "Entrez vos informations pour commencer",
      leftLogin: "Continuez votre aventure d'apprentissage des langues avec des podcasts interactifs et des fonctionnalités alimentées par l'IA.",
      leftSignup: "Apprenez l'anglais efficacement avec des vidéos immersives et du contenu de podcasts engageant.",
    },
    form: {
      email: "Adresse e-mail",
      emailPlaceholder: "Entrez votre e-mail",
      password: "Mot de passe",
      createPassword: "Créer un mot de passe",
      passwordPlaceholderLogin: "Entrez votre mot de passe",
      passwordPlaceholderSignup: "Créez un mot de passe fort",
      forgotPassword: "Mot de passe oublié ?",
      signIn: "Se connecter",
      createAccount: "Créer un compte",
      signingIn: "Connexion...",
      creatingAccount: "Création du compte...",
      haveAccount: "Vous avez déjà un compte ?",
      dontHaveAccount: "Vous n'avez pas de compte ?",
      signInInstead: "Se connecter à la place",
    },
    reset: {
      title: "Réinitialiser le Mot de Passe",
      sentTitle: "Vérifiez Votre E-mail",
      sentDesc: "Nous avons envoyé un lien de réinitialisation de mot de passe à votre adresse e-mail.",
      enterDesc: "Entrez votre adresse e-mail et nous vous enverrons un lien pour réinitialiser votre mot de passe.",
      sentTo: "Nous avons envoyé un lien de réinitialisation de mot de passe à :",
      notReceived: "Vous n'avez pas reçu l'e-mail ? Vérifiez votre dossier spam ou réessayez.",
      backToSignIn: "Retour à la Connexion",
      sendLink: "Envoyer le Lien de Réinitialisation",
      sending: "Envoi du Lien de Réinitialisation...",
      cancel: "Annuler",
    },
    errors: {
      invalidEmail: "Veuillez entrer une adresse e-mail valide.",
      shortPassword: "Le mot de passe doit contenir au moins 6 caractères.",
    }
  },
  search: {
    search: "Rechercher",
    trending: "Tendances",
    podcast: "Podcast",
    video: "Vidéo",
    allCategories: "Toutes les Catégories",
    categoryPrefix: "Catégorie :",
    searchingPodcasts: "Recherche de podcasts...",
    loadingVideos: "Chargement des vidéos...",
    errorTitle: "Erreur de Recherche",
    noResults: "Aucun résultat trouvé",
    tryAdjustPodcasts: "Essayez d'ajuster vos termes de recherche ou parcourez les podcasts tendances à la place",
    tryAdjustVideos: "Essayez d'ajuster vos termes de recherche ou parcourez les vidéos tendances à la place",
    trendingVideos: "Vidéos Tendances",
    loadingMorePodcasts: "Chargement de plus de podcasts...",
    endOfResults: "Vous avez atteint la fin ! C'est tous les podcasts que nous avons trouvés.",
    categories: {
      "Arts": "Arts",
      "Business": "Entreprise",
      "Comedy": "Comédie",
      "Education": "Éducation",
      "Fiction": "Fiction",
      "Government": "Gouvernement",
      "Health & Fitness": "Santé et Fitness",
      "History": "Histoire",
      "Kids & Family": "Enfants et Famille",
      "Leisure": "Loisirs",
      "Music": "Musique",
      "News": "Actualités",
      "Religion & Spirituality": "Religion et Spiritualité",
      "Science": "Science",
      "Society & Culture": "Société et Culture",
      "Sports": "Sports",
      "Technology": "Technologie",
      "True Crime": "Crime Véritable",
      "TV & Film": "TV et Cinéma",
    },
  },
};

// German (de) translations
const de: Dict = {
  hero: {
    goalLabel: "Ziel",
    minuteShort: "Min",
    hour: "Stunde",
    hours: "Stunden",
  },
  analytics: {
    learnMore: "Mehr erfahren",
    chart: {
      practiceTime: "Übungszeit",
      minutes: "Minuten",
      current: "Aktuell",
    },
    cards: {
      today: {
        title: "Heute",
        unit: "Min",
        subtitle: "Heutige Übungszeit",
      },
      sessions: {
        title: "Sitzungen",
        unit: "Übungen",
        subtitle: "Shadowing heute üben",
      },
      streak: {
        title: "Serie",
        unitSingular: "Tag",
        unitPlural: "Tage",
        subtitle: "Aktuelle Übungsserie",
      },
    },
  },
  upgradeDialog: {
    title: "Auf Premium upgraden",
    subtitle: {
      withEpisode: 'Auf "{episodeTitle}" zugreifen und alle Premium-Inhalte freischalten',
      default: "Alle Premium-Inhalte und Funktionen freischalten",
    },
    features: {
      episodes: "Zugriff auf alle Episoden",
      ai: "KI-gestützte Lernfunktionen",
      support: "Prioritätssupport",
    },
    actions: {
      upgrade: "Jetzt upgraden",
      continueFree: "Mit kostenlosem Zugang fortfahren",
    },
  },
  shadowing: {
    title: "Shadowing-Übung",
    showTranslation: "Übersetzung anzeigen",
    showSentenceHint: "Klicken Sie auf das Augensymbol, um den Satz anzuzeigen",
    replay: "Wiederholen",
    continue: "Weiter",
    startShadowing: "Shadowing starten",
    stopRecording: "Aufnahme stoppen",
    transcribeFailed: "Transkription fehlgeschlagen",
  },
  common: {
    loading: "Laden...",
    save: "Speichern",
    speak: "Sprechen",
    signIn: "Anmelden",
    backToHome: "Zurück zur Startseite",
    optional: "Optional",
  },
  installPrompt: {
    title: "Longoflows auf Ihrem Gerät installieren",
    instructions: {
      tap: "Tippen Sie",
      thenAdd: 'dann "Zum Startbildschirm hinzufügen"',
    },
    actions: {
      close: "Installationsaufforderung schließen",
    },
  },
  navigation: {
    tagline: "Ihre Sprachlernplattform",
    actions: {
      collapse: "Seitenleiste einklappen",
      expand: "Seitenleiste ausklappen",
    },
    links: {
      home: "Startseite",
      search: "Suchen",
      episodes: "Episoden",
      course: "Kurs",
      library: "Bibliothek",
      review: "Wiederholen",
      history: "Verlauf",
      settings: "Einstellungen",
    },
  },
  tabs: {
    summary: { name: "Zusammenfassung", desc: "KI-gestützte Episodenzusammenfassung", mobile: "Zusammenfassung" },
    mindmap: { name: "Mindmap", desc: "Visuelle Konzeptzuordnung", mobile: "Karte" },
    transcript: { name: "Transkript", desc: "Vollständiges Episodentranskript", mobile: "Skript" },
    vocabulary: { name: "Wortschatz", desc: "Schlüsselbegriffe & Phrasen", mobile: "Wortschatz" },
    phrase: { name: "Wörterbuch", desc: "Wortschatz in Wörterbuchansicht", mobile: "Wörterbuch" },
    quiz: { name: "Quiz", desc: "Interaktive Übung", mobile: "Quiz" },
  },
  player: {
    freePreview: {
      toastTitle: "Kostenlose Vorschau-Grenze erreicht",
      toastDescription: "Upgraden Sie, um über die ersten 2 Minuten hinaus weiterzuhören.",
      toastUpgradeCta: "Upgraden",
    },
  },
  phraseSection: {
    title: "Wortschatz im Wörterbuch",
    subtitle: "Sehen Sie jedes Wörterbuchwort genau dort, wo es im Transkript erscheint.",
    truncateNotice: "Zeige die ersten {count} Übereinstimmungen",
    actions: {
      openSettings: "Einstellungen öffnen",
    },
    badges: {
      dictionaryMode: "Wörterbuchmodus",
    },
    countLabel: {
      singular: "Wörter",
      plural: "Wörter",
    },
    indicators: {
      cardsLabel: "Wortschatz",
      dictionaryLabel: "Wörterbuch",
      dictionaryMissing: "Wörterbuch nicht ausgewählt",
    },
    jump: "Zum Clip springen",
    empty: {
      noVocabulary: {
        title: "Wortschatz erforderlich",
        description: "Generieren oder importieren Sie Wortschatz, um Kontextphrasen zu erkunden.",
      },
      noMatches: {
        title: "Noch keine Kontextphrasen",
        description: "Wortschatzbegriffe sind im Transkript noch nicht aufgetaucht.",
      },
      noDictionary: {
        title: "Wählen Sie ein Wörterbuch, um Hervorhebungen freizuschalten",
        description: "Wählen Sie ein Wortschatzwörterbuch in den Einstellungen, damit wir Wörter finden können, die Sie gelernt haben.",
      },
    },
  },
  quizSection: {
    title: "Interaktives Quiz",
    description: "Üben Sie Schlüsselbegriffe aus dieser Episode.",
    empty: {
      title: "Noch kein Quiz verfügbar",
      description: "Quizfragen erscheinen hier, sobald der Wortschatz verknüpfte Übungselemente hat.",
    },
    stats: {
      questionSingular: "Frage",
      questionPlural: "Fragen",
      countSingular: "Frage",
      countPlural: "Fragen",
    },
    overview: {
      title: "Quiz-Übersicht",
      nextTerm: "Nächster Begriff",
      progress: "Fortschritt",
      accuracy: "Genauigkeit",
      allCompleted: "Alle Quiz-Elemente abgeschlossen",
    },
    toast: {
      noSentence: "Quiz-Satz für Audio-Generierung nicht verfügbar.",
      audioGenerationFailed: "Audio-Generierung fehlgeschlagen",
      audioGenerated: "Quiz-Audio generiert",
    },
    messages: {
      completed: "Großartige Arbeit! Dieser Begriff ist vollständig geübt.",
    },
    status: {
      completed: "Abgeschlossen",
      todo: "Zu erledigen",
    },
    translation: {
      show: "Übersetzung anzeigen",
      hide: "Übersetzung ausblenden",
    },
    shadowing: {
      defaultTitle: "Episoden-Quiz-Übung",
      cta: "Shadowing",
      dialogTitle: "Shadowing-Übung",
      instructions: "Hören Sie zu und wiederholen Sie den Satz",
      showTranslation: "Übersetzung anzeigen",
      hideTranslation: "Übersetzung ausblenden",
      audioUnavailable: "Referenz-Audio noch nicht verfügbar. Generieren Sie es von der Quiz-Karte, um zu hören.",
      stopRecording: "Aufnahme stoppen",
      startShadowing: "Shadowing starten",
      yourRecording: "Ihre Aufnahme",
      attemptsLabel: "Versuche",
    },
  },
  persona: {
    progress: {
      label: "Schritt {current} von {total}",
    },
    actions: {
      skip: "Vorerst überspringen",
      back: "Zurück",
      continue: "Weiter",
      finish: "Beenden",
    },
    steps: {
      languages: {
        eyebrow: "Schritt 1 · Sprachen",
        title: "Was sind Ihre Muttersprache und Zielsprache?",
        description: "Teilen Sie uns Ihre bevorzugten Sprachen mit, damit wir Übersetzungen personalisieren können.",
      },
      level: {
        eyebrow: "Schritt 2 · Zielniveau",
        title: "Wie würden Sie Ihr Zielsprachniveau einschätzen?",
        description: "Nur eine grobe Selbsteinschätzung, kein Test erforderlich.",
      },
      goals: {
        eyebrow: "Schritt 3 · Ziele",
        title: "Was ist Ihr Hauptziel beim Lernen Ihrer Zielsprache?",
        description: "Wählen Sie die Ziele, die am besten zu Ihrer Reise passen. Sie können sie jederzeit anpassen.",
      },
      topics: {
        eyebrow: "Schritt 4 · Themen / Interessen",
        title: "Welche Art von Videos gefällt Ihnen am besten?",
        description: "Wir empfehlen englische Videos basierend auf diesen Themen.",
      },
      formats: {
        eyebrow: "Schritt 5 · Videoformate",
        title: "Welche Art von Videoformat mögen Sie?",
        description: "Wählen Sie die Stile, die Ihnen gefallen. Wir mischen sie in Ihre Empfehlungen.",
      },
      session: {
        eyebrow: "Schritt 6 · Sitzungsdauer",
        title: "Wie viel Zeit haben Sie normalerweise für eine Sitzung?",
        description: "Wir passen die Videolänge an Ihren Zeitplan an.",
      },
      subtitles: {
        eyebrow: "Schritt 7 · Untertitel & Erklärung",
        title: "Wie sollten wir Ihr Lernen unterstützen?",
        description: "Wählen Sie die Untertitel und Erklärungen, die Sie bevorzugen.",
      },
    },
    goals: {
      everyday: "Alltagsgespräch",
      watchYoutube: "YouTube ohne Untertitel ansehen",
      jobInterview: "Bewerbungsgespräche & Karriere",
      studyExam: "Lernen / Prüfungen (IELTS, TOEFL…)",
      travelAbroad: "Reisen & Leben im Ausland",
    },
    topics: {
      dailyVlog: "Täglicher Vlog & Lifestyle",
      aiTech: "Technik & KI",
      career: "Karriere & Selbstverbesserung",
      comedy: "Comedy & Unterhaltung",
      moviesTv: "Filme & TV-Sendungen",
      news: "Nachrichten & aktuelle Ereignisse",
      gaming: "Gaming",
      education: "Bildung & Tutorials",
    },
    levels: {
      rangeHint: "Wir zielen auf Videos im Bereich {from}–{to} ab.",
      a2: {
        title: "Anfänger – Ich kenne einige Wörter, kann aber den meisten Videos nicht folgen.",
        description: "Grundlegendes CEFR-Niveau",
      },
      b1: {
        title: "Untere Mittelstufe – Ich brauche Untertitel, um den meisten Inhalten zu folgen.",
        description: "Untere Mittelstufe",
      },
      b2: {
        title: "Mittelstufe – Mit Untertiteln verstehe ich 70–80%.",
        description: "Obere Mittelstufe",
      },
      c1: {
        title: "Fortgeschritten – Ich kann den meisten Videos ohne Untertitel folgen.",
        description: "Fortgeschritten",
      },
    },
    formats: {
      vlog: "Vlogs / Alltagsleben",
      interview: "Interviews & Podcasts",
      podcast: "Podcasts / Audio-Geschichten",
      tutorial: "Erklärungen / Tutorials",
      shorts: "Kurze Clips / Highlights",
      movieClip: "Film- / TV-Sendungsszenen",
    },
    sessionLength: {
      label: "Sitzungsdauer",
      description: "{minutes} Min Ziel",
      ten: "5–10 Minuten",
      twenty: "10–20 Minuten",
      thirty: "20–30 Minuten",
      fortyFive: "30+ Minuten",
    },
    sessionFrequency: {
      prompt: "Wie oft möchten Sie üben?",
      three: "Ein paar Mal pro Woche",
      five: "Fast jeden Tag",
      seven: "Jeden Tag",
    },
    subtitles: {
      alwaysTitle: "Immer englische Untertitel anzeigen",
      alwaysDescription: "Untertitel standardmäßig für jedes Video aktiviert lassen.",
      nativeTitle: "Übersetzung in meiner Sprache anzeigen, wenn ich Hilfe brauche",
      nativeDescription: "Muttersprachunterstützung umschalten, wann immer Sie feststecken.",
      nativeLanguageLabel: "Bevorzugte Muttersprache",
      nativeLanguagePlaceholder: "Sprache auswählen",
      nativeLanguageHint: "Wir verwenden dies beim Anzeigen von Übersetzungen oder Erklärungen.",
      nativeLanguage: {
        auto: "Automatisch erkennen",
        zh: "Chinesisch",
        es: "Spanisch",
        ja: "Japanisch",
        ko: "Koreanisch",
      },
    },
    languages: {
      nativeLabel: "Was ist Ihre Muttersprache?",
      targetLabel: "Welche Sprache lernen Sie?",
      targetPlaceholder: "Zielsprache auswählen",
      hint: "Wir verwenden dies, um Lektionen und Niveauanleitung abzustimmen.",
      native: {
        en: "Englisch",
        zh: "Chinesisch",
        es: "Spanisch",
        ja: "Japanisch",
        ko: "Koreanisch",
        fr: "Französisch",
        de: "Deutsch",
        pt: "Portugiesisch",
        ru: "Russisch",
      },
      target: {
        en: "Englisch",
        zh: "Chinesisch",
        es: "Spanisch",
        ja: "Japanisch",
        ko: "Koreanisch",
        fr: "Französisch",
        de: "Deutsch",
        pt: "Portugiesisch",
        ru: "Russisch",
      },
    },
    explanations: {
      title: "Erklärungen, die Sie sehen möchten",
      keyVocab: "Schlüsselwortschatz für jedes Video anzeigen",
      showPhrases: "Nützliche Phrasen & Ausdrücke hervorheben",
      translateSentences: "Schwierige Sätze in Ihre Muttersprache übersetzen",
      pronunciation: "Fokus auf Aussprache und Shadowing",
    },
  },
  vocabQuiz: {
    title: "Wortschatz-Quiz",
    translationUnavailable: "Übersetzung für dieses Segment nicht verfügbar",
    loading: "Laden...",
    translation: {
      show: "Übersetzung anzeigen",
      hide: "Übersetzung ausblenden",
    },
    actions: {
      replay: "Wiederholen",
      continue: "Weiter",
    },
  },
  mobileImmersive: {
    subtitles: {
      show: "Untertitel anzeigen",
      hide: "Untertitel ausblenden",
    },
    translation: {
      show: "Übersetzung anzeigen",
      hide: "Übersetzung ausblenden",
    },
    narration: {
      enable: "Erzählmodus aktivieren",
      disable: "Erzählmodus deaktivieren",
      tooltipEnable: "Wortschatzerklärungen nach der Wiedergabe automatisch abspielen",
      tooltipDisable: "Wortschatzerzählung deaktivieren",
      generating: "Erzähl-Audio wird generiert...",
      failed: "Erzählung generieren fehlgeschlagen",
      modeActive: "Erzählmodus aktiv",
      playing: "Erzählung wird abgespielt...",
      ready: "Erzählung bereit",
      skip: "Überspringen",
      skipTooltip: "Erzählung überspringen und Video fortsetzen",
      currentTerm: "Aktueller Begriff",
      pause: "Pause",
      resume: "Fortsetzen",
      repeat: "Wiederholen",
    },
    status: {
      new: "Neu",
      newDescription: "Gerade hinzugefügt, benötigt erste Überprüfung",
      fuzzy: "Unklar",
      fuzzyDescription: "Benötigt bald mehr Übung",
      mastered: "Gelernt",
      masteredDescription: "Sicher und in langer Überprüfung",
    },
    shadowing: {
      tooltip: "Shadowing-Übung",
      dialogTitle: "Shadowing-Übung",
      dialogDescription: "Wiederholen Sie den aktuellen Untertitel laut.",
      attemptLabel: "Versuch",
      hideText: "Text ausblenden",
      showText: "Text anzeigen",
      hideTranslationShort: "Übersetzung ausblenden",
      showTranslationShort: "Übersetzung anzeigen",
      transcriptionLabel: "Ihre Transkription",
      startRecording: "Shadowing starten",
      stopRecording: "Aufnahme stoppen",
      start: "Shadowing starten",
      stop: "Shadowing stoppen",
      unknownEpisode: "Unbekannte Episode",
    },
  },
  vocabulary: {
    status: {
      label: "Wortschatzklassifizierung",
      new: "Neu",
      newDescription: "Gerade hinzugefügt, benötigt erste Überprüfung",
      fuzzy: "Unklar",
      fuzzyDescription: "Benötigt bald mehr Übung",
      mastered: "Gelernt",
      masteredDescription: "Sicher und in langer Überprüfung",
    },
    section: {
      title: "Schlüsselwörter & Themen",
      topicCountSingular: "{count} Thema",
      topicCountPlural: "{count} Themen",
      revealAllButton: "Alle anzeigen",
      revealAllTitle: "Alle Erklärungen anzeigen",
      restoreBlurButton: "Unschärfe wiederherstellen",
      restoreBlurTitle: "Unschärfe wiederherstellen",
      levelFilterLabel: "Nach CEFR-Niveau filtern",
      levelFilterUserLevel: "Ihr Niveau: {level}",
      levelFilterAll: "Alle",
      noTermsMatchFilter: "Keine Begriffe entsprechen noch den ausgewählten CEFR-Niveaus. Versuchen Sie, zusätzliche Niveaus auszuwählen.",
    },
    swipeTip: {
      badge: "Tipp",
      heading: "Wischen, um diesen Stapel zu verwalten",
      description: "Wischen Sie nach links, um Wörter, die Sie kennen, auszublenden, oder nach rechts, um sie in Ihrem Übungsfluss zu behalten.",
    },
    desktopActions: {
      markKnown: "Als bekannt markieren",
      keepLearning: "Weiter üben",
    },
    card: {
      examplesLabel: "Beispiele",
    },
    stack: {
      viewDetails: "Details anzeigen",
      closeDetails: "Schließen",
    },
  },
  transcript: {
    title: "Transkript",
    generate: "Transkript generieren",
    generating: "Transkript wird generiert...",
    generateDesc: "Generieren Sie ein Transkript, um den vollständigen Text mit Zeitstempeln und interaktiven Funktionen zu sehen.",
    generatingDesc: "Bitte warten Sie, während wir das Transkript für diese Episode generieren.",
    segments: "Segmente",
    usageDeducted: "Transkript-Guthaben verwendet",
    usageRemaining: "{remaining}/{limit} verbleibend diesen Monat",
    usageRemainingUnlimited: "Unbegrenzte Transkripte mit Ihrem Plan",
    dynamicIslandSettings: "Dynamic Island-Einstellungen",
    dynamicIslandTitle: "Dynamic Island",
    dynamicIslandDescription: "Steuern Sie, was im Dynamic Island während des Lernens angezeigt wird.",
    dynamicIslandVocabulary: "Wortschatz-Popup",
    dynamicIslandVocabularyHint: "Wortschatzaufforderungen im Popup-Overlay anzeigen",
    toggleDynamicIslandVocabulary: "Wortschatz-Popup umschalten",
    wordMask: "Untertitel-Animation",
    toggleWordMask: "Untertitel-Animation umschalten",
  },
  premiumPrompt: {
    guest: {
      title: "Anmeldung erforderlich",
      description: "Bitte melden Sie sich an, um auf diesen Inhalt zuzugreifen und Lernfunktionen freizuschalten.",
    },
    free: {
      title: "Premium-Inhalt",
      description: "Diese Episode erfordert ein Premium-Abonnement, um auf alle Funktionen zuzugreifen.",
      secondary: "Oder schauen Sie sich unsere Featured-Episoden für kostenlosen Zugang an.",
    },
    featured: {
      title: "Kostenloser Featured-Inhalt",
      description: "Gefällt Ihnen diese kostenlose Episode? Upgraden Sie auf Premium, um unsere vollständige Inhaltsbibliothek freizuschalten!",
      cta: "Mehr über Premium erfahren",
    },
    fallback: "Zugriff eingeschränkt",
  },
  contentRenderer: {
    alerts: {
      vocabFallback: "Wortschatzgenerierung fehlgeschlagen: {error}. Verwende stattdessen Demo-Daten.",
    },
  },
  upgradePromptDialog: {
    titles: {
      limitReached: "Monatslimit erreicht",
      upgrade: "Auf Premium upgraden",
      error: "Generierung fehlgeschlagen",
    },
    usage: {
      label: "Monatliche Nutzung",
      summary: "{used}/{limit} Transkripte diesen Monat verwendet",
      remaining: "{remaining}/{limit} verbleibend",
      unlimited: "Unbegrenzte Transkripte mit Ihrem Premium-Plan",
    },
    benefits: {
      title: "Auf Premium upgraden",
      items: {
        unlimitedTranscripts: "Unbegrenzte Transkriptgenerierung",
        aiSummaries: "Alle Ready transkribierten Videos ansehen",
        vocabulary: "Vollständige Wortschatzanalyse",
        priority: "Prioritätsverarbeitung",
        export: "In alle Formate exportieren",
      },
    },
    plans: {
      basic: {
        name: "Basis-Plan",
        short: "Basis",
        badge: "Beliebt",
        price: "$12.99",
        period: "/Monat",
        description: "Perfekt für einzelne Lernende",
      },
      premium: {
        name: "Premium-Plan",
        short: "Premium",
        badge: "Bester Wert",
        price: "$19.99",
        period: "/Monat",
        description: "Erweiterte Funktionen & Prioritätssupport",
      },
    },
    actions: {
      upgradeToPlan: "Auf {plan} upgraden",
      continueWithPlan: "Mit {plan} fortfahren",
      later: "Später",
      close: "Schließen",
      tryAgain: "Erneut versuchen",
    },
    status: {
      processing: "Verarbeitung...",
    },
    alerts: {
      checkoutFailed: "Checkout konnte nicht gestartet werden. Bitte versuchen Sie es erneut.",
    },
    error: {
      generic: "Transkript kann nicht generiert werden",
      loginRequired: "Bitte melden Sie sich an, um Transkripte zu generieren. Ihr Fortschritt wird gespeichert.",
    },
    premiumStatus: {
      title: "Sie haben einen Premium-Plan",
      description: "Genießen Sie unbegrenzte Transkriptgenerierung und alle Premium-Funktionen.",
    },
  },
  episode: {
    floatingActions: {
      buttons: {
        open: "Schnellaktionen öffnen",
        collapse: "Schnellaktionen einklappen",
        showTranslation: "Übersetzung anzeigen",
        hideTranslation: "Übersetzung ausblenden",
        generateTranslation: "Übersetzung generieren",
        generatingTranslation: "Übersetzung wird generiert...",
        generateChosenExplanation: "KI-Erklärungen generieren",
        generatingChosenExplanation: "Erklärungen werden generiert",
        enableWordHighlight: "Worthervorhebung aktivieren",
        disableWordHighlight: "Worthervorhebung deaktivieren",
        adjustPracticeMode: "Übungsmodus anpassen",
        enablePracticeMode: "Übungsmodus aktivieren",
        resetProgress: "Episodenfortschritt zurücksetzen",
        backToTop: "Nach oben",
      },
      tooltips: {
        open: "Schnellaktionen öffnen",
        collapse: "Schnellaktionen einklappen",
        showTranslation: "Übersetzung anzeigen",
        hideTranslation: "Übersetzung ausblenden",
        generateTranslation: "Übersetzung generieren",
        generatingTranslation: "Generierung...",
        generateChosenExplanation: "Beispielbasierte Erklärung generieren",
        generatingChosenExplanation: "Erklärungen werden generiert...",
        dictionaryRequiredTitle: "Wörterbuch erforderlich",
        dictionaryRequiredDescription: "Richten Sie ein Wörterbuch in den Einstellungen ein, um Hervorhebungen freizuschalten.",
        enableWordHighlight: "Worthervorhebung aktivieren",
        disableWordHighlight: "Worthervorhebung deaktivieren",
        practiceSetupTitle: "Einrichtung erforderlich",
        practiceSetupDescription: "Generieren Sie Wortschatz oder wählen Sie ein Wörterbuch, um den Übungsmodus zu starten.",
        practiceActiveTitle: "Übungsmodus aktiv",
        practiceOpenTitle: "Übungsmodi öffnen",
        practiceActiveDescription: "Aktuell wird der {mode}-Modus verwendet.",
        practiceOpenDescription: "Wählen Sie einen Lernmodus, der zu Ihrem Ziel passt.",
        resetProgressTitle: "Episodenfortschritt zurücksetzen",
        resetProgressDescription: "Gelernte Wörter nur für diese Episode löschen.",
        backToTop: "Nach oben",
      },
      success: {
        translateGenerated: "Übersetzung erfolgreich generiert!",
        chosenExplanationGenerated: "Erklärungen erfolgreich generiert!",
      },
      alerts: {
        translateMissingData: "Übersetzung kann nicht generiert werden: erforderliche Daten fehlen.",
      },
      errors: {
        translateRequestFailed: "Übersetzungsanfrage fehlgeschlagen",
        translateResponseInvalid: "Übersetzungsantwortformatfehler",
        translateConsoleError: "Übersetzungsgenerierung fehlgeschlagen",
        translateFailed: "Übersetzungsgenerierung fehlgeschlagen, bitte versuchen Sie es erneut.",
        generateVocabularyFirst: "Bitte generieren Sie zuerst Wortschatz",
        dictionaryInitConsole: "Wörterbuchinitialisierung fehlgeschlagen",
        dictionaryLoadFailed: "Wörterbuch kann nicht geladen werden, bitte versuchen Sie es erneut",
        dictionaryNotSelected: "Bitte wählen Sie zuerst ein Wortschatzwörterbuch in den Einstellungen",
        dictionaryInitFailed: "Wortschatzmanager-Initialisierung fehlgeschlagen",
        dictionaryInitUnable: "Wortschatzwörterbuch kann nicht geladen werden, bitte versuchen Sie es erneut",
        chosenExplanationFailed: "Erklärungsgenerierung fehlgeschlagen",
        chosenExplanationMissing: "Erklärungen können nicht generiert werden: Wortschatz ist nicht bereit.",
        chosenExplanationUnauthorized: "Bitte melden Sie sich an, um Erklärungen zu generieren.",
      },
      practice: {
        noDictionary: "Kein Wörterbuch",
        spellingSuffix: "Rechtschreibung",
      },
      practiceModes: {
        vocabulary: "Wortschatz",
        toefl: "TOEFL",
        shadowing: "Shadowing",
      },
      dialog: {
        title: "Episodenfortschritt zurücksetzen",
        description: "Dies löscht alle gelernten Wörter nur für diese Episode und startet Ihre Wortschatzübung neu. Der Fortschritt anderer Episoden wird nicht beeinflusst. Diese Aktion kann nicht rückgängig gemacht werden.",
        cancel: "Abbrechen",
        confirm: "Fortschritt zurücksetzen",
      },
    },
    exportDialog: {
      lockedBadge: "Premium-Funktion",
      lockedDescription: "Export ist in Premium-Plänen verfügbar. Upgraden Sie, um vollständigen Episodeninhalt herunterzuladen, zu kopieren oder zu teilen.",
      title: "Episode exportieren",
      description: '"{title}" in Ihrem bevorzugten Format exportieren',
      quickActionsTitle: "Schnellaktionen",
      downloadTitle: "Dateien herunterladen",
      quickActions: {
        copyMarkdown: "Markdown kopieren",
        copyText: "Text kopieren",
        copyUrl: "URL kopieren",
        email: "E-Mail",
      },
      options: {
        markdown: {
          title: "Markdown-Datei",
          description: "Als formatiertes Markdown (.md) herunterladen — Perfekt für Dokumentation und Notizen",
        },
        text: {
          title: "Nur-Text-Datei",
          description: "Als einfacher Text (.txt) herunterladen — Universelle Kompatibilität",
        },
        json: {
          title: "JSON-Daten",
          description: "Als strukturierte Daten (.json) herunterladen — Für Entwickler und Automatisierung",
        },
      },
      episodeInfo: {
        episodeLabel: "Episode",
        authorLabel: "Autor",
        unknownAuthor: "Unbekannt",
        includesPrefix: "Enthält",
        includesSummary: "Zusammenfassung",
        includesChapters: "Kapitel",
        includesKeyPoints: "Schlüsselpunkte",
        includesTranscript: "Vollständiges Transkript",
      },
    },
    immersiveFloating: {
      subtitles: {
        show: "Untertitel anzeigen",
        hide: "Untertitel ausblenden",
      },
      translation: {
        show: "Kartenübersetzungen anzeigen",
        hide: "Kartenübersetzungen ausblenden",
      },
      translationReveal: {
        show: "Alle Kartenübersetzungen anzeigen",
        hide: "Alle Kartenübersetzungen ausblenden",
      },
      exit: "Immersionsmodus verlassen",
    },
    videoPlayer: {
      immersive: {
        enterFocus: "Fokusmodus",
        exitFocus: "Fokus verlassen",
        enterImmersive: "Immersiver Fokus",
        exitImmersive: "Immersiv verlassen",
        enterFocusTitle: "Fokusmodus betreten",
        exitFocusTitle: "Fokusmodus verlassen",
        enterImmersiveTitle: "Immersionsmodus betreten",
        exitImmersiveTitle: "Immersionsmodus verlassen (Esc)",
      },
      controls: {
        minimize: "Player minimieren",
        maximize: "Player wiederherstellen",
      },
    },
    player: {
      previewLock: {
        badge: "Vorschau-Grenze",
        message: "Die Vorschau des kostenlosen Plans ist auf die ersten 2 Minuten beschränkt. Upgraden Sie, um weiterzuhören.",
        messageShort: "Vorschau-Grenze — upgraden Sie, um fortzufahren",
        tooltip: "Vorschau des kostenlosen Plans auf die ersten 2 Minuten beschränkt",
      },
      freePreview: {
        toastTitle: "Kostenlose Vorschau-Grenze erreicht",
        toastDescription: "Upgraden Sie, um über die ersten 2 Minuten hinaus weiterzuhören.",
        toastUpgradeCta: "Upgraden",
      },
    },
  },
  summary: {
    title: "Zusammenfassung",
    noSummary: "Keine Zusammenfassung verfügbar",
    generate: "Zusammenfassung generieren",
    generating: "Zusammenfassung wird generiert...",
    generateDesc: "Generieren Sie eine KI-gestützte Zusammenfassung, um einen umfassenden Überblick über diese Episode zu erhalten.",
    generatingDesc: "Bitte warten Sie, während wir eine KI-gestützte Zusammenfassung dieser Episode erstellen.",
    aiOverview: "KI-generierter Überblick",
    zhTranslation: "Chinesische Übersetzung",
    zhTranslationExtra: "Chinesische Übersetzung (extra)",
    translationLoading: "Übersetzung wird geladen...",
  },
  home: {
    featured: {
      title: "Featured",
      error: "Laden der Featured-Episoden fehlgeschlagen",
      refresh: "Seite aktualisieren, um es erneut zu versuchen",
    },
    following: {
      title: "Folgen",
    },
    episodes: {
      title: "Episoden",
      viewAll: "Alle anzeigen",
      empty: "Derzeit sind keine Episoden verfügbar.",
    },
    channels: {
      title: "Beliebte Kanäle",
      viewAll: "Alle anzeigen",
    },
    collections: {
      title: "Sammlungen",
      viewAll: "Alle anzeigen",
      countSuffix: "Episoden",
      viewAllInline: "Alle anzeigen",
      categories: {
        "1": "Technologie- & KI-Grenzen",
        "2": "Persönliches Wachstum & Mindset",
        "3": "Gesundheit & Wohlbefinden",
        "4": "Business & Finanzen",
        "5": "Globale Themen & Politik",
        "6": "Beziehungen & Menschliche Verbindung",
        "7": "Wissenschaft & Entdeckung",
        "8": "Lifestyle & Vlogs",
        "9": "Geschichte & Kultur",
        "10": "Umwelt & Nachhaltigkeit",
      },
    },
    youtube: {
      title: "YouTube Featured",
      missingThumbnail: "Miniaturansicht fehlt",
      playVideo: "{title} abspielen",
      defaultCreator: "YouTube-Ersteller",
    },
    free: "KOSTENLOS",
  },
  channel: {
    filters: {
      ariaLabel: "Episoden nach Staffel filtern",
      allSeasons: "Alle Staffeln",
      season: "Staffel {season}",
    },
    badges: {
      episodes: "Episoden",
    },
    empty: {
      title: "Keine Episoden gefunden",
      seasonFiltered: "Keine Episoden für die ausgewählte Staffel gefunden.",
      generic: "Dieser Kanal hat noch keine Episoden.",
    },
  },
  episodes: {
    title: "Trending Episoden",
    loadingMore: "Weitere Episoden werden geladen...",
    loadMore: "Mehr laden",
    noMore: "Keine weiteren Episoden zum Laden",
    noneTitle: "Keine Episoden gefunden",
    noneDesc: "Derzeit sind keine Trending-Episoden verfügbar.",
    errorTitle: "Fehler beim Laden der Episoden",
    tryAgain: "Erneut versuchen",
  },
  library: {
    title: "Meine Bibliothek",
    tabs: {
      favorites: "Favoriten",
      playlists: "Wiedergabelisten",
      podcasts: "Podcasts",
    },
    emptyFavoritesTitle: "Noch keine Favoriten",
    emptyFavoritesDesc: "Episoden, die Sie markieren, erscheinen hier",
    browseEpisodes: "Episoden durchsuchen",
    playlistEmpty: "Diese Wiedergabeliste ist leer",
    playlistsEmptyTitle: "Noch keine Wiedergabelisten",
    playlistsEmptyDesc: "Erstellen Sie Wiedergabelisten, um Ihre Episoden zu organisieren",
    podcastsEmptyTitle: "Noch keine Podcast-Abonnements",
    podcastsEmptyDesc: "Folgen Sie Podcasts, um sie hier zu sehen",
    discoverPodcasts: "Podcasts entdecken",
    playlistDialog: {
      title: "Zur Wiedergabeliste hinzufügen",
      description: "Wählen Sie eine Wiedergabeliste aus, um diese Episode hinzuzufügen, oder erstellen Sie eine neue Wiedergabeliste.",
      addingLabel: "Hinzufügen:",
      nameLabel: "Wiedergabelistenname *",
      namePlaceholder: "Wiedergabelistenname eingeben...",
      descriptionLabel: "Beschreibung (optional)",
      descriptionPlaceholder: "Beschreibung hinzufügen...",
      cancel: "Abbrechen",
      creating: "Erstellen...",
      createAction: "Wiedergabeliste erstellen",
      yourPlaylists: "Ihre Wiedergabelisten",
      createNew: "Neue Wiedergabeliste erstellen",
      createFirst: "Erstellen Sie Ihre erste Wiedergabeliste",
      episodesLabel: "Episoden",
    },
  },
  history: {
    title: "Kürzlich angesehen",
    subtitle: "Verfolgen Sie Ihren Fortschritt und setzen Sie dort fort, wo Sie aufgehört haben",
    loading: "Laden...",
    loginRequiredTitle: "Anmeldung erforderlich",
    loginRequiredDesc: "Bitte melden Sie sich an, um Ihren Wiedergabeverlauf anzuzeigen.",
    noHistoryTitle: "Kein Verlauf gefunden",
    noHistoryAll: "Sie haben noch keine Episoden gehört.",
    noHistoryFiltered: (filter: string) => `Keine ${filter} Episoden gefunden.`,
    completed: "Abgeschlossen",
    left: "verbleibend",
    watchAgain: "Erneut ansehen",
    continue: "Weiter",
    view: "Anzeigen",
  },
  course: {
    index: {
      title: "Kurse",
      subtitle: "Erkunden Sie kuratierte Kurse, um schneller aufzusteigen.",
      cards: {
        idioms: {
          provider: "Lingoflows",
          title: "Häufigste amerikanische Redewendungen",
          description: "Meistern Sie Redewendungen mit alltäglichen Beispielen und Erklärungen.",
          coverAlt: "Cover der häufigsten amerikanischen Redewendungen",
        },
      },
    },
    detail: {
      headerTitle: "Kurse",
      menuButton: "Menü öffnen",
      breadcrumb: {
        courses: "Kurse",
      },
      actions: {
        continue: "Lernen fortsetzen",
      },
      pagination: "Seite {page} / {totalPages} • {total} {unit}",
      paginationPrev: "Zurück",
      paginationNext: "Weiter",
      totalBadge: "{total} {unit}",
      idiomUnit: "Redewendungen",
      errors: {
        load: "Laden der Redewendungen fehlgeschlagen: {message}",
      },
      courses: {
        idioms: {
          title: "Häufigste amerikanische Redewendungen",
          tagline: "Meistern Sie Redewendungen mit mir!",
          provider: "Lingoflows",
          coverAlt: "Cover der häufigsten amerikanischen Redewendungen",
        },
        default: {
          title: "Kurs",
          tagline: "",
          provider: "",
          coverAlt: "Kurscover",
        },
      },
    },
    common: {
      previous: "Zurück",
      next: "Weiter",
    },
    progress: {
      headerLabel: "Fortschritt",
      loading: "Laden",
      status: {
        completed: "Abgeschlossen",
        todo: "Zu erledigen",
      },
      completedTooltip: "Abgeschlossen",
      exampleAria: "Beispiel {index} {status}",
      defaultCourseLabel: "Kurs",
    },
    freePlanLimit: {
      title: "Upgraden Sie, um diese Lektion freizuschalten",
      description:
        "Benutzer des kostenlosen Plans können auf die ersten 10 Redewendungen zugreifen. Upgraden Sie Ihren Plan, um den vollständigen Kurs weiter zu lernen.",
      lockedCardLabel: "Upgraden Sie, um diese Lektion freizuschalten",
      lockedBanner: "Upgraden Sie, um freizuschalten",
      lockedHeading: "Upgraden Sie, um das Lernen fortzusetzen",
      lockedMessage:
        "Benutzer des kostenlosen Plans können auf die ersten 10 Redewendungen zugreifen. Upgraden Sie, um den vollständigen Kurs freizuschalten, einschließlich Lektion #{number}.",
      backToList: "Zurück zur Kursübersicht",
    },
    idiomDetail: {
      breadcrumb: {
        courses: "Kurse",
      },
      sidebar: {
        idiomLabel: "Redewendung",
        difficulty: "Schwierigkeit",
        examples: "Beispiele",
        backToList: "Zurück zur Liste",
      },
      translationToggle: "Übersetzung",
      untitled: "Ohne Titel",
      examples: {
        title: "Beispiele",
        description: "Üben Sie jedes Beispiel und nehmen Sie Ihre Stimme auf, um dieses Kapitel abzuschließen.",
        empty: "Für diese Redewendung sind noch keine Beispiele verfügbar.",
      },
      errors: {
        load: "Redewendungsdetails können nicht geladen werden. Bitte versuchen Sie es später erneut.",
      },
    },
    examples: {
      transcribing: "Transkribieren...",
    },
  },
  savedWords: {
    title: "Wiederholen",
    countSuffix: "gespeicherte Wörter",
    selectedSuffix: "ausgewählt",
    blur: {
      show: "Erklärungen anzeigen",
      hide: "Erklärungen ausblenden",
    },
    play: {
      playing: "Wiedergabe...",
      playSelected: "Ausgewählte abspielen",
      playAll: "Alle abspielen",
    },
    export: {
      label: "Exportieren",
      json: "JSON exportieren",
      csv: "CSV exportieren",
      jsonShort: "JSON",
      csvShort: "CSV",
    },
    selectMode: {
      enter: "Auswählen",
      exit: "Fertig",
      selectAll: "Alle auswählen",
      clear: "Löschen",
    },
    filters: {
      all: "Alle",
    },
    sort: {
      newest: "Neueste zuerst",
      oldest: "Älteste zuerst",
      alphabetical: "A-Z",
    },
    reviewNow: "Jetzt wiederholen",
    due: "fällig",
    onDeck: "im Stapel",
    status: {
      new: "Neu",
      fuzzy: "Unklar",
      mastered: "Gelernt",
    },
    next: "Weiter",
    interval: "Intervall",
    correct: "Richtig",
    wrong: "Falsch",
    reviewed: "Wiederholt",
    explains: "Erklärungen",
    reviewAgain: "Nochmal",
    reviewHard: "Schwer",
    reviewGood: "Gut",
    reviewEasy: "Einfach",
    reviewAllCaughtUp: "Alles erledigt!",
    reviewAllCaughtUpHint: "Derzeit müssen keine Karten wiederholt werden. Kommen Sie später zurück oder fügen Sie neue Wörter zum Lernen hinzu.",
    empty: {
      title: "Keine Wörter gefunden",
      filterHint: "Versuchen Sie, Ihre Suche oder Filter anzupassen.",
      defaultHint: "Beginnen Sie, Wörter zu speichern, während Sie Episoden ansehen!",
    },
    loginRequiredTitle: "Anmeldung erforderlich",
    loginRequiredDesc: "Bitte melden Sie sich an, um Ihre gespeicherten Wörter anzuzeigen.",
    searchPlaceholder: "Wörter suchen...",
    sources: {
      youdao: "Youdao",
      original: "Original",
    },
    relative: {
      now: "jetzt",
      dueSoon: "bald fällig",
      justReviewed: "gerade wiederholt",
      in: "in {value}{unit}",
      ago: "vor {value}{unit}",
      today: "Heute",
      yesterday: "Gestern",
    },
    selectionBar: {
      selectedSuffix: "ausgewählt",
      delete: "Ausgewählte löschen",
    },
  },
  settings: {
    title: "Einstellungen",
    nativeLanguage: "Muttersprache",
    nativeLanguageHint: "Wählen Sie Ihre Muttersprache für Übersetzungen und Benutzeroberfläche",
    theme: {
      title: "Erscheinungsbild",
      hint: "Wählen Sie Ihr bevorzugtes Farbschema",
      light: "Hell",
      lightDesc: "Helles Farbschema",
      dark: "Dunkel",
      darkDesc: "Dunkles Farbschema",
      system: "System",
      systemDesc: "Systemeinstellung folgen",
    },
    cefrLevel: {
      title: "CEFR-Niveau",
      description: "Wählen Sie das CEFR-Niveau, das am besten zu Ihrem Studium passt, um Inhalte zu personalisieren.",
      unset: "Nicht festgelegt",
      saving: "CEFR-Niveau wird gespeichert...",
    },
    interfaceLanguage: "Oberflächensprache",
    interfaceLanguageHint: "Wählen Sie die Sprache für die Anwendungsoberfläche. Dies ist unabhängig von Ihrer Mutterspracheinstellung.",
    videoTitles: {
      title: "Episodentitel-Sprache",
      description: "Chinesische Titel auf der Startseite anzeigen, wenn Übersetzungen verfügbar sind.",
      chineseLabel: "Chinesischer Titel",
      originalLabel: "Originaltitel",
      saving: "Einstellung wird gespeichert...",
    },
    vocabularyDict: "Wortschatzwörterbuch",
    remove: "Entfernen",
    removing: "Wird entfernt...",
    selectVocabularyHint: "Wählen Sie den Wortschatz aus, den Sie basierend auf Ihren Bedürfnissen lernen möchten",
    dailyGoal: {
      title: "Tägliches Shadowing-Ziel",
      desc: "Legen Sie Ihre tägliche Übungszeit fest und sehen Sie schnell Ihren Fortschritt",
      targetMinutes: "Zielminuten",
      minutesLabel: "Minuten",
      progressPrefix: "Heutiger Fortschritt:",
      remainingPrefix: "Verbleibend",
      minAbbr: "Min",
      completedPrefix: "Abgeschlossen",
      todayWord: "heute",
      saving: "Speichern...",
      saved: "Gespeichert",
      loadFailed: "Ziel konnte nicht geladen werden",
      saveFailed: "Speichern fehlgeschlagen",
    },
    examVocab: {
      title: "Prüfungswortschatz",
      subtitle: "Professioneller und standardisierter Prüfungswortschatz",
    },
  },
  user: {
    plan: {
      premium: "Premium",
      free: "Kostenloser Plan",
    },
    badge: {
      premiumMember: "Premium-Mitglied",
      unlimited: "Unbegrenzt",
    },
    usage: {
      transcriptsLabel: "Transkripte",
      remaining: "verbleibend diesen Monat",
      limitReached: "Monatslimit erreicht",
    },
    actions: {
      upgrade: "Auf Premium upgraden",
      settings: "Einstellungen",
      help: "Hilfe & Support",
      logout: "Abmelden",
    },
    dialog: {
      limitReached: "Sie haben Ihr monatliches Transkriptlimit erreicht. Upgraden Sie auf Premium für unbegrenzten Zugang!",
      remaining: "Sie haben diesen Monat noch {remaining} Transkripte. Upgraden Sie für unbegrenzten Zugang!",
    },
  },
  auth: {
    headings: {
      welcome: "Machen Sie heute zählt!",
      start: "Beginnen Sie Ihre Lernreise",
      createAccount: "Konto erstellen",
      signIn: "Anmelden",
    },
    subheadings: {
      login: "Willkommen zurück! Bitte geben Sie Ihre Daten ein",
      signup: "Geben Sie Ihre Daten ein, um zu beginnen",
      leftLogin: "Setzen Sie Ihr Sprachlernabenteuer mit interaktiven Podcasts und KI-gestützten Funktionen fort.",
      leftSignup: "Lernen Sie Englisch effektiv mit immersiven Videos und ansprechenden Podcast-Inhalten.",
    },
    form: {
      email: "E-Mail-Adresse",
      emailPlaceholder: "Geben Sie Ihre E-Mail ein",
      password: "Passwort",
      createPassword: "Passwort erstellen",
      passwordPlaceholderLogin: "Geben Sie Ihr Passwort ein",
      passwordPlaceholderSignup: "Erstellen Sie ein sicheres Passwort",
      forgotPassword: "Passwort vergessen?",
      signIn: "Anmelden",
      createAccount: "Konto erstellen",
      signingIn: "Anmeldung...",
      creatingAccount: "Konto wird erstellt...",
      haveAccount: "Haben Sie bereits ein Konto?",
      dontHaveAccount: "Haben Sie kein Konto?",
      signInInstead: "Stattdessen anmelden",
    },
    reset: {
      title: "Passwort zurücksetzen",
      sentTitle: "Überprüfen Sie Ihre E-Mail",
      sentDesc: "Wir haben einen Link zum Zurücksetzen des Passworts an Ihre E-Mail-Adresse gesendet.",
      enterDesc: "Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.",
      sentTo: "Wir haben einen Link zum Zurücksetzen des Passworts gesendet an:",
      notReceived: "E-Mail nicht erhalten? Überprüfen Sie Ihren Spam-Ordner oder versuchen Sie es erneut.",
      backToSignIn: "Zurück zur Anmeldung",
      sendLink: "Zurücksetzen-Link senden",
      sending: "Zurücksetzen-Link wird gesendet...",
      cancel: "Abbrechen",
    },
    errors: {
      invalidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
      shortPassword: "Das Passwort muss mindestens 6 Zeichen lang sein.",
    }
  },
  search: {
    search: "Suchen",
    trending: "Trending",
    podcast: "Podcast",
    video: "Video",
    allCategories: "Alle Kategorien",
    categoryPrefix: "Kategorie:",
    searchingPodcasts: "Podcasts werden durchsucht...",
    loadingVideos: "Videos werden geladen...",
    errorTitle: "Suchfehler",
    noResults: "Keine Ergebnisse gefunden",
    tryAdjustPodcasts: "Versuchen Sie, Ihre Suchbegriffe anzupassen oder durchsuchen Sie stattdessen Trending-Podcasts",
    tryAdjustVideos: "Versuchen Sie, Ihre Suchbegriffe anzupassen oder durchsuchen Sie stattdessen Trending-Videos",
    trendingVideos: "Trending-Videos",
    loadingMorePodcasts: "Weitere Podcasts werden geladen...",
    endOfResults: "Sie haben das Ende erreicht! Das sind alle Podcasts, die wir gefunden haben.",
    categories: {
      "Arts": "Künste",
      "Business": "Business",
      "Comedy": "Comedy",
      "Education": "Bildung",
      "Fiction": "Fiktion",
      "Government": "Regierung",
      "Health & Fitness": "Gesundheit & Fitness",
      "History": "Geschichte",
      "Kids & Family": "Kinder & Familie",
      "Leisure": "Freizeit",
      "Music": "Musik",
      "News": "Nachrichten",
      "Religion & Spirituality": "Religion & Spiritualität",
      "Science": "Wissenschaft",
      "Society & Culture": "Gesellschaft & Kultur",
      "Sports": "Sport",
      "Technology": "Technologie",
      "True Crime": "Wahre Kriminalität",
      "TV & Film": "TV & Film",
    },
  },
};

// Portuguese (pt) translations
const pt: Dict = {
  hero: {
    goalLabel: "Objetivo",
    minuteShort: "min",
    hour: "hora",
    hours: "horas",
  },
  analytics: {
    learnMore: "Saiba mais",
    chart: {
      practiceTime: "Tempo de Prática",
      minutes: "minutos",
      current: "Atual",
    },
    cards: {
      today: {
        title: "Hoje",
        unit: "min",
        subtitle: "Tempo de prática de hoje",
      },
      sessions: {
        title: "Sessões",
        unit: "práticas",
        subtitle: "Praticar shadowing hoje",
      },
      streak: {
        title: "Sequência",
        unitSingular: "dia",
        unitPlural: "dias",
        subtitle: "Sequência de prática atual",
      },
    },
  },
  upgradeDialog: {
    title: "Atualizar para Premium",
    subtitle: {
      withEpisode: 'Acesse "{episodeTitle}" e desbloqueie todo o conteúdo premium',
      default: "Desbloqueie todo o conteúdo e recursos premium",
    },
    features: {
      episodes: "Acesso a todos os episódios",
      ai: "Recursos de aprendizado com IA",
      support: "Suporte prioritário",
    },
    actions: {
      upgrade: "Atualizar Agora",
      continueFree: "Continuar com Acesso Gratuito",
    },
  },
  shadowing: {
    title: "Prática de Shadowing",
    showTranslation: "Mostrar Tradução",
    showSentenceHint: "Clique no ícone do olho para mostrar a frase",
    replay: "Repetir",
    continue: "Continuar",
    startShadowing: "Iniciar Shadowing",
    stopRecording: "Parar Gravação",
    transcribeFailed: "Falha na transcrição",
  },
  common: {
    loading: "Carregando...",
    save: "Salvar",
    speak: "Falar",
    signIn: "Entrar",
    backToHome: "Voltar ao Início",
    optional: "Opcional",
  },
  installPrompt: {
    title: "Instalar Longoflows no seu dispositivo",
    instructions: {
      tap: "Toque",
      thenAdd: 'depois "Adicionar à Tela Inicial"',
    },
    actions: {
      close: "Fechar aviso de instalação",
    },
  },
  navigation: {
    tagline: "Sua plataforma de aprendizado de idiomas",
    actions: {
      collapse: "Recolher barra lateral",
      expand: "Expandir barra lateral",
    },
    links: {
      home: "Início",
      search: "Buscar",
      episodes: "Episódios",
      course: "Curso",
      library: "Biblioteca",
      review: "Revisar",
      history: "Histórico",
      settings: "Configurações",
    },
  },
  tabs: {
    summary: { name: "Resumo", desc: "Resumo do episódio com IA", mobile: "Resumo" },
    mindmap: { name: "Mapa Mental", desc: "Mapeamento visual de conceitos", mobile: "Mapa" },
    transcript: { name: "Transcrição", desc: "Transcrição completa do episódio", mobile: "Script" },
    vocabulary: { name: "Vocabulário", desc: "Termos e frases-chave", mobile: "Vocab" },
    phrase: { name: "Dicionário", desc: "Vocabulário em visualização de dicionário", mobile: "Dict" },
    quiz: { name: "Quiz", desc: "Prática interativa", mobile: "Quiz" },
  },
  player: {
    freePreview: {
      toastTitle: "Limite de visualização gratuita atingido",
      toastDescription: "Atualize para continuar ouvindo além dos primeiros 2 minutos.",
      toastUpgradeCta: "Atualizar",
    },
  },
  phraseSection: {
    title: "Vocabulário no Dicionário",
    subtitle: "Veja cada palavra do dicionário exatamente onde aparece na transcrição.",
    truncateNotice: "Mostrando as primeiras {count} correspondências",
    actions: {
      openSettings: "Abrir configurações",
    },
    badges: {
      dictionaryMode: "Modo dicionário",
    },
    countLabel: {
      singular: "palavras",
      plural: "palavras",
    },
    indicators: {
      cardsLabel: "Vocabulário",
      dictionaryLabel: "Dicionário",
      dictionaryMissing: "Dicionário não selecionado",
    },
    jump: "Ir para o clipe",
    empty: {
      noVocabulary: {
        title: "Vocabulário necessário",
        description: "Gere ou importe vocabulário para explorar frases contextuais.",
      },
      noMatches: {
        title: "Ainda sem frases contextuais",
        description: "Os termos do vocabulário ainda não apareceram na transcrição.",
      },
      noDictionary: {
        title: "Escolha um dicionário para desbloquear destaques",
        description: "Selecione um dicionário de vocabulário nas configurações para que possamos identificar palavras que você aprendeu.",
      },
    },
  },
  quizSection: {
    title: "Quiz Interativo",
    description: "Pratique termos-chave extraídos deste episódio.",
    empty: {
      title: "Ainda não há quiz disponível",
      description: "As perguntas do quiz aparecerão aqui assim que o vocabulário tiver itens de prática vinculados.",
    },
    stats: {
      questionSingular: "pergunta",
      questionPlural: "perguntas",
      countSingular: "Pergunta",
      countPlural: "Perguntas",
    },
    overview: {
      title: "Visão Geral do Quiz",
      nextTerm: "Próximo termo",
      progress: "Progresso",
      accuracy: "Precisão",
      allCompleted: "Todos os itens do quiz concluídos",
    },
    toast: {
      noSentence: "Frase do quiz não disponível para geração de áudio.",
      audioGenerationFailed: "Falha na geração de áudio",
      audioGenerated: "Áudio do quiz gerado",
    },
    messages: {
      completed: "Ótimo trabalho! Este termo está totalmente praticado.",
    },
    status: {
      completed: "Concluído",
      todo: "A fazer",
    },
    translation: {
      show: "Mostrar tradução",
      hide: "Ocultar tradução",
    },
    shadowing: {
      defaultTitle: "Prática de Quiz do Episódio",
      cta: "Shadowing",
      dialogTitle: "Prática de Shadowing",
      instructions: "Ouça e repita a frase",
      showTranslation: "Mostrar tradução",
      hideTranslation: "Ocultar tradução",
      audioUnavailable: "Áudio de referência ainda não disponível. Gere-o no cartão do quiz para ouvir.",
      stopRecording: "Parar Gravação",
      startShadowing: "Iniciar Shadowing",
      yourRecording: "Sua gravação",
      attemptsLabel: "Tentativas",
    },
  },
  persona: {
    progress: {
      label: "Passo {current} de {total}",
    },
    actions: {
      skip: "Pular por enquanto",
      back: "Voltar",
      continue: "Continuar",
      finish: "Finalizar",
    },
    steps: {
      languages: {
        eyebrow: "Passo 1 · Idiomas",
        title: "Quais são seus idiomas nativo e objetivo?",
        description: "Conte-nos seus idiomas preferidos para personalizar traduções.",
      },
      level: {
        eyebrow: "Passo 2 · Nível Objetivo",
        title: "Como você classificaria seu nível no idioma objetivo?",
        description: "Apenas uma autoavaliação aproximada, nenhum teste necessário.",
      },
      goals: {
        eyebrow: "Passo 3 · Objetivos",
        title: "Qual é seu objetivo principal para aprender seu idioma objetivo?",
        description: "Escolha os objetivos que melhor correspondem à sua jornada. Você pode ajustá-los a qualquer momento.",
      },
      topics: {
        eyebrow: "Passo 4 · Tópicos / Interesses",
        title: "Que tipo de vídeos você mais gosta?",
        description: "Recomendaremos vídeos em inglês com base nesses tópicos.",
      },
      formats: {
        eyebrow: "Passo 5 · Formatos de Vídeo",
        title: "Que tipo de formato de vídeo você gosta?",
        description: "Escolha os estilos que você gosta. Vamos misturá-los em suas recomendações.",
      },
      session: {
        eyebrow: "Passo 6 · Duração da Sessão",
        title: "Quanto tempo você geralmente tem para uma sessão?",
        description: "Vamos ajustar a duração do vídeo ao seu cronograma.",
      },
      subtitles: {
        eyebrow: "Passo 7 · Legendas e Explicação",
        title: "Como devemos apoiar seu aprendizado?",
        description: "Escolha as legendas e explicações que você prefere.",
      },
    },
    goals: {
      everyday: "Conversa cotidiana",
      watchYoutube: "Assistir YouTube sem legendas",
      jobInterview: "Entrevistas de emprego e carreira",
      studyExam: "Estudos / exames (IELTS, TOEFL…)",
      travelAbroad: "Viagem e vida no exterior",
    },
    topics: {
      dailyVlog: "Vlog diário e estilo de vida",
      aiTech: "Tecnologia e IA",
      career: "Carreira e autodesenvolvimento",
      comedy: "Comédia e entretenimento",
      moviesTv: "Filmes e programas de TV",
      news: "Notícias e eventos atuais",
      gaming: "Jogos",
      education: "Educação e tutoriais",
    },
    levels: {
      rangeHint: "Vamos focar em vídeos na faixa {from}–{to}.",
      a2: {
        title: "Iniciante – Conheço algumas palavras, mas não consigo acompanhar a maioria dos vídeos.",
        description: "Nível CEFR básico",
      },
      b1: {
        title: "Pré-intermediário – Preciso de legendas para acompanhar a maioria do conteúdo.",
        description: "Pré-intermediário",
      },
      b2: {
        title: "Intermediário – Com legendas entendo 70–80%.",
        description: "Intermediário superior",
      },
      c1: {
        title: "Avançado – Posso acompanhar a maioria dos vídeos sem legendas.",
        description: "Avançado",
      },
    },
    formats: {
      vlog: "Vlogs / vida cotidiana",
      interview: "Entrevistas e podcasts",
      podcast: "Podcasts / histórias de áudio",
      tutorial: "Explicações / tutoriais",
      shorts: "Clipes curtos / destaques",
      movieClip: "Cenas de filmes / programas de TV",
    },
    sessionLength: {
      label: "Duração da sessão",
      description: "Meta de {minutes} min",
      ten: "5–10 minutos",
      twenty: "10–20 minutos",
      thirty: "20–30 minutos",
      fortyFive: "30+ minutos",
    },
    sessionFrequency: {
      prompt: "Com que frequência você quer praticar?",
      three: "Algumas vezes por semana",
      five: "Quase todos os dias",
      seven: "Todos os dias",
    },
    subtitles: {
      alwaysTitle: "Sempre mostrar legendas em inglês",
      alwaysDescription: "Mantenha as legendas ativadas por padrão para cada vídeo.",
      nativeTitle: "Mostrar tradução no meu idioma quando precisar de ajuda",
      nativeDescription: "Ative o suporte no idioma nativo sempre que ficar preso.",
      nativeLanguageLabel: "Idioma nativo preferido",
      nativeLanguagePlaceholder: "Selecionar idioma",
      nativeLanguageHint: "Usaremos isso ao mostrar traduções ou explicações.",
      nativeLanguage: {
        auto: "Detecção automática",
        zh: "Chinês",
        es: "Espanhol",
        ja: "Japonês",
        ko: "Coreano",
      },
    },
    languages: {
      nativeLabel: "Qual é seu idioma nativo?",
      targetLabel: "Qual idioma você está aprendendo?",
      targetPlaceholder: "Selecionar idioma objetivo",
      hint: "Usaremos isso para corresponder lições e orientação de nível.",
      native: {
        en: "Inglês",
        zh: "Chinês",
        es: "Espanhol",
        ja: "Japonês",
        ko: "Coreano",
        fr: "Francês",
        de: "Alemão",
        pt: "Português",
        ru: "Russo",
      },
      target: {
        en: "Inglês",
        zh: "Chinês",
        es: "Espanhol",
        ja: "Japonês",
        ko: "Coreano",
        fr: "Francês",
        de: "Alemão",
        pt: "Português",
        ru: "Russo",
      },
    },
    explanations: {
      title: "Explicações que você quer ver",
      keyVocab: "Mostrar vocabulário-chave para cada vídeo",
      showPhrases: "Destacar frases e expressões úteis",
      translateSentences: "Traduzir frases difíceis para seu idioma nativo",
      pronunciation: "Focar em pronúncia e shadowing",
    },
  },
  vocabQuiz: {
    title: "Quiz de Vocabulário",
    translationUnavailable: "Tradução não disponível para este segmento",
    loading: "Carregando...",
    translation: {
      show: "Mostrar tradução",
      hide: "Ocultar tradução",
    },
    actions: {
      replay: "Repetir",
      continue: "Continuar",
    },
  },
  mobileImmersive: {
    subtitles: {
      show: "Mostrar legendas",
      hide: "Ocultar legendas",
    },
    translation: {
      show: "Mostrar tradução",
      hide: "Ocultar tradução",
    },
    narration: {
      enable: "Ativar Modo Narração",
      disable: "Desativar Modo Narração",
      tooltipEnable: "Reproduzir automaticamente explicações de vocabulário após a reprodução",
      tooltipDisable: "Desativar narração de vocabulário",
      generating: "Gerando áudio de narração...",
      failed: "Falha na geração de narração",
      modeActive: "Modo Narração Ativo",
      playing: "Reproduzindo narração...",
      ready: "Narração pronta",
      skip: "Pular",
      skipTooltip: "Pular narração e continuar vídeo",
      currentTerm: "Termo atual",
      pause: "Pausar",
      resume: "Retomar",
      repeat: "Repetir",
    },
    status: {
      new: "Novo",
      newDescription: "Recém-adicionado, precisa de primeira revisão",
      fuzzy: "Confuso",
      fuzzyDescription: "Precisa de mais prática em breve",
      mastered: "Dominado",
      masteredDescription: "Confiante e em revisão longa",
    },
    shadowing: {
      tooltip: "Prática de shadowing",
      dialogTitle: "Prática de Shadowing",
      dialogDescription: "Repita a legenda atual em voz alta.",
      attemptLabel: "Tentativa",
      hideText: "Ocultar texto",
      showText: "Mostrar texto",
      hideTranslationShort: "Ocultar tradução",
      showTranslationShort: "Mostrar tradução",
      transcriptionLabel: "Sua transcrição",
      startRecording: "Iniciar shadowing",
      stopRecording: "Parar gravação",
      start: "Iniciar shadowing",
      stop: "Parar shadowing",
      unknownEpisode: "Episódio Desconhecido",
    },
  },
  vocabulary: {
    status: {
      label: "Classificação de vocabulário",
      new: "Novo",
      newDescription: "Recém-adicionado, precisa de primeira revisão",
      fuzzy: "Confuso",
      fuzzyDescription: "Precisa de mais prática em breve",
      mastered: "Dominado",
      masteredDescription: "Confiante e em revisão longa",
    },
    section: {
      title: "Palavras-chave e Tópicos",
      topicCountSingular: "{count} tópico",
      topicCountPlural: "{count} tópicos",
      revealAllButton: "Revelar tudo",
      revealAllTitle: "Revelar todas as explicações",
      restoreBlurButton: "Restaurar desfoque",
      restoreBlurTitle: "Restaurar desfoque",
      levelFilterLabel: "Filtrar por nível CEFR",
      levelFilterUserLevel: "Seu nível: {level}",
      levelFilterAll: "Todos",
      noTermsMatchFilter: "Ainda não há termos que correspondam aos níveis CEFR selecionados. Tente selecionar níveis adicionais.",
    },
    swipeTip: {
      badge: "Dica",
      heading: "Deslize para gerenciar esta pilha",
      description: "Deslize para a esquerda para ocultar palavras que você conhece, ou para a direita para mantê-las em seu fluxo de prática.",
    },
    desktopActions: {
      markKnown: "Marcar como conhecido",
      keepLearning: "Continuar praticando",
    },
    card: {
      examplesLabel: "Exemplos",
    },
    stack: {
      viewDetails: "Ver detalhes",
      closeDetails: "Fechar",
    },
  },
  transcript: {
    title: "Transcrição",
    generate: "Gerar Transcrição",
    generating: "Gerando Transcrição...",
    generateDesc: "Gere uma transcrição para ver o texto completo com timestamps e recursos interativos.",
    generatingDesc: "Aguarde enquanto geramos a transcrição para este episódio.",
    segments: "segmentos",
    usageDeducted: "Crédito de transcrição usado",
    usageRemaining: "{remaining}/{limit} restantes este mês",
    usageRemainingUnlimited: "Transcrições ilimitadas com seu plano",
    dynamicIslandSettings: "Configurações do Dynamic Island",
    dynamicIslandTitle: "Dynamic Island",
    dynamicIslandDescription: "Controle o que aparece no Dynamic Island durante o aprendizado.",
    dynamicIslandVocabulary: "Popup de vocabulário",
    dynamicIslandVocabularyHint: "Mostrar prompts de vocabulário dentro da sobreposição popup",
    toggleDynamicIslandVocabulary: "Alternar popup de vocabulário",
    wordMask: "Animação de legenda",
    toggleWordMask: "Alternar animação de legenda",
  },
  premiumPrompt: {
    guest: {
      title: "Login necessário",
      description: "Por favor, faça login para acessar este conteúdo e desbloquear recursos de aprendizado.",
    },
    free: {
      title: "Conteúdo Premium",
      description: "Este episódio requer uma assinatura premium para acessar todos os recursos.",
      secondary: "Ou confira nossos episódios em destaque para acesso gratuito.",
    },
    featured: {
      title: "Conteúdo em Destaque Gratuito",
      description: "Gostando deste episódio gratuito? Atualize para Premium para desbloquear nossa biblioteca completa de conteúdo!",
      cta: "Saiba mais sobre Premium",
    },
    fallback: "Acesso restrito",
  },
  contentRenderer: {
    alerts: {
      vocabFallback: "Falha na geração de vocabulário: {error}. Usando dados de demonstração em vez disso.",
    },
  },
  upgradePromptDialog: {
    titles: {
      limitReached: "Limite Mensal Atingido",
      upgrade: "Atualizar para Premium",
      error: "Falha na Geração",
    },
    usage: {
      label: "Uso Mensal",
      summary: "{used}/{limit} transcrições usadas este mês",
      remaining: "{remaining}/{limit} restantes",
      unlimited: "Transcrições ilimitadas com seu plano premium",
    },
    benefits: {
      title: "Atualizar para Premium",
      items: {
        unlimitedTranscripts: "Geração ilimitada de transcrições",
        aiSummaries: "Assistir todos os vídeos transcritos Ready",
        vocabulary: "Análise completa de vocabulário",
        priority: "Processamento prioritário",
        export: "Exportar para todos os formatos",
      },
    },
    plans: {
      basic: {
        name: "Plano Básico",
        short: "Básico",
        badge: "Popular",
        price: "$12.99",
        period: "/mês",
        description: "Perfeito para aprendizes individuais",
      },
      premium: {
        name: "Plano Premium",
        short: "Premium",
        badge: "Melhor Valor",
        price: "$19.99",
        period: "/mês",
        description: "Recursos avançados e suporte prioritário",
      },
    },
    actions: {
      upgradeToPlan: "Atualizar para {plan}",
      continueWithPlan: "Continuar com {plan}",
      later: "Mais tarde",
      close: "Fechar",
      tryAgain: "Tentar Novamente",
    },
    status: {
      processing: "Processando...",
    },
    alerts: {
      checkoutFailed: "Falha ao iniciar o checkout. Por favor, tente novamente.",
    },
    error: {
      generic: "Não é possível gerar transcrição",
      loginRequired: "Por favor, faça login para gerar transcrições. Seu progresso será salvo.",
    },
    premiumStatus: {
      title: "Você tem um Plano Premium",
      description: "Aproveite geração ilimitada de transcrições e todos os recursos premium.",
    },
  },
  episode: {
    floatingActions: {
      buttons: {
        open: "Abrir ações rápidas",
        collapse: "Recolher ações rápidas",
        showTranslation: "Mostrar tradução",
        hideTranslation: "Ocultar tradução",
        generateTranslation: "Gerar tradução",
        generatingTranslation: "Gerando tradução...",
        generateChosenExplanation: "Gerar explicações com IA",
        generatingChosenExplanation: "Gerando explicações",
        enableWordHighlight: "Ativar destaque de palavras",
        disableWordHighlight: "Desativar destaque de palavras",
        adjustPracticeMode: "Ajustar modo de prática",
        enablePracticeMode: "Ativar modo de prática",
        resetProgress: "Redefinir progresso do episódio",
        backToTop: "Voltar ao topo",
      },
      tooltips: {
        open: "Abrir Ações Rápidas",
        collapse: "Recolher Ações Rápidas",
        showTranslation: "Mostrar Tradução",
        hideTranslation: "Ocultar Tradução",
        generateTranslation: "Gerar Tradução",
        generatingTranslation: "Gerando...",
        generateChosenExplanation: "Gerar explicação baseada em exemplos",
        generatingChosenExplanation: "Gerando explicações...",
        dictionaryRequiredTitle: "Dicionário necessário",
        dictionaryRequiredDescription: "Configure um dicionário nas Configurações para desbloquear destaques.",
        enableWordHighlight: "Ativar Destaque de Palavras",
        disableWordHighlight: "Desativar Destaque de Palavras",
        practiceSetupTitle: "Configuração necessária",
        practiceSetupDescription: "Gere vocabulário ou escolha um dicionário para iniciar o modo de prática.",
        practiceActiveTitle: "Modo de prática ativo",
        practiceOpenTitle: "Abrir modos de prática",
        practiceActiveDescription: "Atualmente usando modo {mode}.",
        practiceOpenDescription: "Escolha um modo de aprendizado adaptado ao seu objetivo.",
        resetProgressTitle: "Redefinir progresso do episódio",
        resetProgressDescription: "Limpar palavras dominadas apenas para este episódio.",
        backToTop: "Voltar ao topo",
      },
      success: {
        translateGenerated: "Tradução gerada com sucesso!",
        chosenExplanationGenerated: "Explicações geradas com sucesso!",
      },
      alerts: {
        translateMissingData: "Não é possível gerar tradução: dados necessários ausentes.",
      },
      errors: {
        translateRequestFailed: "Falha na solicitação de tradução",
        translateResponseInvalid: "Erro de formato de resposta de tradução",
        translateConsoleError: "Falha na geração de tradução",
        translateFailed: "Falha na geração de tradução, por favor tente novamente.",
        generateVocabularyFirst: "Por favor, gere vocabulário primeiro",
        dictionaryInitConsole: "Falha na inicialização do dicionário",
        dictionaryLoadFailed: "Não é possível carregar o dicionário, por favor tente novamente",
        dictionaryNotSelected: "Por favor, selecione um dicionário de vocabulário nas Configurações primeiro",
        dictionaryInitFailed: "Falha na inicialização do gerenciador de vocabulário",
        dictionaryInitUnable: "Não é possível carregar o dicionário de vocabulário, por favor tente novamente",
        chosenExplanationFailed: "Falha na geração de explicações",
        chosenExplanationMissing: "Não é possível gerar explicações: vocabulário não está pronto.",
        chosenExplanationUnauthorized: "Por favor, faça login para gerar explicações.",
      },
      practice: {
        noDictionary: "Sem Dicionário",
        spellingSuffix: "Ortografia",
      },
      practiceModes: {
        vocabulary: "Vocabulário",
        toefl: "TOEFL",
        shadowing: "Shadowing",
      },
      dialog: {
        title: "Redefinir Progresso do Episódio",
        description: "Isso limpará todas as palavras dominadas apenas para este episódio e reiniciará sua prática de vocabulário. O progresso de outros episódios não será afetado. Esta ação não pode ser desfeita.",
        cancel: "Cancelar",
        confirm: "Redefinir Progresso",
      },
    },
    exportDialog: {
      lockedBadge: "Recurso premium",
      lockedDescription: "A exportação está disponível em planos premium. Atualize para baixar, copiar ou compartilhar conteúdo completo do episódio.",
      title: "Exportar Episódio",
      description: 'Exportar "{title}" em seu formato preferido',
      quickActionsTitle: "Ações Rápidas",
      downloadTitle: "Baixar Arquivos",
      quickActions: {
        copyMarkdown: "Copiar Markdown",
        copyText: "Copiar Texto",
        copyUrl: "Copiar URL",
        email: "E-mail",
      },
      options: {
        markdown: {
          title: "Arquivo Markdown",
          description: "Baixar como Markdown formatado (.md) — Perfeito para documentação e anotações",
        },
        text: {
          title: "Arquivo de Texto Simples",
          description: "Baixar como texto simples (.txt) — Compatibilidade universal",
        },
        json: {
          title: "Dados JSON",
          description: "Baixar como dados estruturados (.json) — Para desenvolvedores e automação",
        },
      },
      episodeInfo: {
        episodeLabel: "Episódio",
        authorLabel: "Autor",
        unknownAuthor: "Desconhecido",
        includesPrefix: "Inclui",
        includesSummary: "Resumo",
        includesChapters: "Capítulos",
        includesKeyPoints: "Pontos-chave",
        includesTranscript: "Transcrição Completa",
      },
    },
    immersiveFloating: {
      subtitles: {
        show: "Mostrar legendas",
        hide: "Ocultar legendas",
      },
      translation: {
        show: "Mostrar traduções de cartões",
        hide: "Ocultar traduções de cartões",
      },
      translationReveal: {
        show: "Revelar todas as traduções de cartões",
        hide: "Ocultar todas as traduções de cartões",
      },
      exit: "Sair do modo imersivo",
    },
    videoPlayer: {
      immersive: {
        enterFocus: "Modo foco",
        exitFocus: "Sair do foco",
        enterImmersive: "Foco imersivo",
        exitImmersive: "Sair do imersivo",
        enterFocusTitle: "Entrar no modo foco",
        exitFocusTitle: "Sair do modo foco",
        enterImmersiveTitle: "Entrar no modo imersivo",
        exitImmersiveTitle: "Sair do modo imersivo (Esc)",
      },
      controls: {
        minimize: "Minimizar player",
        maximize: "Restaurar player",
      },
    },
    player: {
      previewLock: {
        badge: "Limite de visualização",
        message: "A visualização do plano gratuito está limitada aos primeiros 2 minutos. Atualize para continuar ouvindo.",
        messageShort: "Limite de visualização — atualize para continuar",
        tooltip: "Visualização do plano gratuito limitada aos primeiros 2 minutos",
      },
      freePreview: {
        toastTitle: "Limite de visualização gratuita atingido",
        toastDescription: "Atualize para continuar ouvindo além dos primeiros 2 minutos.",
        toastUpgradeCta: "Atualizar",
      },
    },
  },
  summary: {
    title: "Resumo",
    noSummary: "Nenhum Resumo Disponível",
    generate: "Gerar Resumo",
    generating: "Gerando Resumo...",
    generateDesc: "Gere um resumo com IA para obter uma visão geral completa deste episódio.",
    generatingDesc: "Aguarde enquanto criamos um resumo com IA deste episódio.",
    aiOverview: "Visão geral gerada por IA",
    zhTranslation: "Tradução Chinesa",
    zhTranslationExtra: "Tradução Chinesa (extra)",
    translationLoading: "Carregando tradução...",
  },
  home: {
    featured: {
      title: "Em Destaque",
      error: "Falha ao carregar episódios em destaque",
      refresh: "Atualizar página para tentar novamente",
    },
    following: {
      title: "Seguindo",
    },
    episodes: {
      title: "Episódios",
      viewAll: "Ver Tudo",
      empty: "Nenhum episódio disponível no momento.",
    },
    channels: {
      title: "Canais Populares",
      viewAll: "Ver Tudo",
    },
    collections: {
      title: "Coleções",
      viewAll: "Ver Tudo",
      countSuffix: "episódios",
      viewAllInline: "Ver tudo",
      categories: {
        "1": "Fronteiras de Tecnologia e IA",
        "2": "Crescimento Pessoal e Mentalidade",
        "3": "Saúde e Bem-estar",
        "4": "Negócios e Finanças",
        "5": "Questões Globais e Política",
        "6": "Relacionamentos e Conexão Humana",
        "7": "Ciência e Descoberta",
        "8": "Estilo de Vida e Vlogs",
        "9": "História e Cultura",
        "10": "Meio Ambiente e Sustentabilidade",
      },
    },
    youtube: {
      title: "YouTube em Destaque",
      missingThumbnail: "Miniatura ausente",
      playVideo: "Reproduzir {title}",
      defaultCreator: "Criador do YouTube",
    },
    free: "GRÁTIS",
  },
  channel: {
    filters: {
      ariaLabel: "Filtrar episódios por temporada",
      allSeasons: "Todas as Temporadas",
      season: "Temporada {season}",
    },
    badges: {
      episodes: "Episódios",
    },
    empty: {
      title: "Nenhum Episódio Encontrado",
      seasonFiltered: "Nenhum episódio encontrado para a temporada selecionada.",
      generic: "Este canal ainda não tem episódios.",
    },
  },
  episodes: {
    title: "Episódios em Tendência",
    loadingMore: "Carregando mais episódios...",
    loadMore: "Carregar Mais",
    noMore: "Não há mais episódios para carregar",
    noneTitle: "Nenhum Episódio Encontrado",
    noneDesc: "Nenhum episódio em tendência disponível no momento.",
    errorTitle: "Erro ao Carregar Episódios",
    tryAgain: "Tentar Novamente",
  },
  library: {
    title: "Minha Biblioteca",
    tabs: {
      favorites: "Favoritos",
      playlists: "Listas de Reprodução",
      podcasts: "Podcasts",
    },
    emptyFavoritesTitle: "Ainda sem favoritos",
    emptyFavoritesDesc: "Episódios que você marcar aparecerão aqui",
    browseEpisodes: "Navegar Episódios",
    playlistEmpty: "Esta lista de reprodução está vazia",
    playlistsEmptyTitle: "Ainda sem listas de reprodução",
    playlistsEmptyDesc: "Crie listas de reprodução para organizar seus episódios",
    podcastsEmptyTitle: "Ainda sem assinaturas de podcasts",
    podcastsEmptyDesc: "Siga podcasts para vê-los aqui",
    discoverPodcasts: "Descobrir Podcasts",
    playlistDialog: {
      title: "Adicionar à Lista de Reprodução",
      description: "Selecione uma lista de reprodução para adicionar este episódio, ou crie uma nova lista de reprodução.",
      addingLabel: "Adicionando:",
      nameLabel: "Nome da Lista *",
      namePlaceholder: "Digite o nome da lista...",
      descriptionLabel: "Descrição (opcional)",
      descriptionPlaceholder: "Adicione uma descrição...",
      cancel: "Cancelar",
      creating: "Criando...",
      createAction: "Criar Lista de Reprodução",
      yourPlaylists: "Suas Listas de Reprodução",
      createNew: "Criar Nova Lista de Reprodução",
      createFirst: "Crie Sua Primeira Lista de Reprodução",
      episodesLabel: "episódios",
    },
  },
  history: {
    title: "Assistidos Recentemente",
    subtitle: "Acompanhe seu progresso e continue de onde parou",
    loading: "Carregando...",
    loginRequiredTitle: "Login Necessário",
    loginRequiredDesc: "Por favor, faça login para ver seu histórico de visualização.",
    noHistoryTitle: "Nenhum Histórico Encontrado",
    noHistoryAll: "Você ainda não assistiu nenhum episódio.",
    noHistoryFiltered: (filter: string) => `Nenhum episódio ${filter} encontrado.`,
    completed: "Concluído",
    left: "restante",
    watchAgain: "Assistir Novamente",
    continue: "Continuar",
    view: "Ver",
  },
  course: {
    index: {
      title: "Cursos",
      subtitle: "Explore cursos curados para subir de nível mais rápido.",
      cards: {
        idioms: {
          provider: "Lingoflows",
          title: "Expressões Americanas Mais Comuns",
          description: "Domine expressões com exemplos e explicações do dia a dia.",
          coverAlt: "Capa das Expressões Americanas Mais Comuns",
        },
      },
    },
    detail: {
      headerTitle: "Cursos",
      menuButton: "Abrir menu",
      breadcrumb: {
        courses: "Cursos",
      },
      actions: {
        continue: "Continuar Aprendendo",
      },
      pagination: "Página {page} / {totalPages} • {total} {unit}",
      paginationPrev: "Anterior",
      paginationNext: "Próximo",
      totalBadge: "{total} {unit}",
      idiomUnit: "expressões",
      errors: {
        load: "Falha ao carregar expressões: {message}",
      },
      courses: {
        idioms: {
          title: "Expressões Americanas Mais Comuns",
          tagline: "Domine expressões comigo!",
          provider: "Lingoflows",
          coverAlt: "Capa das Expressões Americanas Mais Comuns",
        },
        default: {
          title: "Curso",
          tagline: "",
          provider: "",
          coverAlt: "Capa do curso",
        },
      },
    },
    common: {
      previous: "Anterior",
      next: "Próximo",
    },
    progress: {
      headerLabel: "Progresso",
      loading: "Carregando",
      status: {
        completed: "Concluído",
        todo: "A fazer",
      },
      completedTooltip: "Concluído",
      exampleAria: "Exemplo {index} {status}",
      defaultCourseLabel: "Curso",
    },
    freePlanLimit: {
      title: "Atualize para desbloquear esta lição",
      description:
        "Usuários do plano gratuito podem acessar as primeiras 10 expressões. Atualize seu plano para continuar aprendendo o curso completo.",
      lockedCardLabel: "Atualize para desbloquear esta lição",
      lockedBanner: "Atualize para desbloquear",
      lockedHeading: "Atualize para continuar aprendendo",
      lockedMessage:
        "Usuários do plano gratuito podem acessar as primeiras 10 expressões. Atualize para desbloquear o curso completo, incluindo a lição #{number}.",
      backToList: "Voltar à visão geral do curso",
    },
    idiomDetail: {
      breadcrumb: {
        courses: "Cursos",
      },
      sidebar: {
        idiomLabel: "Expressão",
        difficulty: "Dificuldade",
        examples: "Exemplos",
        backToList: "Voltar à lista",
      },
      translationToggle: "Tradução",
      untitled: "Sem título",
      examples: {
        title: "Exemplos",
        description: "Pratique cada exemplo e grave sua voz para terminar este capítulo.",
        empty: "Ainda não há exemplos disponíveis para esta expressão.",
      },
      errors: {
        load: "Não é possível carregar detalhes da expressão. Por favor, tente novamente mais tarde.",
      },
    },
    examples: {
      transcribing: "Transcrevendo...",
    },
  },
  savedWords: {
    title: "Revisar",
    countSuffix: "palavras salvas",
    selectedSuffix: "selecionadas",
    blur: {
      show: "Mostrar Explicações",
      hide: "Ocultar Explicações",
    },
    play: {
      playing: "Reproduzindo...",
      playSelected: "Reproduzir Selecionadas",
      playAll: "Reproduzir Tudo",
    },
    export: {
      label: "Exportar",
      json: "Exportar JSON",
      csv: "Exportar CSV",
      jsonShort: "JSON",
      csvShort: "CSV",
    },
    selectMode: {
      enter: "Selecionar",
      exit: "Concluído",
      selectAll: "Selecionar Tudo",
      clear: "Limpar",
    },
    filters: {
      all: "Todos",
    },
    sort: {
      newest: "Mais Recentes Primeiro",
      oldest: "Mais Antigos Primeiro",
      alphabetical: "A-Z",
    },
    reviewNow: "Revisar Agora",
    due: "devido",
    onDeck: "na pilha",
    status: {
      new: "Novo",
      fuzzy: "Confuso",
      mastered: "Dominado",
    },
    next: "Próximo",
    interval: "Intervalo",
    correct: "Correto",
    wrong: "Errado",
    reviewed: "Revisado",
    explains: "Explicações",
    reviewAgain: "Novamente",
    reviewHard: "Difícil",
    reviewGood: "Bom",
    reviewEasy: "Fácil",
    reviewAllCaughtUp: "Tudo em dia!",
    reviewAllCaughtUpHint: "Nenhuma carta precisa de revisão no momento. Volte mais tarde ou adicione novas palavras para estudar.",
    empty: {
      title: "Nenhuma palavra encontrada",
      filterHint: "Tente ajustar sua busca ou filtros.",
      defaultHint: "Comece a salvar palavras enquanto assiste episódios!",
    },
    loginRequiredTitle: "Login Necessário",
    loginRequiredDesc: "Por favor, faça login para ver suas palavras salvas.",
    searchPlaceholder: "Buscar palavras...",
    sources: {
      youdao: "Youdao",
      original: "Original",
    },
    relative: {
      now: "agora",
      dueSoon: "devido em breve",
      justReviewed: "recém revisado",
      in: "em {value}{unit}",
      ago: "há {value}{unit}",
      today: "Hoje",
      yesterday: "Ontem",
    },
    selectionBar: {
      selectedSuffix: "selecionadas",
      delete: "Excluir Selecionadas",
    },
  },
  settings: {
    title: "Configurações",
    nativeLanguage: "Idioma nativo",
    nativeLanguageHint: "Escolha seu idioma nativo para traduções e interface",
    theme: {
      title: "Aparência",
      hint: "Escolha seu tema de cores preferido",
      light: "Claro",
      lightDesc: "Tema de cores claro",
      dark: "Escuro",
      darkDesc: "Tema de cores escuro",
      system: "Sistema",
      systemDesc: "Seguir preferência do sistema",
    },
    cefrLevel: {
      title: "Nível CEFR",
      description: "Escolha o nível CEFR que melhor corresponde aos seus estudos para personalizar o conteúdo.",
      unset: "Não definido",
      saving: "Salvando nível CEFR...",
    },
    interfaceLanguage: "Idioma da Interface",
    interfaceLanguageHint: "Escolha o idioma da interface do aplicativo. Isso é independente da sua configuração de idioma nativo.",
    videoTitles: {
      title: "Idioma do título do episódio",
      description: "Mostrar títulos em chinês na página inicial quando as traduções estiverem disponíveis.",
      chineseLabel: "Título em chinês",
      originalLabel: "Título original",
      saving: "Salvando preferência...",
    },
    vocabularyDict: "Dicionário de Vocabulário",
    remove: "Remover",
    removing: "Removendo...",
    selectVocabularyHint: "Selecione o vocabulário que deseja aprender com base em suas necessidades",
    dailyGoal: {
      title: "Objetivo Diário de Shadowing",
      desc: "Defina seu tempo de prática diário e visualize rapidamente seu progresso",
      targetMinutes: "Minutos objetivo",
      minutesLabel: "Minutos",
      progressPrefix: "Progresso de hoje:",
      remainingPrefix: "Restante",
      minAbbr: "min",
      completedPrefix: "Concluído",
      todayWord: "hoje",
      saving: "Salvando...",
      saved: "Salvo",
      loadFailed: "Falha ao carregar objetivo",
      saveFailed: "Falha ao salvar",
    },
    examVocab: {
      title: "Vocabulário de Exame",
      subtitle: "Vocabulário de exames profissionais e padronizados",
    },
  },
  user: {
    plan: {
      premium: "Premium",
      free: "Plano Gratuito",
    },
    badge: {
      premiumMember: "Membro Premium",
      unlimited: "Ilimitado",
    },
    usage: {
      transcriptsLabel: "transcrições",
      remaining: "restantes este mês",
      limitReached: "Limite mensal atingido",
    },
    actions: {
      upgrade: "Atualizar para Premium",
      settings: "Configurações",
      help: "Ajuda e Suporte",
      logout: "Sair",
    },
    dialog: {
      limitReached: "Você atingiu seu limite mensal de transcrições. Atualize para Premium para acesso ilimitado!",
      remaining: "Você tem {remaining} transcrições restantes este mês. Atualize para acesso ilimitado!",
    },
  },
  auth: {
    headings: {
      welcome: "Faça hoje contar!",
      start: "Comece Sua Jornada de Aprendizado",
      createAccount: "Criar Conta",
      signIn: "Entrar",
    },
    subheadings: {
      login: "Bem-vindo de volta! Por favor, insira seus dados",
      signup: "Insira seus dados para começar",
      leftLogin: "Continue sua aventura de aprendizado de idiomas com podcasts interativos e recursos com IA.",
      leftSignup: "Aprenda inglês de forma eficaz com vídeos imersivos e conteúdo envolvente de podcasts.",
    },
    form: {
      email: "Endereço de e-mail",
      emailPlaceholder: "Digite seu e-mail",
      password: "Senha",
      createPassword: "Criar senha",
      passwordPlaceholderLogin: "Digite sua senha",
      passwordPlaceholderSignup: "Crie uma senha forte",
      forgotPassword: "Esqueceu sua senha?",
      signIn: "Entrar",
      createAccount: "Criar conta",
      signingIn: "Entrando...",
      creatingAccount: "Criando conta...",
      haveAccount: "Já tem uma conta?",
      dontHaveAccount: "Não tem uma conta?",
      signInInstead: "Entrar em vez disso",
    },
    reset: {
      title: "Redefinir Senha",
      sentTitle: "Verifique Seu E-mail",
      sentDesc: "Enviamos um link de redefinição de senha para seu endereço de e-mail.",
      enterDesc: "Digite seu endereço de e-mail e enviaremos um link para redefinir sua senha.",
      sentTo: "Enviamos um link de redefinição de senha para:",
      notReceived: "Não recebeu o e-mail? Verifique sua pasta de spam ou tente novamente.",
      backToSignIn: "Voltar para Entrar",
      sendLink: "Enviar Link de Redefinição",
      sending: "Enviando Link de Redefinição...",
      cancel: "Cancelar",
    },
    errors: {
      invalidEmail: "Por favor, insira um endereço de e-mail válido.",
      shortPassword: "A senha deve ter pelo menos 6 caracteres.",
    }
  },
  search: {
    search: "Buscar",
    trending: "Tendência",
    podcast: "Podcast",
    video: "Vídeo",
    allCategories: "Todas as Categorias",
    categoryPrefix: "Categoria:",
    searchingPodcasts: "Buscando podcasts...",
    loadingVideos: "Carregando vídeos...",
    errorTitle: "Erro de Busca",
    noResults: "Nenhum resultado encontrado",
    tryAdjustPodcasts: "Tente ajustar seus termos de busca ou navegue por podcasts em tendência",
    tryAdjustVideos: "Tente ajustar seus termos de busca ou navegue por vídeos em tendência",
    trendingVideos: "Vídeos em Tendência",
    loadingMorePodcasts: "Carregando mais podcasts...",
    endOfResults: "Você chegou ao fim! Esses são todos os podcasts que encontramos.",
    categories: {
      "Arts": "Artes",
      "Business": "Negócios",
      "Comedy": "Comédia",
      "Education": "Educação",
      "Fiction": "Ficção",
      "Government": "Governo",
      "Health & Fitness": "Saúde e Fitness",
      "History": "História",
      "Kids & Family": "Crianças e Família",
      "Leisure": "Lazer",
      "Music": "Música",
      "News": "Notícias",
      "Religion & Spirituality": "Religião e Espiritualidade",
      "Science": "Ciência",
      "Society & Culture": "Sociedade e Cultura",
      "Sports": "Esportes",
      "Technology": "Tecnologia",
      "True Crime": "Crime Verdadeiro",
      "TV & Film": "TV e Cinema",
    },
  },
};

// Russian (ru) translations
const ru: Dict = {
  hero: {
    goalLabel: "Цель",
    minuteShort: "мин",
    hour: "час",
    hours: "часов",
  },
  analytics: {
    learnMore: "Узнать больше",
    chart: {
      practiceTime: "Время практики",
      minutes: "минут",
      current: "Текущий",
    },
    cards: {
      today: {
        title: "Сегодня",
        unit: "мин",
        subtitle: "Время практики сегодня",
      },
      sessions: {
        title: "Сессии",
        unit: "практик",
        subtitle: "Практика shadowing сегодня",
      },
      streak: {
        title: "Серия",
        unitSingular: "день",
        unitPlural: "дней",
        subtitle: "Текущая серия практики",
      },
    },
  },
  upgradeDialog: {
    title: "Перейти на Premium",
    subtitle: {
      withEpisode: 'Доступ к "{episodeTitle}" и разблокировка всего премиум-контента',
      default: "Разблокируйте весь премиум-контент и функции",
    },
    features: {
      episodes: "Доступ ко всем эпизодам",
      ai: "Функции обучения на основе ИИ",
      support: "Приоритетная поддержка",
    },
    actions: {
      upgrade: "Перейти сейчас",
      continueFree: "Продолжить с бесплатным доступом",
    },
  },
  shadowing: {
    title: "Практика Shadowing",
    showTranslation: "Показать перевод",
    showSentenceHint: "Нажмите на иконку глаза, чтобы показать предложение",
    replay: "Повторить",
    continue: "Продолжить",
    startShadowing: "Начать Shadowing",
    stopRecording: "Остановить запись",
    transcribeFailed: "Ошибка транскрипции",
  },
  common: {
    loading: "Загрузка...",
    save: "Сохранить",
    speak: "Говорить",
    signIn: "Войти",
    backToHome: "Вернуться на главную",
    optional: "Необязательно",
  },
  installPrompt: {
    title: "Установить Longoflows на ваше устройство",
    instructions: {
      tap: "Нажмите",
      thenAdd: 'затем "Добавить на главный экран"',
    },
    actions: {
      close: "Закрыть подсказку установки",
    },
  },
  navigation: {
    tagline: "Ваша платформа для изучения языков",
    actions: {
      collapse: "Свернуть боковую панель",
      expand: "Развернуть боковую панель",
    },
    links: {
      home: "Главная",
      search: "Поиск",
      episodes: "Эпизоды",
      course: "Курс",
      library: "Библиотека",
      review: "Повторение",
      history: "История",
      settings: "Настройки",
    },
  },
  tabs: {
    summary: { name: "Резюме", desc: "Резюме эпизода на основе ИИ", mobile: "Резюме" },
    mindmap: { name: "Карта мыслей", desc: "Визуальное отображение концепций", mobile: "Карта" },
    transcript: { name: "Транскрипт", desc: "Полный транскрипт эпизода", mobile: "Скрипт" },
    vocabulary: { name: "Словарь", desc: "Ключевые термины и фразы", mobile: "Словарь" },
    phrase: { name: "Словарь", desc: "Словарь в виде словаря", mobile: "Словарь" },
    quiz: { name: "Викторина", desc: "Интерактивная практика", mobile: "Викторина" },
  },
  player: {
    freePreview: {
      toastTitle: "Достигнут лимит бесплатного предпросмотра",
      toastDescription: "Перейдите на Premium, чтобы продолжить прослушивание после первых 2 минут.",
      toastUpgradeCta: "Перейти",
    },
  },
  phraseSection: {
    title: "Словарь в Словаре",
    subtitle: "Смотрите каждое слово словаря точно там, где оно появляется в транскрипте.",
    truncateNotice: "Показаны первые {count} совпадений",
    actions: {
      openSettings: "Открыть настройки",
    },
    badges: {
      dictionaryMode: "Режим словаря",
    },
    countLabel: {
      singular: "слов",
      plural: "слов",
    },
    indicators: {
      cardsLabel: "Словарь",
      dictionaryLabel: "Словарь",
      dictionaryMissing: "Словарь не выбран",
    },
    jump: "Перейти к клипу",
    empty: {
      noVocabulary: {
        title: "Требуется словарь",
        description: "Сгенерируйте или импортируйте словарь для изучения контекстных фраз.",
      },
      noMatches: {
        title: "Пока нет контекстных фраз",
        description: "Термины словаря еще не появились в транскрипте.",
      },
      noDictionary: {
        title: "Выберите словарь, чтобы разблокировать выделения",
        description: "Выберите словарь в настройках, чтобы мы могли найти слова, которые вы выучили.",
      },
    },
  },
  quizSection: {
    title: "Интерактивная Викторина",
    description: "Практикуйте ключевые термины, извлеченные из этого эпизода.",
    empty: {
      title: "Пока нет доступной викторины",
      description: "Вопросы викторины появятся здесь, как только у словаря будут связанные элементы практики.",
    },
    stats: {
      questionSingular: "вопрос",
      questionPlural: "вопросов",
      countSingular: "Вопрос",
      countPlural: "Вопросы",
    },
    overview: {
      title: "Обзор Викторины",
      nextTerm: "Следующий термин",
      progress: "Прогресс",
      accuracy: "Точность",
      allCompleted: "Все элементы викторины завершены",
    },
    toast: {
      noSentence: "Предложение викторины недоступно для генерации аудио.",
      audioGenerationFailed: "Ошибка генерации аудио",
      audioGenerated: "Аудио викторины сгенерировано",
    },
    messages: {
      completed: "Отличная работа! Этот термин полностью отработан.",
    },
    status: {
      completed: "Завершено",
      todo: "К выполнению",
    },
    translation: {
      show: "Показать перевод",
      hide: "Скрыть перевод",
    },
    shadowing: {
      defaultTitle: "Практика Викторины Эпизода",
      cta: "Shadowing",
      dialogTitle: "Практика Shadowing",
      instructions: "Слушайте и повторяйте предложение",
      showTranslation: "Показать перевод",
      hideTranslation: "Скрыть перевод",
      audioUnavailable: "Эталонное аудио пока недоступно. Сгенерируйте его из карточки викторины, чтобы прослушать.",
      stopRecording: "Остановить запись",
      startShadowing: "Начать Shadowing",
      yourRecording: "Ваша запись",
      attemptsLabel: "Попытки",
    },
  },
  persona: {
    progress: {
      label: "Шаг {current} из {total}",
    },
    actions: {
      skip: "Пропустить пока",
      back: "Назад",
      continue: "Продолжить",
      finish: "Завершить",
    },
    steps: {
      languages: {
        eyebrow: "Шаг 1 · Языки",
        title: "Какие у вас родной и целевой языки?",
        description: "Расскажите нам о ваших предпочтительных языках, чтобы персонализировать переводы.",
      },
      level: {
        eyebrow: "Шаг 2 · Целевой Уровень",
        title: "Как вы оцениваете свой уровень целевого языка?",
        description: "Просто приблизительная самооценка, тест не требуется.",
      },
      goals: {
        eyebrow: "Шаг 3 · Цели",
        title: "Какова ваша основная цель изучения целевого языка?",
        description: "Выберите цели, которые лучше всего соответствуют вашему пути. Вы можете настроить их в любое время.",
      },
      topics: {
        eyebrow: "Шаг 4 · Темы / Интересы",
        title: "Какие видео вам больше всего нравятся?",
        description: "Мы будем рекомендовать английские видео на основе этих тем.",
      },
      formats: {
        eyebrow: "Шаг 5 · Форматы Видео",
        title: "Какой формат видео вам нравится?",
        description: "Выберите стили, которые вам нравятся. Мы смешаем их в ваших рекомендациях.",
      },
      session: {
        eyebrow: "Шаг 6 · Длительность Сессии",
        title: "Сколько времени у вас обычно есть на одну сессию?",
        description: "Мы подберем длину видео под ваш график.",
      },
      subtitles: {
        eyebrow: "Шаг 7 · Субтитры и Объяснение",
        title: "Как мы должны поддерживать ваше обучение?",
        description: "Выберите субтитры и объяснения, которые вы предпочитаете.",
      },
    },
    goals: {
      everyday: "Повседневный разговор",
      watchYoutube: "Смотреть YouTube без субтитров",
      jobInterview: "Собеседования и карьера",
      studyExam: "Учеба / экзамены (IELTS, TOEFL…)",
      travelAbroad: "Путешествия и жизнь за границей",
    },
    topics: {
      dailyVlog: "Ежедневный vlog и образ жизни",
      aiTech: "Технологии и ИИ",
      career: "Карьера и саморазвитие",
      comedy: "Комедия и развлечения",
      moviesTv: "Фильмы и телешоу",
      news: "Новости и текущие события",
      gaming: "Игры",
      education: "Образование и уроки",
    },
    levels: {
      rangeHint: "Мы будем ориентироваться на видео в диапазоне {from}–{to}.",
      a2: {
        title: "Начинающий – Я знаю несколько слов, но не могу следить за большинством видео.",
        description: "Базовый уровень CEFR",
      },
      b1: {
        title: "Ниже среднего – Мне нужны субтитры, чтобы следить за большинством контента.",
        description: "Ниже среднего",
      },
      b2: {
        title: "Средний – С субтитрами я понимаю 70–80%.",
        description: "Выше среднего",
      },
      c1: {
        title: "Продвинутый – Я могу следить за большинством видео без субтитров.",
        description: "Продвинутый",
      },
    },
    formats: {
      vlog: "Vlogs / повседневная жизнь",
      interview: "Интервью и подкасты",
      podcast: "Подкасты / аудио истории",
      tutorial: "Объяснения / уроки",
      shorts: "Короткие клипы / основные моменты",
      movieClip: "Сцены из фильмов / телешоу",
    },
    sessionLength: {
      label: "Длительность сессии",
      description: "Цель {minutes} мин",
      ten: "5–10 минут",
      twenty: "10–20 минут",
      thirty: "20–30 минут",
      fortyFive: "30+ минут",
    },
    sessionFrequency: {
      prompt: "Как часто вы хотите практиковаться?",
      three: "Несколько раз в неделю",
      five: "Почти каждый день",
      seven: "Каждый день",
    },
    subtitles: {
      alwaysTitle: "Всегда показывать английские субтитры",
      alwaysDescription: "Держите субтитры включенными по умолчанию для каждого видео.",
      nativeTitle: "Показывать перевод на моем языке, когда мне нужна помощь",
      nativeDescription: "Переключайте поддержку родного языка всякий раз, когда вы застряли.",
      nativeLanguageLabel: "Предпочтительный родной язык",
      nativeLanguagePlaceholder: "Выбрать язык",
      nativeLanguageHint: "Мы будем использовать это при показе переводов или объяснений.",
      nativeLanguage: {
        auto: "Автоматическое определение",
        zh: "Китайский",
        es: "Испанский",
        ja: "Японский",
        ko: "Корейский",
      },
    },
    languages: {
      nativeLabel: "Какой у вас родной язык?",
      targetLabel: "Какой язык вы изучаете?",
      targetPlaceholder: "Выбрать целевой язык",
      hint: "Мы будем использовать это для сопоставления уроков и руководства по уровням.",
      native: {
        en: "Английский",
        zh: "Китайский",
        es: "Испанский",
        ja: "Японский",
        ko: "Корейский",
        fr: "Французский",
        de: "Немецкий",
        pt: "Португальский",
        ru: "Русский",
      },
      target: {
        en: "Английский",
        zh: "Китайский",
        es: "Испанский",
        ja: "Японский",
        ko: "Корейский",
        fr: "Французский",
        de: "Немецкий",
        pt: "Португальский",
        ru: "Русский",
      },
    },
    explanations: {
      title: "Объяснения, которые вы хотите видеть",
      keyVocab: "Показывать ключевой словарь для каждого видео",
      showPhrases: "Выделять полезные фразы и выражения",
      translateSentences: "Переводить сложные предложения на ваш родной язык",
      pronunciation: "Фокусироваться на произношении и shadowing",
    },
  },
  vocabQuiz: {
    title: "Викторина по Словарю",
    translationUnavailable: "Перевод недоступен для этого сегмента",
    loading: "Загрузка...",
    translation: {
      show: "Показать перевод",
      hide: "Скрыть перевод",
    },
    actions: {
      replay: "Повторить",
      continue: "Продолжить",
    },
  },
  mobileImmersive: {
    subtitles: {
      show: "Показать субтитры",
      hide: "Скрыть субтитры",
    },
    translation: {
      show: "Показать перевод",
      hide: "Скрыть перевод",
    },
    narration: {
      enable: "Включить Режим Рассказа",
      disable: "Отключить Режим Рассказа",
      tooltipEnable: "Автоматически воспроизводить объяснения словаря после воспроизведения",
      tooltipDisable: "Отключить рассказ словаря",
      generating: "Генерация аудио рассказа...",
      failed: "Ошибка генерации рассказа",
      modeActive: "Режим Рассказа Активен",
      playing: "Воспроизведение рассказа...",
      ready: "Рассказ готов",
      skip: "Пропустить",
      skipTooltip: "Пропустить рассказ и продолжить видео",
      currentTerm: "Текущий термин",
      pause: "Пауза",
      resume: "Возобновить",
      repeat: "Повторить",
    },
    status: {
      new: "Новый",
      newDescription: "Только что добавлен, требуется первая проверка",
      fuzzy: "Нечеткий",
      fuzzyDescription: "Требуется больше практики в ближайшее время",
      mastered: "Освоен",
      masteredDescription: "Уверен и на длительной проверке",
    },
    shadowing: {
      tooltip: "Практика shadowing",
      dialogTitle: "Практика Shadowing",
      dialogDescription: "Повторите текущий субтитр вслух.",
      attemptLabel: "Попытка",
      hideText: "Скрыть текст",
      showText: "Показать текст",
      hideTranslationShort: "Скрыть перевод",
      showTranslationShort: "Показать перевод",
      transcriptionLabel: "Ваша транскрипция",
      startRecording: "Начать shadowing",
      stopRecording: "Остановить запись",
      start: "Начать shadowing",
      stop: "Остановить shadowing",
      unknownEpisode: "Неизвестный Эпизод",
    },
  },
  vocabulary: {
    status: {
      label: "Классификация словаря",
      new: "Новый",
      newDescription: "Только что добавлен, требуется первая проверка",
      fuzzy: "Нечеткий",
      fuzzyDescription: "Требуется больше практики в ближайшее время",
      mastered: "Освоен",
      masteredDescription: "Уверен и на длительной проверке",
    },
    section: {
      title: "Ключевые слова и Темы",
      topicCountSingular: "{count} тема",
      topicCountPlural: "{count} тем",
      revealAllButton: "Показать все",
      revealAllTitle: "Показать все объяснения",
      restoreBlurButton: "Восстановить размытие",
      restoreBlurTitle: "Восстановить размытие",
      levelFilterLabel: "Фильтр по уровню CEFR",
      levelFilterUserLevel: "Ваш уровень: {level}",
      levelFilterAll: "Все",
      noTermsMatchFilter: "Пока нет терминов, соответствующих выбранным уровням CEFR. Попробуйте выбрать дополнительные уровни.",
    },
    swipeTip: {
      badge: "Совет",
      heading: "Проведите, чтобы управлять этой стопкой",
      description: "Проведите влево, чтобы скрыть слова, которые вы знаете, или вправо, чтобы оставить их в потоке практики.",
    },
    desktopActions: {
      markKnown: "Отметить как известное",
      keepLearning: "Продолжить практику",
    },
    card: {
      examplesLabel: "Примеры",
    },
    stack: {
      viewDetails: "Просмотреть детали",
      closeDetails: "Закрыть",
    },
  },
  transcript: {
    title: "Транскрипт",
    generate: "Сгенерировать Транскрипт",
    generating: "Генерация Транскрипта...",
    generateDesc: "Сгенерируйте транскрипт, чтобы увидеть полный текст с временными метками и интерактивными функциями.",
    generatingDesc: "Пожалуйста, подождите, пока мы генерируем транскрипт для этого эпизода.",
    segments: "сегментов",
    usageDeducted: "Кредит транскрипта использован",
    usageRemaining: "{remaining}/{limit} осталось в этом месяце",
    usageRemainingUnlimited: "Неограниченные транскрипты с вашим планом",
    dynamicIslandSettings: "Настройки Dynamic Island",
    dynamicIslandTitle: "Dynamic Island",
    dynamicIslandDescription: "Управляйте тем, что появляется в Dynamic Island во время обучения.",
    dynamicIslandVocabulary: "Всплывающее окно словаря",
    dynamicIslandVocabularyHint: "Показывать подсказки словаря внутри всплывающего наложения",
    toggleDynamicIslandVocabulary: "Переключить всплывающее окно словаря",
    wordMask: "Анимация субтитров",
    toggleWordMask: "Переключить анимацию субтитров",
  },
  premiumPrompt: {
    guest: {
      title: "Требуется вход",
      description: "Пожалуйста, войдите, чтобы получить доступ к этому контенту и разблокировать функции обучения.",
    },
    free: {
      title: "Премиум-контент",
      description: "Этот эпизод требует премиум-подписки для доступа ко всем функциям.",
      secondary: "Или ознакомьтесь с нашими рекомендуемыми эпизодами для бесплатного доступа.",
    },
    featured: {
      title: "Бесплатный рекомендуемый контент",
      description: "Вам нравится этот бесплатный эпизод? Перейдите на Premium, чтобы разблокировать нашу полную библиотеку контента!",
      cta: "Узнать о Premium",
    },
    fallback: "Доступ ограничен",
  },
  contentRenderer: {
    alerts: {
      vocabFallback: "Ошибка генерации словаря: {error}. Используются демонстрационные данные вместо этого.",
    },
  },
  upgradePromptDialog: {
    titles: {
      limitReached: "Достигнут месячный лимит",
      upgrade: "Перейти на Premium",
      error: "Ошибка Генерации",
    },
    usage: {
      label: "Месячное использование",
      summary: "{used}/{limit} транскриптов использовано в этом месяце",
      remaining: "{remaining}/{limit} осталось",
      unlimited: "Неограниченные транскрипты с вашим премиум-планом",
    },
    benefits: {
      title: "Перейти на Premium",
      items: {
        unlimitedTranscripts: "Неограниченная генерация транскриптов",
        aiSummaries: "Смотреть все Ready транскрибированные видео",
        vocabulary: "Полный анализ словаря",
        priority: "Приоритетная обработка",
        export: "Экспорт во все форматы",
      },
    },
    plans: {
      basic: {
        name: "Базовый План",
        short: "Базовый",
        badge: "Популярный",
        price: "$12.99",
        period: "/месяц",
        description: "Идеально для индивидуальных учащихся",
      },
      premium: {
        name: "Премиум План",
        short: "Премиум",
        badge: "Лучшая Ценность",
        price: "$19.99",
        period: "/месяц",
        description: "Расширенные функции и приоритетная поддержка",
      },
    },
    actions: {
      upgradeToPlan: "Перейти на {plan}",
      continueWithPlan: "Продолжить с {plan}",
      later: "Позже",
      close: "Закрыть",
      tryAgain: "Попробовать Снова",
    },
    status: {
      processing: "Обработка...",
    },
    alerts: {
      checkoutFailed: "Не удалось начать оформление заказа. Пожалуйста, попробуйте снова.",
    },
    error: {
      generic: "Невозможно сгенерировать транскрипт",
      loginRequired: "Пожалуйста, войдите, чтобы генерировать транскрипты. Ваш прогресс будет сохранен.",
    },
    premiumStatus: {
      title: "У вас есть Премиум План",
      description: "Наслаждайтесь неограниченной генерацией транскриптов и всеми премиум-функциями.",
    },
  },
  episode: {
    floatingActions: {
      buttons: {
        open: "Открыть быстрые действия",
        collapse: "Свернуть быстрые действия",
        showTranslation: "Показать перевод",
        hideTranslation: "Скрыть перевод",
        generateTranslation: "Сгенерировать перевод",
        generatingTranslation: "Генерация перевода...",
        generateChosenExplanation: "Сгенерировать объяснения ИИ",
        generatingChosenExplanation: "Генерация объяснений",
        enableWordHighlight: "Включить выделение слов",
        disableWordHighlight: "Отключить выделение слов",
        adjustPracticeMode: "Настроить режим практики",
        enablePracticeMode: "Включить режим практики",
        resetProgress: "Сбросить прогресс эпизода",
        backToTop: "Вернуться наверх",
      },
      tooltips: {
        open: "Открыть Быстрые Действия",
        collapse: "Свернуть Быстрые Действия",
        showTranslation: "Показать Перевод",
        hideTranslation: "Скрыть Перевод",
        generateTranslation: "Сгенерировать Перевод",
        generatingTranslation: "Генерация...",
        generateChosenExplanation: "Сгенерировать объяснение на основе примеров",
        generatingChosenExplanation: "Генерация объяснений...",
        dictionaryRequiredTitle: "Требуется словарь",
        dictionaryRequiredDescription: "Настройте словарь в Настройках, чтобы разблокировать выделения.",
        enableWordHighlight: "Включить Выделение Слов",
        disableWordHighlight: "Отключить Выделение Слов",
        practiceSetupTitle: "Требуется настройка",
        practiceSetupDescription: "Сгенерируйте словарь или выберите словарь, чтобы начать режим практики.",
        practiceActiveTitle: "Режим практики активен",
        practiceOpenTitle: "Открыть режимы практики",
        practiceActiveDescription: "В настоящее время используется режим {mode}.",
        practiceOpenDescription: "Выберите режим обучения, адаптированный к вашей цели.",
        resetProgressTitle: "Сбросить прогресс эпизода",
        resetProgressDescription: "Очистить освоенные слова только для этого эпизода.",
        backToTop: "Вернуться наверх",
      },
      success: {
        translateGenerated: "Перевод успешно сгенерирован!",
        chosenExplanationGenerated: "Объяснения успешно сгенерированы!",
      },
      alerts: {
        translateMissingData: "Невозможно сгенерировать перевод: отсутствуют необходимые данные.",
      },
      errors: {
        translateRequestFailed: "Ошибка запроса перевода",
        translateResponseInvalid: "Ошибка формата ответа перевода",
        translateConsoleError: "Ошибка генерации перевода",
        translateFailed: "Ошибка генерации перевода, пожалуйста, попробуйте снова.",
        generateVocabularyFirst: "Пожалуйста, сначала сгенерируйте словарь",
        dictionaryInitConsole: "Ошибка инициализации словаря",
        dictionaryLoadFailed: "Невозможно загрузить словарь, пожалуйста, попробуйте снова",
        dictionaryNotSelected: "Пожалуйста, сначала выберите словарь в Настройках",
        dictionaryInitFailed: "Ошибка инициализации менеджера словаря",
        dictionaryInitUnable: "Невозможно загрузить словарь, пожалуйста, попробуйте снова",
        chosenExplanationFailed: "Ошибка генерации объяснений",
        chosenExplanationMissing: "Невозможно сгенерировать объяснения: словарь не готов.",
        chosenExplanationUnauthorized: "Пожалуйста, войдите, чтобы генерировать объяснения.",
      },
      practice: {
        noDictionary: "Нет Словаря",
        spellingSuffix: "Орфография",
      },
      practiceModes: {
        vocabulary: "Словарь",
        toefl: "TOEFL",
        shadowing: "Shadowing",
      },
      dialog: {
        title: "Сбросить Прогресс Эпизода",
        description: "Это очистит все освоенные слова только для этого эпизода и перезапустит вашу практику словаря. Прогресс других эпизодов не будет затронут. Это действие нельзя отменить.",
        cancel: "Отмена",
        confirm: "Сбросить Прогресс",
      },
    },
    exportDialog: {
      lockedBadge: "Премиум-функция",
      lockedDescription: "Экспорт доступен в премиум-планах. Перейдите на Premium, чтобы загрузить, скопировать или поделиться полным контентом эпизода.",
      title: "Экспортировать Эпизод",
      description: 'Экспортировать "{title}" в вашем предпочтительном формате',
      quickActionsTitle: "Быстрые Действия",
      downloadTitle: "Загрузить Файлы",
      quickActions: {
        copyMarkdown: "Копировать Markdown",
        copyText: "Копировать Текст",
        copyUrl: "Копировать URL",
        email: "Электронная почта",
      },
      options: {
        markdown: {
          title: "Файл Markdown",
          description: "Загрузить как отформатированный Markdown (.md) — Идеально для документации и заметок",
        },
        text: {
          title: "Простой Текстовый Файл",
          description: "Загрузить как простой текст (.txt) — Универсальная совместимость",
        },
        json: {
          title: "Данные JSON",
          description: "Загрузить как структурированные данные (.json) — Для разработчиков и автоматизации",
        },
      },
      episodeInfo: {
        episodeLabel: "Эпизод",
        authorLabel: "Автор",
        unknownAuthor: "Неизвестно",
        includesPrefix: "Включает",
        includesSummary: "Резюме",
        includesChapters: "Главы",
        includesKeyPoints: "Ключевые Моменты",
        includesTranscript: "Полный Транскрипт",
      },
    },
    immersiveFloating: {
      subtitles: {
        show: "Показать субтитры",
        hide: "Скрыть субтитры",
      },
      translation: {
        show: "Показать переводы карточек",
        hide: "Скрыть переводы карточек",
      },
      translationReveal: {
        show: "Показать все переводы карточек",
        hide: "Скрыть все переводы карточек",
      },
      exit: "Выйти из режима погружения",
    },
    videoPlayer: {
      immersive: {
        enterFocus: "Режим фокуса",
        exitFocus: "Выйти из фокуса",
        enterImmersive: "Погружающий фокус",
        exitImmersive: "Выйти из погружения",
        enterFocusTitle: "Войти в режим фокуса",
        exitFocusTitle: "Выйти из режима фокуса",
        enterImmersiveTitle: "Войти в режим погружения",
        exitImmersiveTitle: "Выйти из режима погружения (Esc)",
      },
      controls: {
        minimize: "Свернуть плеер",
        maximize: "Восстановить плеер",
      },
    },
    player: {
      previewLock: {
        badge: "Лимит предпросмотра",
        message: "Предпросмотр бесплатного плана ограничен первыми 2 минутами. Перейдите на Premium, чтобы продолжить прослушивание.",
        messageShort: "Лимит предпросмотра — перейдите, чтобы продолжить",
        tooltip: "Предпросмотр бесплатного плана ограничен первыми 2 минутами",
      },
      freePreview: {
        toastTitle: "Достигнут лимит бесплатного предпросмотра",
        toastDescription: "Перейдите на Premium, чтобы продолжить прослушивание после первых 2 минут.",
        toastUpgradeCta: "Перейти",
      },
    },
  },
  summary: {
    title: "Резюме",
    noSummary: "Резюме недоступно",
    generate: "Сгенерировать Резюме",
    generating: "Генерация Резюме...",
    generateDesc: "Сгенерируйте резюме на основе ИИ, чтобы получить полный обзор этого эпизода.",
    generatingDesc: "Пожалуйста, подождите, пока мы создаем резюме на основе ИИ для этого эпизода.",
    aiOverview: "Обзор, сгенерированный ИИ",
    zhTranslation: "Китайский Перевод",
    zhTranslationExtra: "Китайский Перевод (дополнительно)",
    translationLoading: "Загрузка перевода...",
  },
  home: {
    featured: {
      title: "Рекомендуемые",
      error: "Ошибка загрузки рекомендуемых эпизодов",
      refresh: "Обновить страницу, чтобы попробовать снова",
    },
    following: {
      title: "Подписки",
    },
    episodes: {
      title: "Эпизоды",
      viewAll: "Показать Все",
      empty: "В данный момент нет доступных эпизодов.",
    },
    channels: {
      title: "Популярные Каналы",
      viewAll: "Показать Все",
    },
    collections: {
      title: "Коллекции",
      viewAll: "Показать Все",
      countSuffix: "эпизодов",
      viewAllInline: "Показать все",
      categories: {
        "1": "Технологические и ИИ Границы",
        "2": "Личностный Рост и Мышление",
        "3": "Здоровье и Благополучие",
        "4": "Бизнес и Финансы",
        "5": "Глобальные Проблемы и Политика",
        "6": "Отношения и Человеческая Связь",
        "7": "Наука и Открытия",
        "8": "Образ Жизни и Vlogs",
        "9": "История и Культура",
        "10": "Окружающая Среда и Устойчивость",
      },
    },
    youtube: {
      title: "YouTube Рекомендуемые",
      missingThumbnail: "Отсутствует миниатюра",
      playVideo: "Воспроизвести {title}",
      defaultCreator: "Создатель YouTube",
    },
    free: "БЕСПЛАТНО",
  },
  channel: {
    filters: {
      ariaLabel: "Фильтровать эпизоды по сезону",
      allSeasons: "Все Сезоны",
      season: "Сезон {season}",
    },
    badges: {
      episodes: "Эпизоды",
    },
    empty: {
      title: "Эпизоды не найдены",
      seasonFiltered: "Эпизоды для выбранного сезона не найдены.",
      generic: "У этого канала пока нет эпизодов.",
    },
  },
  episodes: {
    title: "Популярные Эпизоды",
    loadingMore: "Загрузка дополнительных эпизодов...",
    loadMore: "Загрузить Больше",
    noMore: "Больше нет эпизодов для загрузки",
    noneTitle: "Эпизоды не найдены",
    noneDesc: "В данный момент нет доступных популярных эпизодов.",
    errorTitle: "Ошибка Загрузки Эпизодов",
    tryAgain: "Попробовать Снова",
  },
  library: {
    title: "Моя Библиотека",
    tabs: {
      favorites: "Избранное",
      playlists: "Плейлисты",
      podcasts: "Подкасты",
    },
    emptyFavoritesTitle: "Пока нет избранного",
    emptyFavoritesDesc: "Эпизоды, которые вы отметите, появятся здесь",
    browseEpisodes: "Просмотреть Эпизоды",
    playlistEmpty: "Этот плейлист пуст",
    playlistsEmptyTitle: "Пока нет плейлистов",
    playlistsEmptyDesc: "Создавайте плейлисты для организации ваших эпизодов",
    podcastsEmptyTitle: "Пока нет подписок на подкасты",
    podcastsEmptyDesc: "Подписывайтесь на подкасты, чтобы видеть их здесь",
    discoverPodcasts: "Открыть Подкасты",
    playlistDialog: {
      title: "Добавить в Плейлист",
      description: "Выберите плейлист для добавления этого эпизода или создайте новый плейлист.",
      addingLabel: "Добавление:",
      nameLabel: "Название Плейлиста *",
      namePlaceholder: "Введите название плейлиста...",
      descriptionLabel: "Описание (необязательно)",
      descriptionPlaceholder: "Добавьте описание...",
      cancel: "Отмена",
      creating: "Создание...",
      createAction: "Создать Плейлист",
      yourPlaylists: "Ваши Плейлисты",
      createNew: "Создать Новый Плейлист",
      createFirst: "Создайте Ваш Первый Плейлист",
      episodesLabel: "эпизодов",
    },
  },
  history: {
    title: "Недавно Просмотренные",
    subtitle: "Отслеживайте свой прогресс и продолжайте с того места, где остановились",
    loading: "Загрузка...",
    loginRequiredTitle: "Требуется Вход",
    loginRequiredDesc: "Пожалуйста, войдите, чтобы просмотреть историю прослушивания.",
    noHistoryTitle: "История не найдена",
    noHistoryAll: "Вы еще не прослушали ни одного эпизода.",
    noHistoryFiltered: (filter: string) => `Эпизоды ${filter} не найдены.`,
    completed: "Завершено",
    left: "осталось",
    watchAgain: "Посмотреть Снова",
    continue: "Продолжить",
    view: "Просмотр",
  },
  course: {
    index: {
      title: "Курсы",
      subtitle: "Изучайте отобранные курсы, чтобы быстрее повысить уровень.",
      cards: {
        idioms: {
          provider: "Lingoflows",
          title: "Самые Распространенные Американские Идиомы",
          description: "Освойте идиомы с повседневными примерами и объяснениями.",
          coverAlt: "Обложка Самых Распространенных Американских Идиом",
        },
      },
    },
    detail: {
      headerTitle: "Курсы",
      menuButton: "Открыть меню",
      breadcrumb: {
        courses: "Курсы",
      },
      actions: {
        continue: "Продолжить Обучение",
      },
      pagination: "Страница {page} / {totalPages} • {total} {unit}",
      paginationPrev: "Предыдущая",
      paginationNext: "Следующая",
      totalBadge: "{total} {unit}",
      idiomUnit: "идиом",
      errors: {
        load: "Ошибка загрузки идиом: {message}",
      },
      courses: {
        idioms: {
          title: "Самые Распространенные Американские Идиомы",
          tagline: "Освойте идиомы вместе со мной!",
          provider: "Lingoflows",
          coverAlt: "Обложка Самых Распространенных Американских Идиом",
        },
        default: {
          title: "Курс",
          tagline: "",
          provider: "",
          coverAlt: "Обложка курса",
        },
      },
    },
    common: {
      previous: "Предыдущая",
      next: "Следующая",
    },
    progress: {
      headerLabel: "Прогресс",
      loading: "Загрузка",
      status: {
        completed: "Завершено",
        todo: "К выполнению",
      },
      completedTooltip: "Завершено",
      exampleAria: "Пример {index} {status}",
      defaultCourseLabel: "Курс",
    },
    freePlanLimit: {
      title: "Перейдите на Premium, чтобы разблокировать этот урок",
      description:
        "Пользователи бесплатного плана могут получить доступ к первым 10 идиомам. Перейдите на Premium, чтобы продолжить изучение полного курса.",
      lockedCardLabel: "Перейдите на Premium, чтобы разблокировать этот урок",
      lockedBanner: "Перейдите, чтобы разблокировать",
      lockedHeading: "Перейдите на Premium, чтобы продолжить обучение",
      lockedMessage:
        "Пользователи бесплатного плана могут получить доступ к первым 10 идиомам. Перейдите на Premium, чтобы разблокировать полный курс, включая урок #{number}.",
      backToList: "Вернуться к обзору курса",
    },
    idiomDetail: {
      breadcrumb: {
        courses: "Курсы",
      },
      sidebar: {
        idiomLabel: "Идиома",
        difficulty: "Сложность",
        examples: "Примеры",
        backToList: "Вернуться к списку",
      },
      translationToggle: "Перевод",
      untitled: "Без названия",
      examples: {
        title: "Примеры",
        description: "Практикуйте каждый пример и записывайте свой голос, чтобы завершить эту главу.",
        empty: "Пока нет примеров для этой идиомы.",
      },
      errors: {
        load: "Невозможно загрузить детали идиомы. Пожалуйста, попробуйте позже.",
      },
    },
    examples: {
      transcribing: "Транскрибирование...",
    },
  },
  savedWords: {
    title: "Повторение",
    countSuffix: "сохраненных слов",
    selectedSuffix: "выбрано",
    blur: {
      show: "Показать Объяснения",
      hide: "Скрыть Объяснения",
    },
    play: {
      playing: "Воспроизведение...",
      playSelected: "Воспроизвести Выбранные",
      playAll: "Воспроизвести Все",
    },
    export: {
      label: "Экспорт",
      json: "Экспорт JSON",
      csv: "Экспорт CSV",
      jsonShort: "JSON",
      csvShort: "CSV",
    },
    selectMode: {
      enter: "Выбрать",
      exit: "Готово",
      selectAll: "Выбрать Все",
      clear: "Очистить",
    },
    filters: {
      all: "Все",
    },
    sort: {
      newest: "Сначала Новые",
      oldest: "Сначала Старые",
      alphabetical: "А-Я",
    },
    reviewNow: "Повторить Сейчас",
    due: "к сроку",
    onDeck: "в стопке",
    status: {
      new: "Новый",
      fuzzy: "Нечеткий",
      mastered: "Освоен",
    },
    next: "Следующий",
    interval: "Интервал",
    correct: "Правильно",
    wrong: "Неправильно",
    reviewed: "Проверено",
    explains: "Объяснения",
    reviewAgain: "Снова",
    reviewHard: "Сложно",
    reviewGood: "Хорошо",
    reviewEasy: "Легко",
    reviewAllCaughtUp: "Все готово!",
    reviewAllCaughtUpHint: "В данный момент нет карточек, требующих проверки. Вернитесь позже или добавьте новые слова для изучения.",
    empty: {
      title: "Слова не найдены",
      filterHint: "Попробуйте настроить поиск или фильтры.",
      defaultHint: "Начните сохранять слова во время просмотра эпизодов!",
    },
    loginRequiredTitle: "Требуется Вход",
    loginRequiredDesc: "Пожалуйста, войдите, чтобы просмотреть сохраненные слова.",
    searchPlaceholder: "Поиск слов...",
    sources: {
      youdao: "Youdao",
      original: "Оригинал",
    },
    relative: {
      now: "сейчас",
      dueSoon: "скоро к сроку",
      justReviewed: "только что проверено",
      in: "через {value}{unit}",
      ago: "{value}{unit} назад",
      today: "Сегодня",
      yesterday: "Вчера",
    },
    selectionBar: {
      selectedSuffix: "выбрано",
      delete: "Удалить Выбранные",
    },
  },
  settings: {
    title: "Настройки",
    nativeLanguage: "Родной язык",
    nativeLanguageHint: "Выберите ваш родной язык для переводов и интерфейса",
    theme: {
      title: "Внешний Вид",
      hint: "Выберите предпочитаемую цветовую тему",
      light: "Светлая",
      lightDesc: "Светлая цветовая тема",
      dark: "Темная",
      darkDesc: "Темная цветовая тема",
      system: "Системная",
      systemDesc: "Следовать системным настройкам",
    },
    cefrLevel: {
      title: "Уровень CEFR",
      description: "Выберите уровень CEFR, который лучше всего соответствует вашему обучению, для персонализации контента.",
      unset: "Не установлено",
      saving: "Сохранение уровня CEFR...",
    },
    interfaceLanguage: "Язык Интерфейса",
    interfaceLanguageHint: "Выберите язык интерфейса приложения. Это независимо от настройки вашего родного языка.",
    videoTitles: {
      title: "Язык названия эпизода",
      description: "Показывать китайские названия на главной странице, когда доступны переводы.",
      chineseLabel: "Китайское название",
      originalLabel: "Оригинальное название",
      saving: "Сохранение предпочтений...",
    },
    vocabularyDict: "Словарь Словаря",
    remove: "Удалить",
    removing: "Удаление...",
    selectVocabularyHint: "Выберите словарь, который вы хотите изучить, в зависимости от ваших потребностей",
    dailyGoal: {
      title: "Ежедневная Цель Shadowing",
      desc: "Установите ежедневное время практики и быстро просмотрите свой прогресс",
      targetMinutes: "Целевые минуты",
      minutesLabel: "Минуты",
      progressPrefix: "Прогресс сегодня:",
      remainingPrefix: "Осталось",
      minAbbr: "мин",
      completedPrefix: "Завершено",
      todayWord: "сегодня",
      saving: "Сохранение...",
      saved: "Сохранено",
      loadFailed: "Ошибка загрузки цели",
      saveFailed: "Ошибка сохранения",
    },
    examVocab: {
      title: "Словарь Экзамена",
      subtitle: "Профессиональный и стандартизированный словарь экзаменов",
    },
  },
  user: {
    plan: {
      premium: "Премиум",
      free: "Бесплатный План",
    },
    badge: {
      premiumMember: "Премиум Член",
      unlimited: "Неограниченно",
    },
    usage: {
      transcriptsLabel: "транскриптов",
      remaining: "осталось в этом месяце",
      limitReached: "Достигнут месячный лимит",
    },
    actions: {
      upgrade: "Перейти на Premium",
      settings: "Настройки",
      help: "Помощь и Поддержка",
      logout: "Выйти",
    },
    dialog: {
      limitReached: "Вы достигли месячного лимита транскриптов. Перейдите на Premium для неограниченного доступа!",
      remaining: "У вас осталось {remaining} транскриптов в этом месяце. Перейдите на Premium для неограниченного доступа!",
    },
  },
  auth: {
    headings: {
      welcome: "Пусть сегодня имеет значение!",
      start: "Начните Ваше Путешествие Обучения",
      createAccount: "Создать Аккаунт",
      signIn: "Войти",
    },
    subheadings: {
      login: "Добро пожаловать обратно! Пожалуйста, введите ваши данные",
      signup: "Введите ваши данные, чтобы начать",
      leftLogin: "Продолжите ваше приключение изучения языков с интерактивными подкастами и функциями на основе ИИ.",
      leftSignup: "Изучайте английский эффективно с погружающими видео и увлекательным контентом подкастов.",
    },
    form: {
      email: "Адрес электронной почты",
      emailPlaceholder: "Введите ваш e-mail",
      password: "Пароль",
      createPassword: "Создать пароль",
      passwordPlaceholderLogin: "Введите ваш пароль",
      passwordPlaceholderSignup: "Создайте надежный пароль",
      forgotPassword: "Забыли пароль?",
      signIn: "Войти",
      createAccount: "Создать аккаунт",
      signingIn: "Вход...",
      creatingAccount: "Создание аккаунта...",
      haveAccount: "Уже есть аккаунт?",
      dontHaveAccount: "Нет аккаунта?",
      signInInstead: "Войти вместо этого",
    },
    reset: {
      title: "Сбросить Пароль",
      sentTitle: "Проверьте Вашу Электронную Почту",
      sentDesc: "Мы отправили ссылку для сброса пароля на ваш адрес электронной почты.",
      enterDesc: "Введите ваш адрес электронной почты, и мы отправим вам ссылку для сброса пароля.",
      sentTo: "Мы отправили ссылку для сброса пароля на:",
      notReceived: "Не получили письмо? Проверьте папку спам или попробуйте снова.",
      backToSignIn: "Вернуться к Входу",
      sendLink: "Отправить Ссылку для Сброса",
      sending: "Отправка Ссылки для Сброса...",
      cancel: "Отмена",
    },
    errors: {
      invalidEmail: "Пожалуйста, введите действительный адрес электронной почты.",
      shortPassword: "Пароль должен содержать не менее 6 символов.",
    }
  },
  search: {
    search: "Поиск",
    trending: "Тренды",
    podcast: "Подкаст",
    video: "Видео",
    allCategories: "Все Категории",
    categoryPrefix: "Категория:",
    searchingPodcasts: "Поиск подкастов...",
    loadingVideos: "Загрузка видео...",
    errorTitle: "Ошибка Поиска",
    noResults: "Результаты не найдены",
    tryAdjustPodcasts: "Попробуйте настроить условия поиска или просмотрите популярные подкасты",
    tryAdjustVideos: "Попробуйте настроить условия поиска или просмотрите популярные видео",
    trendingVideos: "Популярные Видео",
    loadingMorePodcasts: "Загрузка дополнительных подкастов...",
    endOfResults: "Вы достигли конца! Это все подкасты, которые мы нашли.",
    categories: {
      "Arts": "Искусство",
      "Business": "Бизнес",
      "Comedy": "Комедия",
      "Education": "Образование",
      "Fiction": "Художественная литература",
      "Government": "Правительство",
      "Health & Fitness": "Здоровье и Фитнес",
      "History": "История",
      "Kids & Family": "Дети и Семья",
      "Leisure": "Досуг",
      "Music": "Музыка",
      "News": "Новости",
      "Religion & Spirituality": "Религия и Духовность",
      "Science": "Наука",
      "Society & Culture": "Общество и Культура",
      "Sports": "Спорт",
      "Technology": "Технологии",
      "True Crime": "Настоящее Преступление",
      "TV & Film": "ТВ и Кино",
    },
  },
};

function get(obj: Dict, path: string, fallback?: string): string {
  return path.split('.').reduce<any>((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj) ?? fallback ?? path;
}

// Language dictionary map
const languageDicts: Record<string, Dict> = {
  'en': en,
  'zh': zh,
  'es': es, // Spanish
  'ja': ja, // Japanese
  'ko': ko, // Korean
  'fr': fr, // French
  'de': de, // German
  'pt': pt, // Portuguese
  'ru': ru, // Russian
};

export function useI18n() {
  const { language } = useLanguage();
  // Normalize language code (handle legacy 'chinese'/'english' values)
  const normalizedLang = language === 'chinese' ? 'zh' : 
                         language === 'english' ? 'en' : 
                         language || 'en';
  
  // Get dictionary for the language, fallback to English if not available
  const dict = languageDicts[normalizedLang] || en;
  
  return {
    lang: normalizedLang,
    t: (key: string, fallback?: string) => get(dict, key, fallback),
    d: dict,
  };
}
